import axios, { AxiosInstance } from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { IS_DEMO_MODE } from '../lib/demoMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface RegisterResponse {
  user_id: string;
  message: string;
}

export interface VerifyEmailRequest {
  code: string;
}

export interface VerifyEmailResponse {
  user_id: string;
  message: string;
  tenant_id?: string;
  tenant_name?: string;
  // Present when the server auto-logs the user in after verification
  // (omitted for accounts with 2FA enabled).
  session_token?: string;
  expires_at?: number;
}

export interface ResendVerificationEmailRequest {
  email: string;
}

export interface ResendVerificationEmailResponse {
  message: string;
}

export interface RequestPasswordResetRequest {
  email: string;
}

export interface RequestPasswordResetResponse {
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}



export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  session_token: string;
  user_id: string;
  expires_at: number;
  requires_2fa?: boolean;
}

export interface GoogleOAuthLoginRequest {
  email: string;
}

export interface GoogleOAuthLoginResponse {
  google_oauth_available: boolean;
  auth_url: string;
}

export interface GoogleOAuthCallbackRequest {
  code: string;
  state: string;
}

export interface GoogleOAuthCallbackResponse {
  session_token: string;
  user: {
    id: string;
    email: string;
    role: string;
    tenant_id: string;
    is_sso_user: boolean;
  };
}

export interface Verify2FARequest {
  code: string;
  is_backup_code?: boolean;
  remember_device?: boolean;
  device_name?: string;
}

export interface Verify2FAResponse {
  session_token: string;
  expires_at: number;
  device_token?: string;
}

export interface Setup2FAInitiateResponse {
  qr_code_url?: string;
  qr_code?: string;
  secret: string;
  backup_codes?: string[];
}

export interface Setup2FAVerifyRequest {
  code: string;
}

export interface Setup2FAVerifyResponse {
  verified: boolean;
  backup_codes: string[];
  message: string;
}

export interface Disable2FARequest {
  current_password: string;
  code: string;                // TOTP code or backup code
  is_backup_code?: boolean;    // Whether the code is a backup code (default: false)
}

export interface Disable2FAResponse {
  message: string;
}

export interface Get2FAStatusResponse {
  enabled: boolean;
  verified: boolean;
  backup_codes_count: number;
}

export interface CreateTenantRequest {
  name: string;
  description?: string;
}

export interface CreateTenantResponse {
  tenant_id: string;
  name: string;
  message: string;
}

export interface LinkUserToTenantRequest {
  user_id: string;
  tenant_id: string;
}

// Flow-related interfaces to match proto definitions
export interface Endpoint {
  ns: string;
  kind: string;
  name: string;
  labels: string[];
}

export interface Policy {
  name: string;
  namespace: string;
  labels: string[];
  revision: number;
  kind: string;
}

export interface Flow {
  src_ip: string;
  dst_ip: string;
  src: Endpoint;
  dst: Endpoint;
  direction: string;
  port: number;
  protocol: string;
  allowed: boolean;
  count: number;
  first_seen: string; // ISO timestamp
  last_seen: string; // ISO timestamp
  ingress_allowed_by: Policy[];
  egress_allowed_by: Policy[];
}

export interface SuspiciousFlowWithReason {
  suspicious_flow: Flow;
  reason: string;
}

// Updated NetworkPolicy interface to match NetworkPolicyWithCluster proto
export interface NetworkPolicy {
  id: string;
  cluster_id: string;
  cluster_name: string;
  namespace: string;
  name: string;
  target_workload_name: string;
  target_workload_kind: string;
  allow_all_yaml: string;
  trimmed_yaml: string;
  suspicious_flows: SuspiciousFlowWithReason[];
  allowed_flows: Flow[];
  risk_score: number;
  risk_rationale: string;
  dest_namespaces: string[]; // Destination namespaces referenced in the policy
  review_status?: string; // Review status: 'pending', 'approved', 'rejected'
}

export interface NetworkPoliciesResponse {
  policies: NetworkPolicy[];
  nextPageToken: string;
  totalCount: number;
}

// Authorization Policy interfaces
export interface L7AccessLog {
  source: L7Endpoint;
  destination: L7Endpoint;
  protocol: string;
  allowed: boolean;
  count: number;
  first_seen: string;
  last_seen: string;
  http_data?: HttpData;
  classification?: 'required' | 'suspicious' | 'unknown';
  classification_reason?: string;
}

export interface SuspiciousL7AccessLogWithReason {
  suspicious_log: L7AccessLog;
  reason: string;
}

export interface L7Endpoint {
  namespace: string;
  kind: string;
  name: string;
  service_name?: string;
  port?: number;
}

export interface HttpData {
  method: string;
  path: string;
  response_code?: number;
  duration_ms?: number;
  bytes_sent?: number;
  bytes_received?: number;
}

export interface AuthorizationPolicy {
  id: string;
  cluster_id: string;
  cluster_name: string;
  namespace: string;
  name: string;
  target_workload_name: string;
  target_workload_kind: string;
  allow_all_yaml: string;
  trimmed_yaml: string;
  suspicious_l7_logs?: SuspiciousL7AccessLogWithReason[];
  allowed_l7_logs?: L7AccessLog[];
  reasons: string[];
  risk_score: number;
  risk_rationale: string;
  dest_namespaces: string[]; // Destination namespaces referenced in the policy
  review_status?: string; // 'pending', 'approved', 'rejected'
}

export interface AuthorizationPoliciesResponse {
  policies: AuthorizationPolicy[];
  nextPageToken: string;
  totalCount: number;
}

// New interface for existing authorization policies
export interface ExistingAuthorizationPolicy {
  id: string;
  name: string;
  namespace: string;
  target_workload_name: string;
  target_workload_kind: string;
  yaml: string;
  policy_hit_count: number;
  recommended_policy_id?: string; // empty if not recommended
  cluster_id: string;
  cluster_name: string;
  ingested_at: number; // Unix timestamp
  
  // Policy effectiveness fields (only present if recommended_policy_id is empty)
  suspicious_l7_logs?: SuspiciousL7AccessLogWithReason[];
  allowed_l7_logs?: L7AccessLog[];
  reasons?: string[];
  suspicious_allowed_ratio?: number;
  required_allowed_ratio?: number;
  effectiveness_reason?: string;
  final_effectiveness_score?: number;
}

export interface ExistingAuthorizationPoliciesResponse {
  policies: ExistingAuthorizationPolicy[];
  nextPageToken: string;
  totalCount: number;
}

// New interface for existing network policies
export interface ExistingPolicy {
  id: string;
  name: string;
  namespace: string;
  target_workload_name: string;
  target_workload_kind: string;
  yaml: string;
  policy_hit_count: number;
  recommended_policy_id?: string; // empty if not recommended
  cluster_id: string;
  cluster_name: string;
  ingested_at: number; // Unix timestamp
  
  // Policy effectiveness fields (only present if recommended_policy_id is empty)
  suspicious_flows?: SuspiciousFlowWithReason[];
  allowed_flows?: Flow[];
  reasons?: string[];
  suspicious_allowed_ratio?: number;
  required_allowed_ratio?: number;
  effectiveness_reason?: string;
  final_effectiveness_score?: number;
}

export interface ExistingPoliciesResponse {
  policies: ExistingPolicy[];
  nextPageToken: string;
  totalCount: number;
}

export interface StreamHealth {
  data?: boolean;
  control?: boolean;
  flows?: boolean;
  events?: boolean;
  logs?: boolean;
}

export interface Cluster {
  id: string;
  cluster_name: string;
  description: string;
  is_connected: boolean;
  last_seen?: number;
  has_active_flows?: boolean;
  last_flow_seen?: number;
  safe_apply?: boolean;
  has_datadog?: boolean;
  connection_status?: 'connected' | 'degraded' | 'disconnected';
  stream_health?: StreamHealth;
}

export interface ClustersResponse {
  clusters: Cluster[];
}

export interface ClusterStats {
  total_clusters: number;
  connected_clusters: number;
  disconnected_clusters: number;
  degraded_clusters: number;
  last_updated: number;
}

export interface ClusterStatsResponse {
  total_clusters: number;
  connected_clusters: number;
  disconnected_clusters: number;
  degraded_clusters: number;
  last_updated: number;
}

export interface OnboardClusterRequest {
  cluster_name: string;
  description?: string;
}

export interface OnboardClusterResponse {
  access_token: string;
  cluster_id: string;
  cluster_name: string;
  expires_in: number;
  token_type: string;
  chart_version?: string;
}

// Keep old interface for backward compatibility temporarily
export interface GenerateOperatorTokenRequest {
  cluster_name: string;
  description?: string;
  safe_apply?: boolean;
}

export interface GenerateOperatorTokenResponse {
  access_token: string;
  cluster_id: string;
  cluster_name: string;
  expires_in: number;
  token_type: string;
  chart_version?: string;
  chart_name?: string; // kestrel-operator (SaaS) or kestrel-operator-onprem (on-prem)
  mtls_certificates?: {
    client_cert: string;
    client_key: string;
    ca_cert: string;
  };
}

export interface ClusterSummary {
  cluster_id: string;
  cluster_name: string;
  description: string;
  created_at: number;
  expires_at: number;
  is_expired: boolean;
  last_seen: number;
  is_connected: boolean;
  has_active_flows?: boolean;
  last_flow_seen?: number;
  safe_apply?: boolean;
  auto_remediate?: boolean;
  auto_remediate_incident_types?: string[];
  connection_status?: 'connected' | 'degraded' | 'disconnected';
  stream_health?: StreamHealth;
}

export interface ListClusterSummariesResponse {
  clusters: ClusterSummary[];
  next_page_token?: string;
}

export interface OffboardClusterRequest {
  cluster_id: string;
}

export interface OffboardClusterResponse {
  message: string;
}

// Keep old interface for backward compatibility temporarily  
export interface DeleteOperatorTokenRequest {
  cluster_id: string;
}

export interface DeleteOperatorTokenResponse {
  message: string;
}

export interface Tenant {
  tenant_id: string;
  name: string;
  setup_completed: boolean;
  created_at: number;
  // True while the org still carries the random placeholder name
  // (My-Organization-<6 digits>) assigned at signup.
  auto_generated_name?: boolean;
}

// Workflows trial consumption (GET /billing/subscriptions). Credit is raw
// billed cost vs the advertised trial credit ($1000 company / $50 personal);
// executions reset daily.
export interface TrialUsage {
  executions_today: number;
  executions_per_day: number;
  credit_used: number;
  credit_total: number;
}

export interface UpdateTenantRequest {
  name: string;
}

export interface UpdateTenantResponse {
  tenant_id: string;
  name: string;
  message: string;
}

export interface PolicyCountResponse {
  policy_count: number;
  reviewed_only: boolean;
}

export interface UserCountResponse {
  user_count: number;
}

// Recent activity interfaces
export interface RecentPolicy {
  id: string;
  namespace: string;
  cluster_name?: string;
  created_at: number;
  reviewed: boolean;
}

export interface PendingReviewCount {
  pending_count: number;
}

export interface PolicyAwaitingReview {
  policy_id: string;
  policy_name: string;
  namespace: string;
  cluster_name?: string;
  target_workload_name: string;
  target_workload_kind: string;
  risk_score: number;
  risk_rationale: string;
  dest_namespaces: string[];
  created_at: number;
  reviewer_type: string;
  namespace_id?: string;
}

export interface RecentCluster {
  id: string;
  cluster_name: string;
  description: string;
  created_at: number;
  last_seen: number;
  is_connected: boolean;
}

export interface RecentUser {
  id: string;
  email: string;
  created_at: number;
}

export interface RecentActivity {
  type: 'policy' | 'authorization-policy' | 'cluster' | 'user';
  action: string;
  details: string;
  time: string;
  data: any;
}

export interface RecentActivitiesResponse {
  activities: RecentActivity[];
}

// Inventory interfaces for topology map
export interface InventoryCluster {
  id: string;
  name: string;
  uid: string;
  description: string;
  created_at: string;
  is_connected: boolean;
  connection_status?: 'connected' | 'degraded' | 'disconnected';
  stream_health?: StreamHealth;
  // Incident data (active security incidents affecting this cluster, last 4 hours)
  incident_count?: number;
  incident_severity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface InventoryNamespace {
  id: string;
  name: string;
  uid: string;
  cluster_uid: string;
  cluster_name?: string;
  labels: string;
  created_at: string;
  incident_count?: number;
  incident_severity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface InventoryNode {
  id: string;
  name: string;
  uid: string;
  cluster_uid: string;
  internal_ips: string[];
  external_ips: string[];
  hostname: string;
  ready: boolean;
  instance_type?: string;
  zone?: string;
  labels: Record<string, string>;
  created_at: string;
}

export interface InventoryWorkload {
  id: string;
  name: string;
  uid: string;
  kind: string;
  namespace_id: string;
  cluster_uid: string;
  labels: any;
  created_at: string;
}

export interface InventoryService {
  id: string;
  name: string;
  uid: string;
  namespace_id: string;
  cluster_uid: string;
  service_type: string;
  cluster_ip: string;
  external_ips: string[];
  labels: any;
  annotations: any;
  selector: any;
  created_at: string;
}

export interface InventoryClustersResponse {
  clusters: InventoryCluster[];
}

export interface InventoryNamespacesResponse {
  namespaces: InventoryNamespace[];
  total_count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

export interface InventoryNodesResponse {
  nodes: InventoryNode[];
}

export interface InventoryWorkloadsResponse {
  workloads: InventoryWorkload[];
}

export interface InventoryServicesResponse {
  services: InventoryService[];
}

// Enhanced inventory interface with risk findings and incident data
export interface EnhancedResource {
  id: string;
  name: string;
  type: 'cluster' | 'workload' | 'service';
  kind?: string;
  namespace?: string;
  cluster_id?: string;
  cluster_name?: string;
  uid?: string;
  labels?: any;
  created_at?: number;
  is_connected?: boolean;
  connection_status?: 'connected' | 'degraded' | 'disconnected';
  stream_health?: StreamHealth;
  service_type?: string;
  cluster_ip?: string;
  risk_findings?: any[];
  risk_severity?: 'high' | 'medium' | 'low';
  risk_count: number;
  // Incident data (active security incidents affecting this resource)
  incident_count: number;
  incident_severity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface EnhancedInventoryResponse {
  resources: EnhancedResource[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  unique_kinds_count?: number;
}

// Flow summary interface to match the FlowStore backend
export interface FlowSummary {
  src_namespace: string;
  src_kind: string;
  src_name: string;
  src_ip?: string; // IP address for external/cloud endpoints
  dst_namespace: string;
  dst_kind: string;
  dst_name: string;
  dst_ip?: string; // IP address for external/cloud endpoints
  protocol: string;
  port: number;
  allowed: boolean;
  suspicious?: boolean;
  suspicious_reason?: string;
  count: number;
  first_seen: number;
  last_seen: number;
  // New aggregated fields for namespace-level summaries
  protocols?: string[];
  ports?: number[];
  // Simple aggregated counts for mixed status flows
  allowed_count?: number;
  denied_count?: number;
  suspicious_count?: number;
  // Known IP enrichment (last-resort resolution for external IPs)
  src_known_ip_name?: string;
  dst_known_ip_name?: string;
}

// Known IP interfaces for mapping external IPs to friendly names
export interface KnownIP {
  id: string;
  tenant_id: string;
  ip_address: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateKnownIPRequest {
  ip_address: string;
  name: string;
  description?: string;
}

export interface UpdateKnownIPRequest {
  name?: string;
  description?: string;
}

export interface KnownIPsResponse {
  known_ips: KnownIP[];
  count: number;
}

export interface L7AccessLogSummary {
  src_namespace: string;
  src_kind: string;
  src_name: string;
  src_ip?: string; // IP address for external/cloud endpoints
  src_service_name?: string;
  dst_namespace: string;
  dst_kind: string;
  dst_name: string;
  dst_ip?: string; // IP address for external/cloud endpoints
  dst_service_name?: string;
  protocol: string;
  l7_protocol: string;
  port: number;
  method?: string;        // HTTP method
  path?: string;          // HTTP path
  response_code?: number; // HTTP response code
  allowed: boolean;
  suspicious?: boolean;
  suspicious_reason?: string;
  count: number;
  first_seen: number;
  last_seen: number;
  total_duration_ms?: number; // Sum of all durations
  bytes_sent?: number;        // Total bytes sent
  bytes_received?: number;    // Total bytes received
  
  // Aggregated fields for namespace-level summaries
  protocols?: string[];
  ports?: number[];
  // Simple aggregated counts for mixed status L7 logs
  allowed_count?: number;
  denied_count?: number;
  suspicious_count?: number;
}

export interface InventoryFlowsResponse {
  flows: FlowSummary[];
  has_more: boolean;
  next_page_token: string;
  total_returned: number;
  limit: number;
}

export interface InventoryL7AccessLogsResponse {
  access_logs: L7AccessLogSummary[];
  has_more: boolean;
  next_page_token: string;
  total_returned: number;
  limit: number;
}

export interface WorkloadFlowsResponse {
  flows: FlowSummary[];
  l7_access_logs: L7AccessLogSummary[];
  has_more: boolean;
  next_page_token: string;
  total_returned: number;
  limit: number;
}

export interface User {
  user_id: string;
  email: string;
  role: string;
  created_at: number;
  picture_url?: string;
  given_name?: string;
  family_name?: string;
}

export interface Invitation {
  invitation_id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: number;
  expires_at: number;
}

export interface AssignNamespaceOwnerRequest {
  user_id: string;
  namespace_id: string;
}

export interface RemoveNamespaceOwnerRequest {
  user_id: string;
  namespace_id: string;
}

export interface GetNamespaceOwnersResponse {
  user_ids: string[];
}

// Cloud resource ownership interfaces
export interface CloudResourceOwnershipMapping {
  user_id: string;
  cloud_resource_id: string;
  user_email?: string;
  resource_name?: string;
  resource_type?: string;
  resource_arn?: string;
  cloud_provider?: 'aws' | 'oci';
  vpc_id?: string;
}

export interface CloudResourceOwnershipRequest {
  id: string;
  cloud_resource_id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: number;
  user_email?: string;
  resource_name?: string;
  resource_type?: string;
  cloud_provider?: 'aws' | 'oci';
}

export interface VPCResourceGroup {
  vpc_id: string;
  vpc_name: string;
  region: string;
  cloud_provider: 'aws' | 'oci';
  aws_account_id?: string;
  resource_count: number;
}

export interface OwnedCloudResource {
  id: string;
  name: string;
  resource_type: string;
  arn: string;
  cloud_provider: 'aws' | 'oci';
  aws_account_id?: string;
  region?: string;
}

// Approval preference interfaces
export interface SetTenantApprovalPreferenceRequest {
  requires_ops_admin_approval: boolean;
}

export interface GetTenantApprovalPreferenceResponse {
  requires_ops_admin_approval: boolean;
}

// Risk Assessment interfaces
export interface RemediationStep {
  step_number: number;
  description: string;
  file_path: string;
  file_type: 'create' | 'update' | 'delete';
  yaml_content: string; // New/remediated YAML
  explanation: string;
  existing_yaml?: string; // Original YAML (for updates)
  resource_name?: string; // e.g., "my-deployment"
  resource_kind?: string; // e.g., "Deployment"
  resource_namespace?: string; // e.g., "default"
}

export interface AffectedResource {
  name: string; // "my-deployment"
  namespace: string; // "default" (empty for cluster-scoped resources)
  resource_type: string; // "Deployment", "Service", etc.
}

export interface RiskFinding {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  category: string;
  recommendation: string;
  affected_resources: AffectedResource[];
  remediation_steps?: RemediationStep[];
  cluster_id?: string;
  cluster_name?: string;
}

// Special interface for topology map risk findings (uses string array for affected_resources)
export interface TopologyRiskFinding {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  category: string;
  recommendation: string;
  affected_resources: string[]; // Format: "namespace/resource" or "namespace/*"
  remediation_steps?: RemediationStep[];
}

// Topology map incident interface (simplified for topology visualization)
export interface TopologyIncident {
  id: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  detected_at: number;
  root_cause?: string;
  incident_type?: string;
}

// Workload Network Policies interface
export interface WorkloadNetworkPolicies {
  recommended_policies: NetworkPolicy[];
  existing_policies: ExistingPolicy[];
}

// Workload Authorization Policies interface
export interface WorkloadAuthorizationPolicies {
  recommended_policies: AuthorizationPolicy[];
  existing_policies: ExistingAuthorizationPolicy[];
}

// Vulnerability Dashboard interfaces
export interface VulnerabilityFinding {
  id: string;
  assessment_id: string;
  cluster_id: string;
  title: string;
  description: string;
  severity: string;
  category: string;
  affected_resources_count: number;
  created_at: string;
  has_remediation_steps: boolean;
  // Validation status fields
  validation_status?: 'fixed' | 'not_fixed' | 'error' | 'unknown';
  validation_summary?: string;
  validated_at?: string;
}

export interface VulnerabilityCategory {
  name: string;
  total_findings: number;
  high_severity_count: number;
  medium_severity_count: number;
  low_severity_count: number;
  top_findings: VulnerabilityFinding[];
  average_severity_score: number;
}

export interface ClusterVulnerabilitySummary {
  cluster_id: string;
  cluster_name: string;
  total_findings: number;
  high_severity_count: number;
  medium_severity_count: number;
  low_severity_count: number;
  overall_severity_score: number;
  last_assessment: string;
  categories: VulnerabilityCategory[];
}

export interface VulnerabilityTrend {
  date: string;
  total_findings: number;
  high_severity_count: number;
  medium_severity_count: number;
  low_severity_count: number;
  average_severity_score: number;
}

export interface RiskTrendDataPoint {
  date: string;
  high_risk: number;
  medium_risk: number;
  low_risk: number;
}

export interface RiskTrendsResponse {
  trends: RiskTrendDataPoint[];
}

export interface CategoryTrendDataPoint {
  date: string;
  network_security: number;
  rbac_security: number;
  privilege_security: number;
  container_image_security: number;
  resource_usage_security: number;
  storage_security: number;
}

export interface CategoryTrendsResponse {
  trends: CategoryTrendDataPoint[];
}

export interface AffectedResource {
  namespace: string;
  workload: string;
  resource_type: string;
  resource_name: string;
  container_name?: string;
}

export interface RemediationStep {
  step_number: number;
  action: string;
  command?: string;
  expected_outcome: string;
}

export interface UniqueFinding {
  id: string;
  tenant_id: string;
  cluster_id: string;
  category: string;
  title: string;
  description: string;
  severity: string;
  recommendation: string;
  affected_resources: AffectedResource[];
  affected_resources_count: number;
  remediation_steps: RemediationStep[];
  has_remediation_steps: boolean;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  status: string;
  validation_status: string;
  validation_summary: string;
  validated_at?: string;
  assessment_ids: string[];
  finding_ids: string[];
  latest_assessment_id: string;
  latest_finding_id: string;
  created_at: string;
  updated_at: string;
}

export interface UniqueFindingsSummary {
  total_findings: number;
  total_occurrences: number;
  new_this_week: number;
  fixed_findings: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
  findings: UniqueFinding[];
}

export interface VulnerabilityDashboard {
  total_findings: number;
  total_clusters: number;
  total_assessments: number;
  overall_severity_score: number;
  categories: VulnerabilityCategory[];
  clusters: ClusterVulnerabilitySummary[];
  critical_findings: VulnerabilityFinding[];
  all_findings: VulnerabilityFinding[];
  trends: VulnerabilityTrend[];
  // Validation statistics
  total_fixed_findings?: number;
  total_not_fixed_findings?: number;
  total_validated_findings?: number;
}

// Finding validation interfaces
export interface FindingValidationResult {
  finding_id: string;
  finding_title: string;
  validation_status: string; // "fixed", "not_fixed", "error"
  validation_summary: string;
  validation_details: string[];
  validated_at: string;
}

export interface ValidateFixesResponse {
  assessment_id: string;
  validation_status: string; // "in_progress", "completed", "failed"
  total_findings: number;
  fixed_findings?: number;
  not_fixed_findings?: number;
  error_findings?: number; // Findings where validation process failed
  results?: FindingValidationResult[];
  validated_at?: string;
}

export interface RiskAssessment {
  id: string;
  cluster_id: string;
  cluster_name: string;
  status: 'ongoing' | 'completed' | 'failed';
  severity_score: number; // 0-10 scale
  created_at: string;
  completed_at?: string;
  findings_count?: number;
  high_risk_findings?: number;
  medium_risk_findings?: number;
  low_risk_findings?: number;
  progress?: number;
  final_report?: string;
  error_message?: string;
  structured_findings?: RiskFinding[];
  validation_status?: 'pending' | 'running' | 'completed' | 'failed';
  validation_started_at?: string;
  validation_completed_at?: string;
  validation_results?: ValidateFixesResponse;
}

export interface RiskAssessmentsResponse {
  assessments: RiskAssessment[];
  nextPageToken: string;
  totalCount: number;
}

export interface TriggerRiskAssessmentRequest {
  cluster_id: string;
}

export interface TriggerRiskAssessmentResponse {
  assessment_id: string;
  message: string;
}

// Cloud Risk Assessment types
export interface TriggerCloudRiskAssessmentRequest {
  aws_connection_id: string;
}

export interface TriggerCloudRiskAssessmentResponse {
  success: boolean;
  assessment_id: string;
  aws_account_id: string;
  message: string;
}

// OCI Risk Assessment types
export interface TriggerOCIRiskAssessmentRequest {
  oci_connection_id: string;
}

export interface TriggerOCIRiskAssessmentResponse {
  success: boolean;
  assessment_id: string;
  oci_tenancy_ocid: string;
  message: string;
}

export interface OCIRiskAssessment {
  id: string;
  cloud_provider: string;
  oci_connection_id: string;
  oci_tenancy_ocid: string;
  tenant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  final_report: string;
  error_message: string;
  created_at: string;
  completed_at?: string;
  findings_count: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
  severity_score: number;
  progress?: number;
}

export interface ListOCIRiskAssessmentsResponse {
  assessments: OCIRiskAssessment[];
  total_count: number;
}

export interface CloudRiskFinding {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  category: string;
  recommendation: string;
  affected_resources: {
    name: string;
    resource_type: string;
    arn?: string;  // AWS ARN
    ocid?: string; // OCI OCID
  }[];
}

export interface CloudRiskAssessment {
  id: string;
  aws_connection_id: string;
  aws_account_id: string;
  tenant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  final_report: string;
  error_message: string;
  created_at: string;
  completed_at?: string;
  findings_count: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
  severity_score: number;
  progress?: number;
  structured_findings?: CloudRiskFinding[];
}

export interface ListCloudRiskAssessmentsResponse {
  assessments: CloudRiskAssessment[];
  total_count: number;
}

// Cloud Unique Finding types (deduplicated findings across assessments)
export interface CloudUniqueFinding {
  id: string;
  tenant_id: string;
  cloud_provider: 'aws' | 'oci';  // Cloud provider type
  aws_connection_id?: string;      // AWS connection (optional for OCI)
  aws_account_id?: string;         // AWS account (optional for OCI)
  oci_connection_id?: string;      // OCI connection (optional for AWS)
  oci_tenancy_ocid?: string;       // OCI tenancy (optional for AWS)
  compartment_id?: string;         // OCI compartment
  category: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  recommendation?: string;
  affected_resources: {
    name: string;
    resource_type: string;
    arn?: string;   // AWS ARN
    ocid?: string;  // OCI OCID
  }[];
  affected_resources_count: number;
  remediation_steps?: any;
  has_remediation_steps: boolean;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  status: 'open' | 'fixed' | 'dismissed' | 'investigating';
  dismissed_by?: string;
  dismissed_at?: string;
  dismissed_reason?: string;
  assessment_ids: string[];
  finding_ids: string[];
  latest_assessment_id: string;
  latest_finding_id: string;
  created_at: string;
  updated_at: string;
}

export interface CloudUniqueFindingsSummary {
  total_findings: number;
  total_occurrences: number;
  new_this_week: number;
  dismissed_findings: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
  findings: CloudUniqueFinding[];
}

// Discriminated union for cloud account summaries
export interface AWSAccountSummary {
  cloud_provider: "aws";
  aws_connection_id: string;
  aws_account_id: string;
  finding_count: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
}

export interface OCIAccountSummary {
  cloud_provider: "oci";
  oci_connection_id: string;
  oci_tenancy_ocid: string;
  finding_count: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  low_risk_findings: number;
}

export type CloudAccountSummary = AWSAccountSummary | OCIAccountSummary;

export interface CloudVulnerabilityDashboardResponse {
  summary: CloudUniqueFindingsSummary;
  account_summaries: CloudAccountSummary[];
}

export interface CloudUniqueFindingsResponse {
  findings: CloudUniqueFinding[];
  count: number;
}

export interface CloudSeverityTrend {
  date: string;
  high_risk: number;
  medium_risk: number;
  low_risk: number;
}

export interface CloudCategoryTrend {
  date: string;
  access_control: number;
  credential_security: number;
  policy_security: number;
  compliance: number;
  network_security: number;
  data_protection: number;
  other: number;
}

export interface CloudTrendsResponse {
  trends: CloudSeverityTrend[] | CloudCategoryTrend[];
  start_date: string;
  end_date: string;
}

export interface RiskAssessmentDetails {
  assessment_id: string;
  cluster_id: string;
  cluster_name: string;
  status: 'ongoing' | 'completed' | 'failed';
  agent_thinking: RiskAssessmentAgentThinking[];
  final_report: string;
  error_message: string;
  created_at: string;
  completed_at?: string;
  severity_score?: number;
  findings_count?: number;
  high_risk_findings?: number;
  medium_risk_findings?: number;
  low_risk_findings?: number;
  structured_findings?: RiskFinding[];
  validation_status?: 'pending' | 'running' | 'completed' | 'failed';
  validation_started_at?: string;
  validation_completed_at?: string;
  validation_results?: ValidateFixesResponse;
}

// Attack Chain interfaces
export interface AttackChainStep {
  id: string;
  attack_chain_id: string;
  step_number: number;
  finding_id?: string; // Optional - can be empty for contextual steps
  step_description: string;
  attacker_capability: string;
  created_at: string;
}

export interface AttackChain {
  id: string;
  assessment_id: string;
  cluster_id: string;
  tenant_id: string;
  chain_name: string;
  severity: 'critical' | 'high' | 'medium';
  risk_score: number; // 0.0 to 10.0
  impact_description: string;
  remediation_summary: string;
  step_count: number;
  is_outdated: boolean; // Tracks if attack chain is stale due to finding changes
  steps: AttackChainStep[];
  created_at: string;
}

export interface AttackChainsResponse {
  attack_chains: AttackChain[];
  total_count: number;
}

// Cloud Attack Chain Types
export interface CloudAttackChainStep {
  id: string;
  attack_chain_id: string;
  step_number: number;
  cloud_unique_finding_id?: string;
  step_description: string;
  attacker_capability: string;
  aws_service: string;
  affected_resource_type: string;
  affected_resource_arn: string;
  mitre_technique: string;
  mitre_technique_name: string;
  created_at: string;
}

export interface CloudAttackChain {
  id: string;
  assessment_id?: string;
  aws_connection_id: string;
  aws_account_id: string;
  tenant_id: string;
  chain_name: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  risk_score: number;
  impact_description: string;
  remediation_summary: string;
  attack_type: string;
  mitre_tactics: string[];
  step_count: number;
  is_outdated: boolean;
  steps: CloudAttackChainStep[];
  created_at: string;
  updated_at: string;
}

export interface CloudAttackChainsResponse {
  attack_chains: CloudAttackChain[];
  total_count: number;
}

// Audit Event interfaces
export interface AuditEvent {
  id: string;
  user_email: string;
  user_role: string;
  action: string;
  resource: string;
  method: string;
  path?: string;
  status_code: number;
  ip_address?: string;
  request_id?: string;
  request_data?: Record<string, any>;
  timestamp: string;
}

export interface AuditEventListResponse {
  events: AuditEvent[];
  total: number;
  total_pages: number;
  current_page: number;
  page_size: number;
}

export interface AuditEventStats {
  total_events: number;
  top_actions: Array<{ action: string; count: number }>;
}

// Okta Configuration interfaces
export interface OktaOIDCConfig {
  id?: string;
  okta_domain: string;
  client_id: string;
  client_secret?: string; // Only for creation/update
  allowed_domains: string[];
  redirect_uri?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface OktaSCIMConfig {
  id?: string;
  okta_domain: string;
  scim_app_name: string;
  scim_token?: string; // Only for creation/update
  scim_base_url?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface OktaConfigResponse {
  tenant_id: string;
  oidc_app?: OktaOIDCConfig;
  scim_app?: OktaSCIMConfig;
}

// GitHub Integration interfaces
export interface GitHubOrgMetadata {
  organization_id?: number;
  organization_name?: string;
  avatar_url?: string;
  description?: string;
  public_repos?: number;
  private_repos?: number;
  total_repos?: number;
  connected_user_id?: number;
  connected_user?: string;
}

export interface GitHubIntegration {
  id: string;
  tenant_id: string;
  github_org_name: string;
  scopes: string[];
  github_metadata: GitHubOrgMetadata;
  connected_at: string;
  connected_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GitHubRepoMetadata {
  description?: string;
  language?: string;
  html_url?: string;
  clone_url?: string;
  ssh_url?: string;
  size?: number;
  star_count?: number;
  watcher_count?: number;
  forks_count?: number;
  open_issues?: number;
  has_issues?: boolean;
  has_wiki?: boolean;
  has_pages?: boolean;
  archived?: boolean;
  disabled?: boolean;
  created_at?: string;
  updated_at?: string;
  pushed_at?: string;
  has_admin_access?: boolean;
  has_read_access?: boolean;
}

export interface GitHubRepository {
  id: string;
  github_integration_id: string;
  tenant_id: string;
  github_repo_id: number;
  repo_name: string;
  repo_full_name: string;
  repo_owner: string;
  default_branch: string;
  is_private: boolean;
  is_fork: boolean;
  has_write_access: boolean;
  is_enabled: boolean;
  github_metadata: GitHubRepoMetadata;
  created_at: string;
  updated_at: string;
}

export interface GitHubIntegrationStatus {
  is_connected: boolean;
  has_valid_token: boolean;
  status?: 'active' | 'suspended' | 'inactive';
  organization?: string | null;
  app_name?: string;
  connected_at?: string;
  integration?: GitHubIntegration;
  repository_count: number;
  enabled_repo_count: number;
  error?: string;
}

export interface GitHubConnectResponse {
  installation_url: string;
  app_name: string;
}

export interface GitHubCallbackRequest {
  code: string;
  state: string;
}

export interface GitHubCallbackResponse {
  success: boolean;
  organization: string;
  connected_at: string;
  repository_sync: string;
}

export interface GitHubRepositoriesResponse {
  repositories: GitHubRepository[];
  integration: GitHubIntegration;
}

// GitLab Integration interfaces
export interface GitLabMetadata {
  avatar_url?: string;
  name?: string;
  web_url?: string;
  state?: string;
  is_admin?: boolean;
}

export interface GitLabIntegration {
  id: string;
  tenant_id: string;
  gitlab_user_id: number;
  gitlab_username: string;
  gitlab_email?: string;
  gitlab_group_id?: number;
  gitlab_group_path?: string;
  gitlab_group_name?: string;
  token_expires_at?: string;
  token_scope?: string;
  gitlab_metadata: GitLabMetadata;
  connected_at: string;
  connected_by: string;
  is_active: boolean;
  last_token_refresh_at?: string;
  last_api_call_at?: string;
  created_at: string;
  updated_at: string;
}

export interface GitLabRepoMetadata {
  description?: string;
  star_count?: number;
  forks_count?: number;
  http_url?: string;
  ssh_url?: string;
}

export interface GitLabRepository {
  id: string;
  gitlab_integration_id: string;
  tenant_id: string;
  gitlab_project_id: number;
  project_name: string;
  project_path: string;
  project_namespace: string;
  default_branch: string;
  project_url?: string;
  visibility: string;
  is_fork: boolean;
  is_archived: boolean;
  is_enabled: boolean;
  iac_type?: string;
  iac_root_path?: string;
  iac_enabled: boolean;
  iac_aws_connection_id?: string;
  iac_oci_connection_id?: string;
  gitlab_metadata: GitLabRepoMetadata;
  created_at: string;
  updated_at: string;
}

export interface GitLabIntegrationStatus {
  is_connected: boolean;
  has_valid_token: boolean;
  username?: string | null;
  user_id?: number;
  email?: string;
  connected_at?: string;
  is_active?: boolean;
  repository_count: number;
  enabled_repo_count: number;
  iac_enabled_count?: number;
  token_expires_at?: string;
  last_token_refresh?: string;
  metadata?: GitLabMetadata;
  error?: string;
}

export interface GitLabConnectResponse {
  authorization_url: string;
}

export interface GitLabCallbackRequest {
  code: string;
  state: string;
}

export interface GitLabCallbackResponse {
  success: boolean;
  message: string;
  integration: {
    id: string;
    gitlab_username: string;
    connected_at: string;
  };
}

export interface GitLabRepositoriesResponse {
  repositories: GitLabRepository[];
  integration: {
    id: string;
    gitlab_username: string;
  };
}

export interface GitLabProject {
  id: number;
  name: string;
  full_path: string;
  namespace: string;
  default_branch: string;
  visibility: string;
  description?: string;
  web_url: string;
  http_url?: string;
  is_private: boolean;
  is_fork: boolean;
  is_archived: boolean;
  has_write_access: boolean;
  star_count?: number;
  forks_count?: number;
  created_at: string;
  updated_at?: string;
}

export interface GitLabProjectsResponse {
  projects: GitLabProject[];
  count: number;
}

export interface GitLabSyncRepositoriesRequest {
  project_ids: number[];
}

export interface GitLabSyncRepositoriesResponse {
  success: boolean;
  synced: GitLabRepository[];
  synced_count: number;
  errors: string[];
}

export interface GitLabCreateMergeRequestRequest {
  repository_id: string;
  target_file: string;
  content: string;
  mr_title: string;
  mr_description?: string;
  commit_message?: string;
  update_mode?: string;
  source_type: string;
  source_id?: string;
  aws_connection_id?: string;
}

export interface GitLabCreateMergeRequestResponse {
  success: boolean;
  merge_request: {
    iid: number;
    id: number;
    url: string;
    title: string;
    state: string;
    branch: string;
  };
}

export interface RiskAssessmentAgentThinking {
  agent_type: string;
  thinking_overview: string;
  turn_number: number;
  tool_calls: ToolCall[];
  findings: string;
  created_at: string;
}

export interface ToolCall {
  tool: string;
  cmd: string;
  output: string;
  success: boolean;
  error: string;
}

export interface NetworkPolicySimulation {
  id: string;
  tenant_id: string;
  cluster_id: string;
  cluster_name: string;
  name: string;
  description: string;
  policy_yaml: string;
  policy_namespace: string;
  policy_name: string;
  target_workload_name: string;
  target_workload_kind: string;
  target_workload_namespace: string;
  
  // Flow analysis results
  allowed_flows: Flow[];
  denied_flows: Flow[];
  suspicious_flows: SuspiciousFlowWithReason[];
  
  // Policy effectiveness metrics
  total_flows_analyzed: number;
  flows_allowed_count: number;
  flows_denied_count: number;
  suspicious_flows_count: number;
  required_flows_count: number;
  
  // Effectiveness scores and analysis
  effectiveness_score: number;
  effectiveness_reason: string;
  suspicious_allowed_ratio: number;
  required_allowed_ratio: number;
  
  // Metadata
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface CreateNetworkPolicySimulationRequest {
  cluster_id: string;
  name: string;
  description?: string;
  policy_yaml: string;
}

export interface CreateNetworkPolicySimulationResponse {
  simulation_id: string;
  message: string;
}

export interface NetworkPolicySimulationsResponse {
  simulations: NetworkPolicySimulation[];
  nextPageToken: string;
  totalCount: number;
}

export interface AuthorizationPolicySimulation {
  id: string;
  tenant_id: string;
  cluster_id: string;
  cluster_name: string;
  name: string;
  description: string;
  policy_yaml: string;
  policy_namespace: string;
  policy_name: string;
  target_workload_name: string;
  target_workload_kind: string;
  target_workload_namespace: string;
  
  // L7 access log analysis results
  allowed_l7_logs: L7AccessLog[];
  denied_l7_logs: L7AccessLog[];
  suspicious_l7_logs: SuspiciousL7AccessLogWithReason[];
  
  // Policy effectiveness metrics
  total_l7_logs_analyzed: number;
  l7_logs_allowed_count: number;
  l7_logs_denied_count: number;
  suspicious_l7_logs_count: number;
  required_l7_logs_count: number;
  
  // Effectiveness scores and analysis
  effectiveness_score: number;
  effectiveness_reason: string;
  suspicious_allowed_ratio: number;
  required_allowed_ratio: number;
  
  // Metadata
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface CreateAuthorizationPolicySimulationRequest {
  cluster_id: string;
  name: string;
  description?: string;
  policy_yaml: string;
}

export interface CreateAuthorizationPolicySimulationResponse {
  simulation_id: string;
  message: string;
}

export interface AuthorizationPolicySimulationsResponse {
  simulations: AuthorizationPolicySimulation[];
  nextPageToken: string;
  totalCount: number;
}

// Helper function to format timestamps to relative time
function formatTimeAgo(timestamp: number | string): string {
  const now = Date.now();
  let timestampMs: number;
  
  if (typeof timestamp === 'string') {
    // Handle ISO string timestamps
    timestampMs = new Date(timestamp).getTime();
  } else {
    // Handle Unix timestamps (seconds)
    timestampMs = timestamp * 1000;
  }
  
  const diff = now - timestampMs;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
}

// PagerDuty Integration interfaces
export interface PagerDutyStatus {
  connected: boolean;
  service_name?: string;
  service_id?: string;
  created_at?: number;
  created_by?: string;
  // Enhanced fields
  service_count?: number;
  has_api_token: boolean;
  last_synced_at?: number;
  webhook_url?: string;
}

export interface PagerDutyService {
  id: string;
  pagerduty_service_id: string;
  pagerduty_service_name: string;
  description?: string;
  is_default: boolean;
  is_configured: boolean;
  synced_at: number;
}

export interface PagerDutyNamespaceAssignment {
  id: string;
  service_id: string;
  service_name?: string;
  namespace_id: string;
  namespace_name: string;
  cluster_id: string;
  cluster_name?: string;
  created_at: number;
}

export interface PagerDutyCloudResourceAssignment {
  id: string;
  service_id: string;
  service_name?: string;
  cloud_resource_id: string;
  resource_name?: string;
  resource_type: string;
  resource_arn?: string;
  cloud_provider: string;
  aws_account_id?: string;
  region?: string;
  created_at: number;
}

export interface PagerDutySeverityMapping {
  critical: string;
  high: string;
  medium: string;
  low: string;
}

export interface PagerDutyClusterOverride {
  enabled?: boolean;
  routing_key?: string;
}

export interface PagerDutyConfig {
  notify_k8s_incidents: boolean;
  notify_cloud_incidents: boolean;
  skip_transient_incidents: boolean;
  min_severity: string;
  severity_mapping: PagerDutySeverityMapping;
  cluster_overrides: Record<string, PagerDutyClusterOverride>;
  incident_type_filters: string[];
}

class ApiClient {
  private client: AxiosInstance;
  private sessionToken: string | null = null;
  private deviceFingerprint: string | null = null;
  private fingerprintPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable cookies
      timeout: 300000, // 5 minute default timeout for all requests (can be overridden per request)
    });

    // Initialize fingerprinting
    this.initializeFingerprint();

    // Add request interceptor to include session token and device fingerprint
    this.client.interceptors.request.use(async (config) => {
      if (this.sessionToken) {
        config.headers['X-Session-Token'] = this.sessionToken;
      }

      // Add device fingerprint for login and 2FA requests
      if (config.url?.includes('/login') || config.url?.includes('/auth/2fa/verify')) {
        const fingerprint = await this.getDeviceFingerprint();
        config.headers['X-Device-Fingerprint'] = fingerprint;
      }

      return config;
    });

    // Load session token from localStorage
    const storedToken = localStorage.getItem('sessionToken');
    if (storedToken) {
      this.sessionToken = storedToken;
    }
  }

  private async initializeFingerprint(): Promise<void> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.deviceFingerprint = result.visitorId;

      // Store in localStorage for consistency across sessions
      localStorage.setItem('deviceFingerprint', this.deviceFingerprint);
    } catch (error) {
      console.error('Failed to generate device fingerprint:', error);
      // Fallback to stored fingerprint or generate a UUID
      this.deviceFingerprint = localStorage.getItem('deviceFingerprint') || this.generateFallbackFingerprint();
      localStorage.setItem('deviceFingerprint', this.deviceFingerprint);
    }
  }

  private generateFallbackFingerprint(): string {
    // Generate a UUID v4 as fallback
    return 'fallback-' + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  private async getDeviceFingerprint(): Promise<string> {
    // If we don't have a fingerprint yet, wait for initialization
    if (!this.deviceFingerprint) {
      if (!this.fingerprintPromise) {
        this.fingerprintPromise = this.initializeFingerprint().then(() => this.deviceFingerprint!);
      }
      return await this.fingerprintPromise;
    }
    return this.deviceFingerprint;
  }

  setSessionToken(token: string | null) {
    this.sessionToken = token;
    if (token) {
      localStorage.setItem('sessionToken', token);
    } else {
      localStorage.removeItem('sessionToken');
    }
  }

  hasSessionToken(): boolean {
    return !!(this.sessionToken || localStorage.getItem('sessionToken'));
  }


  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.client.post<RegisterResponse>('/register', data);
    return response.data;
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    const response = await this.client.post<VerifyEmailResponse>('/verify-email', data);
    // The server auto-logs the user in after successful verification unless 2FA is enabled
    if (response.data.session_token) {
      this.setSessionToken(response.data.session_token);
    }
    return response.data;
  }

  async resendVerificationEmail(data: ResendVerificationEmailRequest): Promise<ResendVerificationEmailResponse> {
    const response = await this.client.post<ResendVerificationEmailResponse>('/resend-verification-email', data);
    return response.data;
  }

  async requestPasswordReset(data: RequestPasswordResetRequest): Promise<RequestPasswordResetResponse> {
    const response = await this.client.post<RequestPasswordResetResponse>('/request-password-reset', data);
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await this.client.post<ResetPasswordResponse>('/reset-password', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/login', data);
    // Only set session token if 2FA is not required
    if (!response.data.requires_2fa) {
      this.setSessionToken(response.data.session_token);
    }
    return response.data;
  }

  async verify2FA(data: Verify2FARequest): Promise<Verify2FAResponse> {
    const response = await this.client.post<Verify2FAResponse>('/auth/2fa/verify', data);
    this.setSessionToken(response.data.session_token);
    return response.data;
  }

  async setup2FAInitiate(): Promise<Setup2FAInitiateResponse> {
    const response = await this.client.post<Setup2FAInitiateResponse>('/auth/2fa/setup/initiate');
    return response.data;
  }

  async setup2FAVerify(data: Setup2FAVerifyRequest): Promise<Setup2FAVerifyResponse> {
    const response = await this.client.post<Setup2FAVerifyResponse>('/auth/2fa/setup/verify', data);
    return response.data;
  }

  // -- API Key management -------------------------------------------------

  async listApiKeys(): Promise<any[]> {
    const response = await this.client.get('/api-keys');
    return response.data || [];
  }

  async createApiKey(data: { name: string; scopes: string[]; expires_in: string }): Promise<any> {
    const response = await this.client.post('/api-keys', data);
    return response.data;
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await this.client.post(`/api-keys/${keyId}/revoke`);
  }

  async deleteApiKey(keyId: string): Promise<void> {
    await this.client.delete(`/api-keys/${keyId}`);
  }

  // -- Billing / Usage ----------------------------------------------------

  async getBillingUsage(): Promise<any> {
    const response = await this.client.get('/billing/usage');
    return response.data;
  }

  async getBillingUsageHistory(): Promise<any[]> {
    const response = await this.client.get('/billing/usage/history');
    return response.data || [];
  }

  async getBillingSubscriptions(): Promise<any[]> {
    const response = await this.client.get('/billing/subscriptions');
    const data = response.data;
    // Endpoint returns { subscriptions, trials_used }; older callers expect a
    // bare array, so unwrap. Tolerate the legacy array shape too.
    if (Array.isArray(data)) return data;
    return data?.subscriptions || [];
  }

  async getBillingSubscriptionsFull(): Promise<{
    subscriptions: any[];
    trials_used: Record<string, boolean>;
    personal_trial_tier: boolean;
    trial_usage: TrialUsage | null;
  }> {
    const response = await this.client.get('/billing/subscriptions');
    const data = response.data;
    if (Array.isArray(data)) return { subscriptions: data, trials_used: {}, personal_trial_tier: false, trial_usage: null };
    return {
      subscriptions: data?.subscriptions || [],
      trials_used: data?.trials_used || {},
      personal_trial_tier: !!data?.personal_trial_tier,
      trial_usage: data?.trial_usage || null,
    };
  }

  async createBillingSubscription(data: { product: string; plan: string; units?: number; chat_limit?: number }): Promise<any> {
    const response = await this.client.post('/billing/subscriptions', data);
    return response.data;
  }

  async cancelBillingSubscription(product: string): Promise<void> {
    await this.client.post('/billing/subscriptions/cancel', { product });
  }

  async getBillingAlerts(): Promise<any[]> {
    const response = await this.client.get('/billing/alerts');
    return response.data || [];
  }

  async createBillingAlert(data: { product: string; threshold_type: string; threshold_value: number; notify_email?: boolean; notify_email_address?: string; notify_slack?: boolean; slack_channel_id?: string; slack_dm_user_id?: string }): Promise<any> {
    const response = await this.client.post('/billing/alerts', data);
    return response.data;
  }

  async deleteBillingAlert(alertId: string): Promise<void> {
    await this.client.delete(`/billing/alerts/${alertId}`);
  }

  async getBlockPricing(): Promise<any> {
    const response = await this.client.get('/billing/block-pricing');
    return response.data;
  }

  async productCheckout(data: { product: string; plan: string; units?: number }): Promise<{ session_id: string; client_secret: string; publishable_key: string }> {
    const response = await this.client.post('/billing/product-checkout', data);
    return response.data;
  }

  async startProductTrial(product: string): Promise<any> {
    const response = await this.client.post('/billing/start-product-trial', { product });
    return response.data;
  }

  async getWorkflowCost(workflowId: string): Promise<any> {
    const response = await this.client.get(`/workflows/${workflowId}/cost`);
    return response.data;
  }

  async get2FAStatus(): Promise<Get2FAStatusResponse> {
    const response = await this.client.get<Get2FAStatusResponse>('/auth/2fa/status');
    return response.data;
  }

  async disable2FA(data: Disable2FARequest): Promise<Disable2FAResponse> {
    const response = await this.client.post<Disable2FAResponse>('/auth/2fa/disable', data);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/logout');
    this.setSessionToken(null);
  }

  async validateSession(): Promise<boolean> {
    try {
      const response = await this.client.get('/validate-session');
      return response.data.valid;
    } catch {
      return false;
    }
  }

  async createTenant(data: CreateTenantRequest): Promise<CreateTenantResponse> {
    const response = await this.client.post<CreateTenantResponse>('/tenant/create', data);
    return response.data;
  }

  async linkUserToTenant(data: LinkUserToTenantRequest): Promise<void> {
    await this.client.post('/users/link-tenant', data);
  }

  async getNetworkPolicy(id: string): Promise<NetworkPolicy> {
    const response = await this.client.get<NetworkPolicy>(`/network-policy/${id}`);
    return response.data;
  }

  async getExistingNetworkPolicy(id: string): Promise<ExistingPolicy> {
    const response = await this.client.get<ExistingPolicy>(`/existing-network-policy/${id}`);
    return response.data;
  }

  async getAuthorizationPolicy(id: string): Promise<AuthorizationPolicy> {
    const response = await this.client.get<AuthorizationPolicy>(`/authorization-policy/${id}`);
    return response.data;
  }

  async listNetworkPolicies(
    reviewStatus?: 'all' | 'approved' | 'rejected' | 'pending',
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<NetworkPoliciesResponse> {
    const params = new URLSearchParams();
    if (reviewStatus !== undefined) {
      params.append('review_status', reviewStatus);
    }
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (clusterId) {
      params.append('cluster_id', clusterId);
    }

    const response = await this.client.get<NetworkPoliciesResponse>('/network-policies', { params });
    return response.data;
  }

  async listAuthorizationPolicies(
    reviewStatus?: 'all' | 'approved' | 'rejected' | 'pending',
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<AuthorizationPoliciesResponse> {
    const params = new URLSearchParams();
    if (reviewStatus !== undefined) {
      params.append('review_status', reviewStatus);
    }
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (clusterId) {
      params.append('cluster_id', clusterId);
    }

    const response = await this.client.get<AuthorizationPoliciesResponse>('/authorization-policies', { params });
    return response.data;
  }

  async listExistingNetworkPolicies(
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<ExistingPoliciesResponse> {
    const params = new URLSearchParams();
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (clusterId) {
      params.append('cluster_id', clusterId);
    }

    const response = await this.client.get<ExistingPoliciesResponse>('/existing-network-policies', { params });
    return response.data;
  }

  async getExistingAuthorizationPolicy(id: string): Promise<ExistingAuthorizationPolicy> {
    const response = await this.client.get<ExistingAuthorizationPolicy>(`/existing-authorization-policy/${id}`);
    return response.data;
  }

  async listExistingAuthorizationPolicies(
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<ExistingAuthorizationPoliciesResponse> {
    const params = new URLSearchParams();
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (clusterId) {
      params.append('cluster_id', clusterId);
    }

    const response = await this.client.get<ExistingAuthorizationPoliciesResponse>('/existing-authorization-policies', { params });
    return response.data;
  }

  async listClusters(): Promise<ClustersResponse> {
    const response = await this.client.get<ClustersResponse>('/clusters');
    return response.data;
  }

  async getClusterStats(): Promise<ClusterStatsResponse> {
    const response = await this.client.get<ClusterStatsResponse>('/cluster-stats');
    return response.data;
  }

  async onboardCluster(request: OnboardClusterRequest): Promise<OnboardClusterResponse> {
    const response = await this.client.post<OnboardClusterResponse>('/operator/onboard-cluster', request);
    return response.data;
  }

  // Keep old method for backward compatibility temporarily
  async generateOperatorToken(request: GenerateOperatorTokenRequest): Promise<GenerateOperatorTokenResponse> {
    return this.onboardCluster(request);
  }

    async listClusterSummaries(pageSize?: number, pageToken?: string): Promise<ListClusterSummariesResponse> {
    const params = new URLSearchParams();
    if (pageSize) params.append('page_size', pageSize.toString());
    if (pageToken) params.append('page_token', pageToken);

    const response = await this.client.get<ListClusterSummariesResponse>('/operator/tokens', { params });
    return response.data;
  }
  
  async offboardCluster(request: OffboardClusterRequest): Promise<OffboardClusterResponse> {
    const response = await this.client.delete<OffboardClusterResponse>('/operator/offboard-cluster', { data: request });
    return response.data;
  }

  // Keep old method for backward compatibility temporarily
  async deleteOperatorToken(request: DeleteOperatorTokenRequest): Promise<DeleteOperatorTokenResponse> {
    return this.offboardCluster(request);
  }

  async updateClusterSafeApply(request: { cluster_id: string; safe_apply: boolean }): Promise<{ message: string }> {
    const response = await this.client.put<{ message: string }>('/clusters/safe-apply', request);
    return response.data;
  }

  async updateClusterAutoRemediation(request: { 
    cluster_id: string; 
    auto_remediate: boolean; 
    incident_types: string[];
  }): Promise<{ message: string; auto_remediate: boolean; incident_types: string[] }> {
    const response = await this.client.put('/clusters/auto-remediation', request);
    return response.data;
  }

  async createChatYAMLChange(request: any): Promise<{ change_id: string; message: string }> {
    const response = await this.client.post<{ change_id: string; message: string }>('/chat/yaml-changes', request);
    return response.data;
  }

  async createChatCloudChange(request: {
    aws_connection_id?: string;
    aws_account_id?: string; // Account ID (e.g., from @cloud:380983552929)
    oci_connection_id?: string; // OCI connection ID for OCI CLI changes
    oci_connection_name?: string; // OCI connection name (e.g., oci-Kestrel-OCI)
    change_type: string; // terraform, cloudformation, aws_cli, oci_cli
    resource_type: string;
    resource_name: string;
    action: string; // create, update, delete
    content: string;
    existing_content?: string;
    explanation?: string;
    file_path?: string;
    chat_message_id?: string;
  }): Promise<{ change_id: string; message: string }> {
    const response = await this.client.post<{ change_id: string; message: string }>('/chat/cloud-changes', request);
    return response.data;
  }

  async listChatCloudChanges(params: { page_size?: number; offset?: number }): Promise<{
    cloud_changes: any[];
    has_more: boolean;
    count: number;
  }> {
    const response = await this.client.get('/chat/cloud-changes', { params });
    return response.data;
  }

  async createChatCodeChange(request: {
    repo_platform: string;
    repo_full_name: string;
    github_repository_id?: string;
    gitlab_repository_id?: string;
    file_path: string;
    line_start: number;
    line_end: number;
    language?: string;
    original_code: string;
    fixed_code: string;
    explanation?: string;
    chat_message_id?: string;
    user_query?: string;
  }): Promise<{ change_id: string; message: string }> {
    const response = await this.client.post<{ change_id: string; message: string }>('/chat/code-changes', request);
    return response.data;
  }

  async listChatCodeChanges(params: { page_size?: number; offset?: number }): Promise<{
    changes: any[];
    has_more: boolean;
  }> {
    const response = await this.client.get('/chat/code-changes', { params });
    return response.data;
  }

  async applyChatCloudChange(params: { change_id: string }): Promise<{
    success: boolean;
    message: string;
    output?: string;
    error_message?: string;
    fix_attempted?: boolean;
    fixed_command?: string;
    fix_explanation?: string;
    fix_changes_made?: string[];
    cannot_auto_fix?: boolean;
  }> {
    const response = await this.client.post('/chat/cloud-changes/apply', params);
    return response.data;
  }

  async createCloudChangePR(params: {
    change_id: string;
    repository_id: string;
    file_path?: string;
    title?: string;
    description?: string;
  }): Promise<{
    success: boolean;
    pr_url?: string;
    pr_number?: number;
    branch?: string;
    file_path?: string;
    error?: string;
  }> {
    const response = await this.client.post('/chat/cloud-changes/create-pr', params);
    return response.data;
  }

  async listChatYAMLChanges(params: { my_reviews_only?: boolean; page_size?: number; page_token?: string }): Promise<any> {
    const response = await this.client.get('/chat/yaml-changes', { params });
    return response.data;
  }

  async approveChatYAMLChange(request: { change_id: string }): Promise<{ message: string; can_safe_apply: boolean }> {
    const response = await this.client.post<{ message: string; can_safe_apply: boolean }>('/chat/yaml-changes/approve', request);
    return response.data;
  }

  async applyChatYAMLChange(request: { change_id: string }): Promise<{ message: string; success: boolean; error_message?: string }> {
    // First, get the change details to extract the YAML content and cluster info
    const changes = await this.listChatYAMLChanges({});
    const change = changes.changes?.find((c: any) => c.id === request.change_id);

    if (!change) {
      return {
        success: false,
        message: 'Change not found',
        error_message: 'The requested YAML change could not be found'
      };
    }

    // Get current user info for tracking
    const currentUser = await this.getCurrentUser();

    // Call router's unified YAML apply endpoint
    const requestId = `chat_change_${request.change_id}_${Date.now()}`;
    const routerRequest = {
      cluster_id: change.cluster_id,
      tenant_id: change.tenant_id,
      request_id: requestId,
      manifests: [
        {
          manifest_id: change.id,
          yaml_content: change.yaml_content,
          resource_type: change.resource_kind || '',
          namespace: change.resource_namespace || ''
        }
      ],
      timeout_seconds: 60,
      change_id: request.change_id,  // Server will mark as applied/failed
      user_id: currentUser.id
    };

    try {
      const response = await this.client.post<{ request_id: string; success: boolean; message: string; error?: string }>(
        '/yaml-apply',
        routerRequest
      );

      return {
        success: response.data.success,
        message: response.data.message || (response.data.success ? 'YAML change applied successfully' : 'YAML change application failed'),
        error_message: response.data.error
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to apply YAML change',
        error_message: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  }

  async updateTenant(data: UpdateTenantRequest): Promise<UpdateTenantResponse> {
    const response = await this.client.put<UpdateTenantResponse>('/tenant', data);
    return response.data;
  }

  async getTenant(): Promise<Tenant> {
    const response = await this.client.get<Tenant>('/tenant');
    return response.data;
  }

  // User management and RBAC
  async getCurrentUser() {
    const response = await this.client.get('/user');
    return response.data;
  }

  async updateUserProfile(data: { email: string }) {
    const response = await this.client.put('/user/profile', data);
    return response.data;
  }

  async deleteCurrentUser() {
    const response = await this.client.delete('/user');
    return response.data;
  }

  async deleteCurrentTenant() {
    const response = await this.client.delete('/tenant');
    return response.data;
  }

  async listTenantUsers() {
    const response = await this.client.get('/users');
    return response.data;
  }

  async inviteUser(data: { email: string; role: string }) {
    const response = await this.client.post('/users/invite', data);
    return response.data;
  }

  async listInvitations(): Promise<{ invitations: Invitation[] }> {
    const response = await this.client.get<{ invitations: Invitation[] }>('/invitations');
    return response.data;
  }

  async revokeInvitation(invitationId: string): Promise<any> {
    return this.client.delete(`/invitations/${invitationId}`);
  }

  async removeUserFromOrg(userId: string): Promise<any> {
    return this.client.post('/users/remove', { user_id: userId });
  }

  async updateUserRole(req: { user_id: string, role: string }): Promise<any> {
    return this.client.put('/users/role', { user_id: req.user_id, role: req.role });
  }

  async assignNamespaceOwner(req: AssignNamespaceOwnerRequest): Promise<any> {
    return this.client.post('/namespaces/owners', req);
  }

  // Dashboard stats methods
  async getPolicyCount(reviewedOnly?: boolean): Promise<PolicyCountResponse> {
    const params = new URLSearchParams();
    if (reviewedOnly !== undefined) {
      params.append('reviewed', reviewedOnly.toString());
    }
    
    const url = `/stats/policy-count${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<PolicyCountResponse>(url);
    return response.data;
  }

  async getAuthorizationPolicyCount(reviewedOnly?: boolean): Promise<PolicyCountResponse> {
    const params = new URLSearchParams();
    if (reviewedOnly !== undefined) {
      params.append('reviewed', reviewedOnly.toString());
    }
    
    const url = `/stats/authorization-policy-count${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<PolicyCountResponse>(url);
    return response.data;
  }

  async getUserCount(): Promise<UserCountResponse> {
    const response = await this.client.get<UserCountResponse>('/stats/user-count');
    return response.data;
  }

  // Recent activity methods
  async getRecentPolicies(reviewed?: boolean, limit?: number): Promise<RecentActivitiesResponse> {
    const params = new URLSearchParams();
    if (reviewed !== undefined) {
      params.append('reviewed', reviewed.toString());
    }
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const url = `/activity/recent-policies${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ policies: RecentPolicy[] }>(url);
    return { activities: response.data.policies.map(p => ({
      type: 'policy',
      action: p.reviewed ? 'Network policy reviewed' : 'New network policy created',
      details: `${p.namespace} namespace${p.cluster_name ? ` (${p.cluster_name})` : ''}`,
      time: formatTimeAgo(p.created_at),
      data: p
    }))};
  }

  async getRecentAuthorizationPolicies(reviewed?: boolean, limit?: number): Promise<RecentActivitiesResponse> {
    const params = new URLSearchParams();
    if (reviewed !== undefined) {
      params.append('reviewed', reviewed.toString());
    }
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const url = `/activity/recent-authorization-policies${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ policies: RecentPolicy[] }>(url);
    return { activities: response.data.policies.map(p => ({
      type: 'authorization-policy',
      action: p.reviewed ? 'Authorization policy reviewed' : 'New authorization policy created',
      details: `${p.namespace} namespace${p.cluster_name ? ` (${p.cluster_name})` : ''}`,
      time: formatTimeAgo(p.created_at),
      data: p
    }))};
  }

  async getRecentClusters(limit?: number): Promise<RecentActivitiesResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const url = `/activity/recent-clusters${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ clusters: RecentCluster[] }>(url);
    
    // Only show clusters that have actually connected (have last_seen timestamp)
    const connectedClusters = response.data.clusters.filter(c => c.last_seen > 0);
    
    return { activities: connectedClusters.map(c => ({
      type: 'cluster',
      action: 'Cluster connected',
      details: c.cluster_name,
      time: formatTimeAgo(c.created_at), // Use created_at to show when cluster was first onboarded
      data: c
    }))};
  }

  async getRecentUsers(limit?: number): Promise<RecentActivitiesResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const url = `/activity/recent-users${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ users: RecentUser[] }>(url);
    return { activities: response.data.users.map(u => ({
      type: 'user',
      action: 'New user joined',
      details: u.email,
      time: formatTimeAgo(u.created_at),
      data: u
    }))};
  }

  // Inventory methods for topology map
  async getInventoryClusters(): Promise<InventoryClustersResponse> {
    const response = await this.client.get<InventoryClustersResponse>('/inventory/clusters');
    return response.data;
  }

  async getInventoryNamespaces(clusterUID: string): Promise<InventoryNamespacesResponse> {
    const params = new URLSearchParams();
    params.append('cluster_uid', clusterUID);

    const response = await this.client.get<InventoryNamespacesResponse>('/inventory/namespaces', { params });
    return response.data;
  }

  async getInventoryNamespacesPaginated(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    cluster_id?: string;
  }): Promise<InventoryNamespacesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.cluster_id) queryParams.append('cluster_uid', params.cluster_id);
    const query = queryParams.toString();
    const response = await this.client.get<InventoryNamespacesResponse>(`/inventory/namespaces${query ? `?${query}` : ''}`);
    return response.data;
  }

  async getInventoryNodes(clusterUID: string): Promise<InventoryNodesResponse> {
    const params = new URLSearchParams();
    params.append('cluster_uid', clusterUID);
    
    const response = await this.client.get<InventoryNodesResponse>('/inventory/nodes', { params });
    return response.data;
  }

  async getInventoryWorkloads(namespaceID: string): Promise<InventoryWorkloadsResponse> {
    const params = new URLSearchParams();
    params.append('namespace_id', namespaceID);
    
    const response = await this.client.get<InventoryWorkloadsResponse>('/inventory/workloads', { params });
    return response.data;
  }

  async getInventoryServices(namespaceID: string): Promise<InventoryServicesResponse> {
    const params = new URLSearchParams();
    params.append('namespace_id', namespaceID);

    const response = await this.client.get<InventoryServicesResponse>('/inventory/services', { params });
    return response.data;
  }

  async getEnhancedInventory(params?: {
    type?: 'all' | 'cluster' | 'workload' | 'service';
    cluster_id?: string;
    namespace?: string;
    include_risks?: boolean;
    include_incidents?: boolean;
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<EnhancedInventoryResponse> {
    const queryParams = new URLSearchParams();

    if (params?.type) queryParams.append('type', params.type);
    if (params?.cluster_id) queryParams.append('cluster_id', params.cluster_id);
    if (params?.namespace) queryParams.append('namespace', params.namespace);
    if (params?.include_risks !== undefined) queryParams.append('include_risks', params.include_risks.toString());
    if (params?.include_incidents !== undefined) queryParams.append('include_incidents', params.include_incidents.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await this.client.get<EnhancedInventoryResponse>('/inventory/enhanced', { params: queryParams });
    return response.data;
  }

  async getSearchSuggestions(query: string, filters?: {
    type?: string;
    cluster_id?: string;
    namespace?: string;
  }): Promise<{ suggestions: string[] }> {
    if (!query || query.length < 2) {
      return { suggestions: [] };
    }

    const params = new URLSearchParams();
    params.append('q', query);

    if (filters?.type) params.append('type', filters.type);
    if (filters?.cluster_id) params.append('cluster_id', filters.cluster_id);
    if (filters?.namespace) params.append('namespace', filters.namespace);

    const response = await this.client.get<{ suggestions: string[] }>('/inventory/search-suggestions', { params });
    return response.data;
  }

  async getInventoryFlows(namespaceID: string, limit?: number, pageToken?: string): Promise<InventoryFlowsResponse> {
    const params = new URLSearchParams();
    params.append('namespace_id', namespaceID);
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    
    const response = await this.client.get<InventoryFlowsResponse>('/flows', { params });
    return response.data;
  }

  async getWorkloadFlows(params: {
    cluster_id: string;
    namespace: string;
    workload_name: string;
    limit?: number;
  }): Promise<WorkloadFlowsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('namespace', params.namespace);
    queryParams.append('workload_name', params.workload_name);
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    const response = await this.client.get<WorkloadFlowsResponse>('/flows/workload', { params: queryParams });
    return response.data;
  }

  async getIndividualFlowsBetweenNamespaces(clusterID: string, srcNamespace: string, dstNamespace: string, limit?: number): Promise<{ flows: FlowSummary[], total: number, limit: number, cluster: string, from: string, to: string }> {
    const params = new URLSearchParams();
    params.append('cluster_id', clusterID);
    params.append('src_namespace', srcNamespace);
    params.append('dst_namespace', dstNamespace);
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const response = await this.client.get(`/flows/namespaces`, { params });
    return response.data;
  }

  async getInventoryClusterFlows(clusterUID: string, limit?: number, pageToken?: string, level?: string): Promise<InventoryFlowsResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (level) {
      params.append('level', level);
    }

    const response = await this.client.get<InventoryFlowsResponse>(`/inventory/clusters/${clusterUID}/flows`, { params });
    return response.data;
  }

  async getIndividualL7AccessLogsBetweenNamespaces(clusterID: string, srcNamespace: string, dstNamespace: string, limit?: number): Promise<{ access_logs: L7AccessLogSummary[], total: number, limit: number, cluster: string, from: string, to: string }> {
    const params = new URLSearchParams();
    params.append('cluster_id', clusterID);
    params.append('src_namespace', srcNamespace);
    params.append('dst_namespace', dstNamespace);
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }

    const response = await this.client.get(`/flows/l7-access-logs/namespaces`, { params });
    return response.data;
  }

  async getInventoryClusterL7AccessLogs(clusterUID: string, limit?: number, pageToken?: string, level?: string): Promise<InventoryL7AccessLogsResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (level) {
      params.append('level', level);
    }

    const response = await this.client.get<InventoryL7AccessLogsResponse>(`/inventory/clusters/${clusterUID}/l7-access-logs`, { params });
    return response.data;
  }

  async getL7AccessLogs(namespaceID: string, limit?: number, pageToken?: string): Promise<InventoryL7AccessLogsResponse> {
    const params = new URLSearchParams();
    params.append('namespace_id', namespaceID);
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }

    const response = await this.client.get<InventoryL7AccessLogsResponse>('/flows/l7-access-logs', { params });
    return response.data;
  }

  async removeNamespaceOwner(req: RemoveNamespaceOwnerRequest): Promise<any> {
    return this.client.delete('/namespaces/owners', { data: req });
  }

  async getNamespaceOwners(namespaceId: string): Promise<GetNamespaceOwnersResponse> {
    const response = await this.client.get<GetNamespaceOwnersResponse>(`/namespaces/owners?namespace_id=${namespaceId}`);
    return response.data;
  }

  async getAllNamespaceOwnershipMappings(): Promise<any> {
    const response = await this.client.get('/namespaces/owners/all', {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return response.data;
  }

  async listMyNamespaces(): Promise<any> {
    const response = await this.client.get('/users/me/namespaces');
    return response.data;
  }

  async requestNamespaceOwnership(req: { namespace_id: string }): Promise<any> {
    return this.client.post('/namespaces/ownership-requests', req);
  }

  async listNamespaceOwnershipRequests(): Promise<{ requests: any[] }> {
    const response = await this.client.get('/namespaces/ownership-requests');
    return response.data;
  }

  async approveNamespaceOwnershipRequest(req: { request_id: string }): Promise<any> {
    return this.client.post('/namespaces/ownership-requests/approve', req);
  }

  async rejectNamespaceOwnershipRequest(req: { request_id: string }): Promise<any> {
    return this.client.post('/namespaces/ownership-requests/reject', req);
  }

  // Cloud resource ownership methods
  async assignCloudResourceOwner(req: { user_id: string; cloud_resource_id: string }): Promise<any> {
    return this.client.post('/cloud-resources/owners', req);
  }

  async removeCloudResourceOwner(req: { user_id: string; cloud_resource_id: string }): Promise<any> {
    return this.client.delete('/cloud-resources/owners', { data: req });
  }

  async getCloudResourceOwners(cloudResourceId: string): Promise<{ user_ids: string[] }> {
    const response = await this.client.get<{ user_ids: string[] }>(`/cloud-resources/owners?cloud_resource_id=${cloudResourceId}`);
    return response.data;
  }

  async getAllCloudResourceOwnershipMappings(): Promise<{ assignments: CloudResourceOwnershipMapping[] }> {
    const response = await this.client.get('/cloud-resources/owners/all', {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return response.data;
  }

  async listMyCloudResources(): Promise<{ cloud_resources: OwnedCloudResource[] }> {
    const response = await this.client.get('/users/me/cloud-resources');
    return response.data;
  }

  async requestCloudResourceOwnership(req: { cloud_resource_id: string }): Promise<any> {
    return this.client.post('/cloud-resources/ownership-requests', req);
  }

  async listCloudResourceOwnershipRequests(): Promise<{ requests: CloudResourceOwnershipRequest[] }> {
    const response = await this.client.get('/cloud-resources/ownership-requests');
    return response.data;
  }

  async approveCloudResourceOwnershipRequest(req: { request_id: string }): Promise<any> {
    return this.client.post('/cloud-resources/ownership-requests/approve', req);
  }

  async rejectCloudResourceOwnershipRequest(req: { request_id: string }): Promise<any> {
    return this.client.post('/cloud-resources/ownership-requests/reject', req);
  }

  // VPC-based cloud resource ownership methods
  async assignCloudResourceOwnerByVPC(req: { user_id: string; vpc_id: string }): Promise<{ message: string; resources_count: number }> {
    const response = await this.client.post('/cloud-resources/owners/by-vpc', req);
    return response.data;
  }

  async removeCloudResourceOwnerByVPC(req: { user_id: string; vpc_id: string }): Promise<{ message: string; resources_count: number }> {
    const response = await this.client.delete('/cloud-resources/owners/by-vpc', { data: req });
    return response.data;
  }

  async getCloudResourceVPCs(): Promise<{ vpcs: VPCResourceGroup[] }> {
    const response = await this.client.get('/cloud-resources/vpcs');
    return response.data;
  }

  // Tenant approval preference methods
  async setTenantApprovalPreference(requires_ops_admin_approval: boolean): Promise<void> {
    await this.client.post('/tenant/approval-preference', {
      requires_ops_admin_approval
    });
  }

  async getTenantApprovalPreference(): Promise<GetTenantApprovalPreferenceResponse> {
    const response = await this.client.get<GetTenantApprovalPreferenceResponse>('/tenant/approval-preference');
    return response.data;
  }

  async getPendingNetworkPolicyReviewCount(): Promise<PendingReviewCount> {
    const response = await this.client.get<PendingReviewCount>('/policy-reviews/pending-network-count');
    return response.data;
  }

  async getPendingAuthorizationPolicyReviewCount(): Promise<PendingReviewCount> {
    const response = await this.client.get<PendingReviewCount>('/policy-reviews/pending-authorization-count');
    return response.data;
  }

  async getPoliciesAwaitingReview(pageSize?: number, pageToken?: string): Promise<{ policies: PolicyAwaitingReview[]; next_page_token?: string }> {
    const params = new URLSearchParams();
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    
    const url = `/policy-reviews/awaiting${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ policies: PolicyAwaitingReview[]; next_page_token?: string }>(url);
    return response.data;
  }

  async submitPolicyReview(policyId: string, status: 'approved' | 'rejected'): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/policy-reviews/submit', {
      policy_id: policyId,
      policy_type: 'network',
      status
    });
    return response.data;
  }

  async getPolicyReviewStatus(policyId: string): Promise<any> {
    const response = await this.client.get(`/policy-reviews/status?policy_id=${policyId}&policy_type=network`);
    return response.data;
  }

  async submitAuthorizationPolicyReview(policyId: string, status: 'approved' | 'rejected'): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/policy-reviews/submit', {
      policy_id: policyId,
      policy_type: 'authorization',
      status
    });
    return response.data;
  }

  async getAuthorizationPolicyReviewStatus(policyId: string): Promise<any> {
    const response = await this.client.get(`/policy-reviews/status?policy_id=${policyId}&policy_type=authorization`);
    return response.data;
  }

  async getAuthorizationPoliciesAwaitingReview(pageSize?: number, pageToken?: string): Promise<{ policies: any[]; nextPageToken?: string; totalCount?: number }> {
    const params = new URLSearchParams();
    params.append('policy_type', 'authorization');
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    
    const url = `/policy-reviews/awaiting${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ policies: any[]; next_page_token?: string; total_count?: number }>(url);
    
    return {
      policies: response.data.policies || [],
      nextPageToken: response.data.next_page_token,
      totalCount: response.data.total_count
    };
  }

  // Risk Assessment methods
  async listRiskAssessments(
    clusterId?: string,
    pageSize?: number,
    pageToken?: string
  ): Promise<RiskAssessmentsResponse> {
    const params = new URLSearchParams();
    if (clusterId) params.append('cluster_id', clusterId);
    if (pageSize !== undefined) params.append('page_size', pageSize.toString());
    if (pageToken) params.append('page_token', pageToken);

    const response = await this.client.get<RiskAssessmentsResponse>('/risk-assessments', { params });
    return response.data;
  }

  async triggerRiskAssessment(request: TriggerRiskAssessmentRequest): Promise<TriggerRiskAssessmentResponse> {
    const response = await this.client.post<TriggerRiskAssessmentResponse>('/risk-assessments/trigger', request);
    return response.data;
  }

  async triggerCloudRiskAssessment(request: TriggerCloudRiskAssessmentRequest): Promise<TriggerCloudRiskAssessmentResponse> {
    const response = await this.client.post<TriggerCloudRiskAssessmentResponse>('/cloud-risk-assessment/trigger', request);
    return response.data;
  }

  async listCloudRiskAssessments(limit?: number): Promise<ListCloudRiskAssessmentsResponse> {
    const params = limit ? { limit } : {};
    const response = await this.client.get<ListCloudRiskAssessmentsResponse>('/cloud-risk-assessments', { params });
    return response.data;
  }

  // OCI Risk Assessment APIs
  async triggerOCIRiskAssessment(request: TriggerOCIRiskAssessmentRequest): Promise<TriggerOCIRiskAssessmentResponse> {
    const response = await this.client.post<TriggerOCIRiskAssessmentResponse>('/oci-risk-assessment/trigger', request);
    return response.data;
  }

  async listOCIRiskAssessments(ociConnectionId?: string, limit?: number): Promise<ListOCIRiskAssessmentsResponse> {
    const params: Record<string, string | number> = {};
    if (ociConnectionId) params.oci_connection_id = ociConnectionId;
    if (limit) params.limit = limit;
    const response = await this.client.get<ListOCIRiskAssessmentsResponse>('/oci-risk-assessments', { params });
    return response.data;
  }

  // Cloud Vulnerability Dashboard APIs (supports both AWS and OCI)
  async getCloudVulnerabilityDashboard(params?: {
    aws_connection_id?: string;
    oci_connection_id?: string;
    cloud_provider?: string; // "aws", "oci", or empty for all
  }): Promise<CloudVulnerabilityDashboardResponse> {
    const queryParams = new URLSearchParams();
    if (params?.aws_connection_id) queryParams.append('aws_connection_id', params.aws_connection_id);
    if (params?.oci_connection_id) queryParams.append('oci_connection_id', params.oci_connection_id);
    if (params?.cloud_provider) queryParams.append('cloud_provider', params.cloud_provider);
    const query = queryParams.toString();
    const response = await this.client.get<CloudVulnerabilityDashboardResponse>(
      `/cloud-vulnerability-dashboard${query ? `?${query}` : ''}`
    );
    return response.data;
  }

  async getCloudUniqueFindings(params?: {
    aws_connection_id?: string;
    status?: string;
    severity?: string;
    category?: string;
  }): Promise<CloudUniqueFindingsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.aws_connection_id) queryParams.append('aws_connection_id', params.aws_connection_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.category) queryParams.append('category', params.category);
    const query = queryParams.toString();
    const response = await this.client.get<CloudUniqueFindingsResponse>(
      `/cloud-unique-findings${query ? `?${query}` : ''}`
    );
    return response.data;
  }

  async getCloudUniqueFindingById(findingId: string): Promise<CloudUniqueFinding> {
    const response = await this.client.get<CloudUniqueFinding>(`/cloud-unique-findings/${findingId}`);
    return response.data;
  }

  async dismissCloudUniqueFinding(findingId: string, reason?: string): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean }>(
      `/cloud-unique-findings/${findingId}/dismiss`,
      { reason }
    );
    return response.data;
  }

  async updateCloudUniqueFindingStatus(findingId: string, status: string): Promise<{ success: boolean }> {
    const response = await this.client.patch<{ success: boolean }>(
      `/cloud-unique-findings/${findingId}/status`,
      { status }
    );
    return response.data;
  }

  async getCloudSeverityTrends(params?: {
    aws_connection_id?: string;
    oci_connection_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<CloudTrendsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.aws_connection_id) queryParams.append('aws_connection_id', params.aws_connection_id);
    if (params?.oci_connection_id) queryParams.append('oci_connection_id', params.oci_connection_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString();
    const response = await this.client.get<CloudTrendsResponse>(
      `/cloud-severity-trends${query ? `?${query}` : ''}`
    );
    return response.data;
  }

  async getCloudCategoryTrends(params?: {
    aws_connection_id?: string;
    oci_connection_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<CloudTrendsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.aws_connection_id) queryParams.append('aws_connection_id', params.aws_connection_id);
    if (params?.oci_connection_id) queryParams.append('oci_connection_id', params.oci_connection_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString();
    const response = await this.client.get<CloudTrendsResponse>(
      `/cloud-category-trends${query ? `?${query}` : ''}`
    );
    return response.data;
  }

  async getRiskAssessmentDetails(assessmentId: string): Promise<RiskAssessmentDetails> {
    const response = await this.client.get<RiskAssessmentDetails>(`/risk-assessments/${assessmentId}`);
    return response.data;
  }

  async getRiskAssessmentFinding(assessmentId: string, findingId: string): Promise<RiskFinding> {
    const response = await this.client.get<RiskFinding>(`/risk-assessments/${assessmentId}/findings/${findingId}`);
    return response.data;
  }

  async getUniqueFinding(findingId: string): Promise<RiskFinding> {
    const response = await this.client.get<RiskFinding>(`/unique-findings/${findingId}`);
    return response.data;
  }

  async dismissFinding(assessmentId: string, findingId: string): Promise<void> {
    await this.client.delete(`/risk-assessments/${assessmentId}/findings/${findingId}`);
  }

  async deleteUniqueFinding(findingId: string): Promise<void> {
    await this.client.delete(`/unique-findings/${findingId}`);
  }

  async getRiskFindingsByResource(params: {
    cluster_id: string;
    resource_type: 'workload' | 'namespace' | 'cluster';
    namespace?: string;
    workload?: string;
    resource_kind?: string; // Kubernetes resource kind like "Deployment", "StatefulSet", etc.
  }): Promise<{ findings: TopologyRiskFinding[]; count: number }> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('resource_type', params.resource_type);
    if (params.namespace) queryParams.append('namespace', params.namespace);
    if (params.workload) queryParams.append('workload', params.workload);
    if (params.resource_kind) queryParams.append('resource_kind', params.resource_kind);

    const response = await this.client.get<{ findings: TopologyRiskFinding[]; count: number }>(
      '/risk-assessments/resource-findings',
      { params: queryParams }
    );
    return response.data;
  }

  async getIncidentsByResource(params: {
    cluster_id: string;
    resource_type: 'workload' | 'namespace' | 'cluster' | 'service';
    namespace?: string;
    workload?: string;
    service?: string;
  }): Promise<{ incidents: TopologyIncident[]; count: number }> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('resource_type', params.resource_type);
    if (params.namespace) queryParams.append('namespace', params.namespace);
    if (params.workload) queryParams.append('workload', params.workload);
    if (params.service) queryParams.append('service', params.service);

    const response = await this.client.get<{ incidents: TopologyIncident[]; count: number }>(
      '/incidents/resource-incidents',
      { params: queryParams }
    );
    return response.data;
  }

  async getSuspiciousFlowReasons(params: {
    cluster_id: string;
    src_namespace: string;
    src_name: string;
    dst_namespace: string;
    dst_name: string;
    port: string;
    protocol: string;
  }): Promise<{ reasons: string[]; flow: any }> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('src_namespace', params.src_namespace);
    queryParams.append('src_name', params.src_name);
    queryParams.append('dst_namespace', params.dst_namespace);
    queryParams.append('dst_name', params.dst_name);
    queryParams.append('port', params.port);
    queryParams.append('protocol', params.protocol);
    
    const response = await this.client.get<{ reasons: string[]; flow: any }>(
      '/flows/suspicious-reasons',
      { params: queryParams }
    );
    return response.data;
  }

  // Attack Chain methods
  async getAttackChainsByAssessment(assessmentId: string): Promise<AttackChainsResponse> {
    const response = await this.client.get<AttackChainsResponse>(
      `/risk-assessments/${assessmentId}/attack-chains`
    );
    return response.data;
  }

  async getAttackChain(chainId: string): Promise<AttackChain> {
    const response = await this.client.get<AttackChain>(`/attack-chains/${chainId}`);
    return response.data;
  }

  async generateAttackChains(assessmentId: string): Promise<{ message: string; status: string }> {
    const response = await this.client.post<{ message: string; status: string }>(
      `/risk-assessments/${assessmentId}/attack-chains/generate`
    );
    return response.data;
  }

  async getAttackChainsByCluster(clusterId?: string): Promise<AttackChainsResponse> {
    const url = clusterId
      ? `/attack-chains-by-cluster?cluster_id=${encodeURIComponent(clusterId)}`
      : '/attack-chains-by-cluster';
    const response = await this.client.get<AttackChainsResponse>(url);
    return response.data;
  }

  // Cloud Attack Chains
  async getCloudAttackChains(options?: { awsConnectionId?: string; ociConnectionId?: string }): Promise<CloudAttackChainsResponse> {
    const params = new URLSearchParams();
    if (options?.awsConnectionId) {
      params.append('aws_connection_id', options.awsConnectionId);
    }
    if (options?.ociConnectionId) {
      params.append('oci_connection_id', options.ociConnectionId);
    }
    const response = await this.client.get<CloudAttackChainsResponse>(
      `/cloud-attack-chains${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getCloudAttackChain(chainId: string): Promise<CloudAttackChain> {
    const response = await this.client.get<CloudAttackChain>(`/cloud-attack-chains/${chainId}`);
    return response.data;
  }

  async generateCloudAttackChains(awsConnectionId: string): Promise<{ status: string; message: string }> {
    const response = await this.client.post<{ status: string; message: string }>(
      '/cloud-attack-chains/generate',
      { aws_connection_id: awsConnectionId }
    );
    return response.data;
  }

  async generateOCIAttackChains(ociConnectionId: string): Promise<{ status: string; message: string }> {
    const response = await this.client.post<{ status: string; message: string }>(
      '/cloud-attack-chains/generate-oci',
      { oci_connection_id: ociConnectionId }
    );
    return response.data;
  }

  // Get network policies for a workload
  async getWorkloadNetworkPolicies(params: {
    cluster_id: string;
    target_key: string;
  }): Promise<WorkloadNetworkPolicies> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('target_key', params.target_key);

    const response = await this.client.get<WorkloadNetworkPolicies>(
      '/workload-network-policies',
      { params: queryParams }
    );
    return response.data;
  }

  // Get authorization policies for a workload
  async getWorkloadAuthorizationPolicies(params: {
    cluster_id: string;
    target_key: string;
  }): Promise<WorkloadAuthorizationPolicies> {
    const queryParams = new URLSearchParams();
    queryParams.append('cluster_id', params.cluster_id);
    queryParams.append('target_key', params.target_key);

    const response = await this.client.get<WorkloadAuthorizationPolicies>(
      '/workload-authorization-policies',
      { params: queryParams }
    );
    return response.data;
  }

  // Get vulnerability dashboard data
  async getVulnerabilityDashboard(params?: {
    assessment_ids?: string[];
    categories?: string[];
    severities?: string[];
    cluster_id?: string;
  }): Promise<VulnerabilityDashboard> {
    const searchParams = new URLSearchParams();

    if (params?.assessment_ids?.length) {
      searchParams.set('assessment_ids', params.assessment_ids.join(','));
    }
    if (params?.categories?.length) {
      searchParams.set('categories', params.categories.join(','));
    }
    if (params?.severities?.length) {
      searchParams.set('severities', params.severities.join(','));
    }
    if (params?.cluster_id) {
      searchParams.set('cluster_id', params.cluster_id);
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/vulnerability-dashboard?${queryString}` : '/vulnerability-dashboard';

    const response = await this.client.get<VulnerabilityDashboard>(url);
    return response.data;
  }

  // Get risk trends data for a date range (severity-based, from unique_findings table)
  async getRiskTrends(startDate?: string, endDate?: string, clusterId?: string): Promise<RiskTrendsResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (clusterId) params.cluster_id = clusterId;

    const response = await this.client.get<RiskTrendsResponse>('/risk-trends', { params });
    return response.data;
  }

  // Get category trends data for a date range (from unique_findings table)
  async getCategoryTrends(startDate?: string, endDate?: string, clusterId?: string): Promise<CategoryTrendsResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (clusterId) params.cluster_id = clusterId;

    const response = await this.client.get<CategoryTrendsResponse>('/risk-trends/categories', { params });
    return response.data;
  }

  // Get unique findings with optional cluster filter
  async getUniqueFindings(clusterId?: string): Promise<UniqueFindingsSummary> {
    const params: Record<string, string> = {};
    if (clusterId) params.cluster_id = clusterId;

    const response = await this.client.get<UniqueFindingsSummary>('/unique-findings', { params });
    return response.data;
  }

  // Validate if findings in a risk assessment have been fixed
  async validateRiskAssessmentFixes(assessmentId: string): Promise<ValidateFixesResponse> {
    const response = await this.client.post<ValidateFixesResponse>(
      `/risk-assessment/${assessmentId}/validate-fixes`
    );
    return response.data;
  }

  // Audit Event methods
  async listAuditEvents(
    page?: number,
    pageSize?: number,
    userEmail?: string,
    action?: string,
    resource?: string,
    method?: string,
    startDate?: string,
    endDate?: string,
    userRole?: string,
    isExport?: boolean
  ): Promise<AuditEventListResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (pageSize !== undefined) params.append('page_size', pageSize.toString());
    if (userEmail) params.append('user_email', userEmail);
    if (action) params.append('action', action);
    if (resource) params.append('resource', resource);
    if (method) params.append('method', method);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (userRole) params.append('user_role', userRole);
    if (isExport) params.append('export', 'true');

    const response = await this.client.get<AuditEventListResponse>(`/audit-events?${params}`);
    return response.data;
  }

  async getAuditEventStats(days?: number): Promise<AuditEventStats> {
    const params = new URLSearchParams();
    if (days !== undefined) params.append('days', days.toString());
    
    const response = await this.client.get<AuditEventStats>(`/audit-events/stats?${params}`);
    return response.data;
  }

  // Okta Configuration APIs
  async getOktaConfig(): Promise<OktaConfigResponse> {
    const response = await this.client.get<OktaConfigResponse>('/okta/config');
    return response.data;
  }

  async getOktaOIDCConfig(): Promise<OktaOIDCConfig> {
    const response = await this.client.get<OktaOIDCConfig>('/okta/oidc/config');
    return response.data;
  }

  async createOktaOIDCConfig(config: OktaOIDCConfig): Promise<OktaOIDCConfig> {
    const response = await this.client.post<OktaOIDCConfig>('/okta/oidc/config', config);
    return response.data;
  }

  async updateOktaOIDCConfig(config: OktaOIDCConfig): Promise<OktaOIDCConfig> {
    const response = await this.client.put<OktaOIDCConfig>('/okta/oidc/config', config);
    return response.data;
  }

  async deleteOktaOIDCConfig(): Promise<void> {
    await this.client.delete('/okta/oidc/config');
  }

  async getOktaSCIMConfig(): Promise<OktaSCIMConfig> {
    const response = await this.client.get<OktaSCIMConfig>('/okta/scim/config');
    return response.data;
  }

  async createOktaSCIMConfig(config: OktaSCIMConfig): Promise<OktaSCIMConfig> {
    const response = await this.client.post<OktaSCIMConfig>('/okta/scim/config', config);
    return response.data;
  }

  async updateOktaSCIMConfig(config: OktaSCIMConfig): Promise<OktaSCIMConfig> {
    const response = await this.client.put<OktaSCIMConfig>('/okta/scim/config', config);
    return response.data;
  }

  async deleteOktaSCIMConfig(): Promise<void> {
    await this.client.delete('/okta/scim/config');
  }
  

  // Network Policy Simulation methods
  async createNetworkPolicySimulation(request: CreateNetworkPolicySimulationRequest): Promise<CreateNetworkPolicySimulationResponse> {
    const response = await this.client.post('/network-policy-simulations', request);
    return response.data;
  }

  async listNetworkPolicySimulations(
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<NetworkPolicySimulationsResponse> {
    const params: any = {};
    if (pageSize) params.page_size = pageSize;
    if (pageToken) params.page_token = pageToken;
    if (clusterId) params.cluster_id = clusterId;

    const response = await this.client.get('/network-policy-simulations', { params });
    return response.data;
  }

  async getNetworkPolicySimulation(simulationId: string): Promise<NetworkPolicySimulation> {
    const response = await this.client.get(`/network-policy-simulations/${simulationId}`);
    return response.data;
  }

  async deleteNetworkPolicySimulation(simulationId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/network-policy-simulations/${simulationId}`);
    return response.data;
  }

  // Authorization Policy Simulation methods
  async createAuthorizationPolicySimulation(request: CreateAuthorizationPolicySimulationRequest): Promise<CreateAuthorizationPolicySimulationResponse> {
    const response = await this.client.post('/authorization-policy-simulations', request);
    return response.data;
  }

  async listAuthorizationPolicySimulations(
    pageSize?: number,
    pageToken?: string,
    clusterId?: string
  ): Promise<AuthorizationPolicySimulationsResponse> {
    const params: any = {};
    if (pageSize) params.page_size = pageSize;
    if (pageToken) params.page_token = pageToken;
    if (clusterId) params.cluster_id = clusterId;

    const response = await this.client.get('/authorization-policy-simulations', { params });
    return response.data;
  }

  async getAuthorizationPolicySimulation(simulationId: string): Promise<AuthorizationPolicySimulation> {
    const response = await this.client.get(`/authorization-policy-simulations/${simulationId}`);
    return response.data;
  }

  async deleteAuthorizationPolicySimulation(simulationId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/authorization-policy-simulations/${simulationId}`);
    return response.data;
  }

  // Chat History methods
  async listChatSessions(includeDeleted: boolean = false, contextType?: string, contextId?: string): Promise<ChatSessionsResponse> {
    const params = new URLSearchParams();
    if (includeDeleted) {
      params.append('include_deleted', 'true');
    }
    if (contextType) {
      params.append('context_type', contextType);
    }
    if (contextId) {
      params.append('context_id', contextId);
    }
    const response = await this.client.get<ChatSessionsResponse>(`/chat/sessions?${params}`);
    return response.data;
  }

  async createChatSession(request: CreateChatSessionRequest): Promise<ChatSession> {
    const response = await this.client.post<ChatSession>('/chat/sessions', request);
    return response.data;
  }

  async getChatSession(sessionId: string): Promise<ChatSession> {
    const response = await this.client.get<ChatSession>(`/chat/sessions/${sessionId}`);
    return response.data;
  }

  async updateChatSession(sessionId: string, request: UpdateChatSessionRequest): Promise<{ message: string }> {
    const response = await this.client.put<{ message: string }>(`/chat/sessions/${sessionId}`, request);
    return response.data;
  }

  async deleteChatSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/chat/sessions/${sessionId}`);
    return response.data;
  }

  async getSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
    const response = await this.client.get<SessionMessagesResponse>(`/chat/sessions/${sessionId}/messages`);
    return response.data;
  }

  async addSessionMessage(sessionId: string, request: AddMessageRequest): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/chat/sessions/${sessionId}/messages`, request);
    return response.data;
  }

  async loadChatSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/chat/sessions/${sessionId}/load`);
    return response.data;
  }

  async restoreChatSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/chat/sessions/${sessionId}/restore`);
    return response.data;
  }

  // AWS Integration methods
  // type parameter can be 'cloud-ingestion' to filter to Cloud Ingestion roles only and deduplicate
  async getAWSConnectionStatus(type?: 'cloud-ingestion'): Promise<AWSConnectionsResponse> {
    const params = type ? { type } : {};
    const response = await this.client.get('/integrations/aws/status', { params });
    return response.data;
  }

  async bootstrapAWSIntegration(request: AWSBootstrapRequest): Promise<AWSBootstrapResponse> {
    const response = await this.client.post('/integrations/aws/bootstrap', request);
    return response.data;
  }

  async verifyAWSRole(request: AWSVerifyRequest): Promise<AWSVerifyResponse> {
    const response = await this.client.post('/integrations/aws/verify', request);
    return response.data;
  }

  async getCloudIngestionTemplate(region: string): Promise<AWSBootstrapResponse> {
    const response = await this.client.get(`/integrations/aws/cloudformation-template?region=${region}`);
    return response.data;
  }

  async getVPCFlowLogsTemplate(region: string): Promise<AWSBootstrapResponse> {
    const response = await this.client.get(`/integrations/aws/vpc-flow-logs-template?region=${region}`);
    return response.data;
  }

  async getOnPremCloudInventoryTemplate(region: string): Promise<OnPremCloudInventoryTemplateResponse> {
    const response = await this.client.get(`/integrations/aws/cloudformation-template/onprem?region=${region}`);
    return response.data;
  }

  async downloadOnPremCloudInventoryTemplate(): Promise<Blob> {
    const response = await this.client.get('/integrations/aws/cloudformation-template/onprem/download', {
      responseType: 'blob'
    });
    return response.data;
  }

  async getHealthStatus(): Promise<HealthStatus> {
    const response = await this.client.get('/health');
    return response.data;
  }

  async deleteAWSConnection(connectionId: string): Promise<{ message: string }> {
    const response = await this.client.delete('/integrations/aws/disconnect', {
      data: { connection_id: connectionId }
    });
    return response.data;
  }

  // OCI (Oracle Cloud Infrastructure) Integration methods
  async getOCIConnectionStatus(): Promise<OCIConnectionsResponse> {
    const response = await this.client.get('/integrations/oci/status');
    return response.data;
  }

  async getOCISetupInstructions(): Promise<OCISetupInstructionsResponse> {
    const response = await this.client.get('/integrations/oci/setup-instructions');
    return response.data;
  }

  async verifyOCICredentials(request: OCIVerifyRequest): Promise<OCIVerifyResponse> {
    const response = await this.client.post('/integrations/oci/verify', request);
    return response.data;
  }

  async deleteOCIConnection(connectionId: string): Promise<{ message: string }> {
    const response = await this.client.delete('/integrations/oci/disconnect', {
      data: { connection_id: connectionId }
    });
    return response.data;
  }

  async getOCIRegions(): Promise<OCIRegionsResponse> {
    const response = await this.client.get('/integrations/oci/regions');
    return response.data;
  }

  // GCP (Google Cloud) Integration API

  /**
   * Generate the Cloud Shell setup script and derived resource names for a
   * project. Changes nothing in Kestrel or GCP.
   */
  async bootstrapGCP(request: GCPBootstrapRequest): Promise<GCPBootstrapResponse> {
    const response = await this.client.post('/gcp/bootstrap', request);
    return response.data;
  }

  /**
   * Verify Kestrel can impersonate the project's service account, persisting
   * the connection on success.
   */
  async verifyGCPConnection(request: GCPVerifyRequest): Promise<GCPVerifyResponse> {
    const response = await this.client.post('/gcp/verify', request);
    return response.data;
  }

  async getGCPConnections(): Promise<GCPConnection[]> {
    const response = await this.client.get('/gcp/connections');
    return response.data;
  }

  async deleteGCPConnection(connectionId: string): Promise<{ status: string }> {
    const response = await this.client.delete(`/gcp/connections/${connectionId}`);
    return response.data;
  }

  /**
   * Attach or clear the BigQuery billing export table. Separate from connect
   * because customers commonly connect first and enable the export later.
   */
  async updateGCPBillingExport(connectionId: string, billingExportTable: string): Promise<{ status: string; has_billing_export: boolean }> {
    const response = await this.client.put(`/gcp/connections/${connectionId}/billing-export`, {
      billing_export_table: billingExportTable,
    });
    return response.data;
  }

  async getGCPStatus(): Promise<GCPStatusResponse> {
    const response = await this.client.get('/integrations/gcp/status');
    return response.data;
  }

  // GCP workflow dropdown sources. All fail soft server-side (empty list) so a
  // misconfigured project never breaks a builder panel.

  async getGCPZones(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-zones', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPRegions(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-regions', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPBudgetNames(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-budget-names', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPBillingServices(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-billing-services', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  /**
   * Resource names for the GCP cost remediation block dropdowns. Zone applies
   * to instances/disks; region applies to addresses.
   */
  async getGCPResourceNames(
    resourceType: 'instances' | 'disks' | 'addresses' | 'snapshots',
    gcpProject?: string,
    zone?: string,
    region?: string,
  ): Promise<{ key: string; name: string }[]> {
    const endpoints: Record<string, string> = {
      instances: '/workflows/gcp-instance-names',
      disks: '/workflows/gcp-disk-names',
      addresses: '/workflows/gcp-address-names',
      snapshots: '/workflows/gcp-snapshot-names',
    };
    const params: Record<string, string> = {};
    if (gcpProject) params.gcp_project = gcpProject;
    if (zone) params.zone = zone;
    if (region) params.region = region;
    const response = await this.client.get(endpoints[resourceType], {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  }

  // GCP compute/ops dropdown sources (Phase 2).

  async getGCPInstanceGroups(gcpProject?: string, zone?: string): Promise<{ key: string; name: string }[]> {
    const params: Record<string, string> = {};
    if (gcpProject) params.gcp_project = gcpProject;
    if (zone) params.zone = zone;
    const response = await this.client.get('/workflows/gcp-instance-groups', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  }

  async getGCPBuildTriggers(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-build-triggers', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPAlertPolicies(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-alert-policies', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPGKEClusters(gcpProject?: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/workflows/gcp-gke-clusters', {
      params: gcpProject ? { gcp_project: gcpProject } : undefined,
    });
    return response.data;
  }

  async getGCPNodePools(gcpProject?: string, clusterName?: string): Promise<{ key: string; name: string }[]> {
    const params: Record<string, string> = {};
    if (gcpProject) params.gcp_project = gcpProject;
    if (clusterName) params.cluster_name = clusterName;
    const response = await this.client.get('/workflows/gcp-node-pools', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  }

  async getGCPCloudRunServices(gcpProject?: string, region?: string): Promise<{ key: string; name: string }[]> {
    const params: Record<string, string> = {};
    if (gcpProject) params.gcp_project = gcpProject;
    if (region) params.region = region;
    const response = await this.client.get('/workflows/gcp-cloudrun-services', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  }

  // GCP security and data dropdown sources (Phase 3).

  /**
   * Resource names for the GCP security and data block dropdowns. All are
   * project-scoped; Dataflow jobs additionally accept a region.
   */
  async getGCPResourceList(
    kind: 'service-accounts' | 'buckets' | 'sql-instances' | 'subscriptions' | 'dataflow-jobs' | 'datasets',
    gcpProject?: string,
    region?: string,
  ): Promise<{ key: string; name: string }[]> {
    const params: Record<string, string> = {};
    if (gcpProject) params.gcp_project = gcpProject;
    if (region) params.region = region;
    const response = await this.client.get(`/workflows/gcp-${kind}`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  }

  // OCI Resource Ingestion API

  /**
   * Get OCI resources for the tenant
   */
  async getOCIResources(params?: {
    resource_type?: string;
    status?: string;
    oci_tenancy_ocid?: string;
    page?: number;
    page_size?: number;
  }): Promise<OCIResourcesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.resource_type) queryParams.append('resource_type', params.resource_type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.oci_tenancy_ocid) queryParams.append('oci_tenancy_ocid', params.oci_tenancy_ocid);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString();
    const response = await this.client.get(`/oci-resources${query ? `?${query}` : ''}`);
    return response.data;
  }

  /**
   * Get OCI resources summary (count by type and tenancy)
   */
  async getOCIResourcesSummary(): Promise<OCIResourcesSummary> {
    const response = await this.client.get('/oci-resources/summary');
    return response.data;
  }

  /**
   * Get OCI sync status for all connections
   */
  async getOCISyncStatus(): Promise<OCISyncStatusResponse> {
    const response = await this.client.get('/oci-resources/sync-status');
    return response.data;
  }

  /**
   * Trigger an immediate OCI resource sync for a connection
   */
  async triggerOCISync(connectionId: string): Promise<OCITriggerSyncResponse> {
    const response = await this.client.post('/oci-resources/sync', {
      connection_id: connectionId
    });
    return response.data;
  }

  // Cloud Resources API
  // Unified endpoint for AWS and OCI resources. Use cloud_provider to filter:
  // - "aws" for AWS only
  // - "oci" for OCI only
  // - undefined/empty for all providers
  async getCloudResources(params?: {
    account_id?: string;
    resource_type?: string;
    resource_types?: string; // comma-separated list of resource types
    cloud_provider?: 'aws' | 'oci';
    status?: string;
    region?: string;
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<CloudResourcesResponse> {
    // Use POST with body when resource_types is provided to avoid long query strings
    if (params?.resource_types) {
      const response = await this.client.post('/cloud-resources', {
        resource_types: params.resource_types.split(','),
        cloud_provider: params.cloud_provider,
        status: params.status,
        region: params.region,
        search: params.search,
        page: params.page,
        page_size: params.page_size,
      });
      return response.data;
    }

    // Use GET for simple queries (account_id, single resource_type, no type filter)
    const queryParams = new URLSearchParams();
    if (params?.account_id) queryParams.append('account_id', params.account_id);
    if (params?.resource_type) queryParams.append('resource_type', params.resource_type);
    if (params?.cloud_provider) queryParams.append('cloud_provider', params.cloud_provider);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.region) queryParams.append('region', params.region);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    const response = await this.client.get(`/cloud-resources${query ? `?${query}` : ''}`);
    return response.data;
  }

  async getCloudResourcesSummary(accountId?: string): Promise<CloudResourcesSummary> {
    const query = accountId ? `?account_id=${accountId}` : '';
    const response = await this.client.get(`/cloud-resources/summary${query}`);
    return response.data;
  }

  // AWS Account Flow Logs methods

  /**
   * Get VPC flow summaries for an AWS account (account-wide flow log collection)
   */
  async getAWSAccountFlows(connectionId: string, limit?: number): Promise<AWSAccountFlowsResponse> {
    const query = limit ? `?limit=${limit}` : '';
    const response = await this.client.get(`/aws-accounts/${connectionId}/flows${query}`);
    return response.data;
  }

  /**
   * Get VPC Flow Logs status for an AWS account
   */
  async getAWSAccountFlowLogsStatus(connectionId: string): Promise<AWSAccountFlowLogsStatus> {
    const response = await this.client.get(`/aws-accounts/${connectionId}/flow-logs`);
    return response.data;
  }

  /**
   * Enable VPC Flow Logs for all VPCs in an AWS account
   */
  async enableAWSAccountFlowLogs(connectionId: string): Promise<EnableAWSAccountFlowLogsResponse> {
    const response = await this.client.post(`/aws-accounts/${connectionId}/flow-logs`);
    return response.data;
  }

  /**
   * Disable VPC Flow Logs for all VPCs in an AWS account
   */
  async disableAWSAccountFlowLogs(connectionId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/aws-accounts/${connectionId}/flow-logs`);
    return response.data;
  }

  // OCI VCN Flow Logs methods (mirror of AWS VPC Flow Logs for OCI)

  /**
   * Get VCN Flow Logs status for an OCI tenancy
   */
  async getOCITenancyFlowLogsStatus(connectionId: string): Promise<OCITenancyFlowLogsStatus> {
    const response = await this.client.get(`/oci-tenancies/${connectionId}/flow-logs`);
    return response.data;
  }

  /**
   * Enable VCN Flow Logs for all VCNs in an OCI tenancy
   */
  async enableOCITenancyFlowLogs(connectionId: string): Promise<EnableOCITenancyFlowLogsResponse> {
    const response = await this.client.post(`/oci-tenancies/${connectionId}/flow-logs`);
    return response.data;
  }

  /**
   * Disable VCN Flow Logs for all VCNs in an OCI tenancy
   */
  async disableOCITenancyFlowLogs(connectionId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/oci-tenancies/${connectionId}/flow-logs`);
    return response.data;
  }

  /**
   * Get flow summaries for an OCI tenancy
   */
  async getOCITenancyFlows(connectionId: string, limit?: number): Promise<{ tenancy_ocid: string; flows: FlowSummary[]; count: number }> {
    const params = limit ? { limit } : {};
    const response = await this.client.get(`/oci-tenancies/${connectionId}/flows`, { params });
    return response.data;
  }

  // EKS Cluster methods
  async getEKSClusters(): Promise<EKSClustersResponse> {
    const response = await this.client.get('/eks-clusters');
    return response.data;
  }

  async createEKSCluster(request: CreateEKSClusterRequest): Promise<EKSCluster> {
    const response = await this.client.post('/eks-clusters', request);
    return response.data;
  }

  async getEKSCluster(clusterId: string): Promise<EKSCluster> {
    const response = await this.client.get(`/eks-clusters/${clusterId}`);
    return response.data;
  }

  async getEKSClusterProgress(clusterId: string): Promise<EKSClusterProgressResponse> {
    const response = await this.client.get(`/eks-clusters/${clusterId}/progress`);
    return response.data;
  }

  async deleteEKSCluster(clusterId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/eks-clusters/${clusterId}`);
    return response.data;
  }

  async getClusterSizes(): Promise<ClusterSizesResponse> {
    const response = await this.client.get('/cluster-sizes');
    return response.data;
  }

  // VPC Flow Logs methods (for EKS VPC CNI network traffic)
  async getVPCFlowLogsStatus(clusterId: string): Promise<VPCFlowLogsStatus> {
    const response = await this.client.get<VPCFlowLogsStatus>(`/clusters/${clusterId}/vpc-flow-logs`);
    return response.data;
  }

  async enableVPCFlowLogs(clusterId: string, request: EnableVPCFlowLogsRequest): Promise<EnableVPCFlowLogsResponse> {
    const response = await this.client.post<EnableVPCFlowLogsResponse>(`/clusters/${clusterId}/vpc-flow-logs`, request);
    return response.data;
  }

  async disableVPCFlowLogs(clusterId: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/clusters/${clusterId}/vpc-flow-logs`);
    return response.data;
  }

  // Onboarding methods
  async getOnboardingChecklistStatus(): Promise<{ dismissed: boolean }> {
    const response = await this.client.get('/onboarding/checklist-status');
    return response.data;
  }

  async dismissOnboardingChecklist(): Promise<{ success: boolean }> {
    const response = await this.client.post('/onboarding/dismiss-checklist');
    return response.data;
  }

  async getSetupWizardState(): Promise<{ completed: boolean; selected_integrations: string[] }> {
    const response = await this.client.get('/onboarding/setup-state');
    return response.data;
  }

  async updateSetupWizardState(data: {
    completed?: boolean;
    selected_integrations?: string[];
  }): Promise<{ completed: boolean; selected_integrations: string[] }> {
    const response = await this.client.post('/onboarding/setup-state', data);
    return response.data;
  }

  // Stripe Billing methods
  async startFreeTrial(): Promise<StartFreeTrialResponse> {
    const response = await this.client.post('/billing/start-free-trial');
    return response.data;
  }

  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    const response = await this.client.post('/billing/create-checkout-session', request);
    return response.data;
  }

  async createCustomerPortalSession(): Promise<CustomerPortalResponse> {
    const response = await this.client.post('/billing/create-portal-session');
    return response.data;
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    const response = await this.client.get('/billing/subscription-status');
    return response.data;
  }

  async getInvoices(): Promise<InvoicesResponse> {
    const response = await this.client.get('/billing/invoices');
    return response.data;
  }

  // On-Prem License methods
  async getOnPremPricing(): Promise<OnPremPricing> {
    const response = await this.client.get('/onprem/pricing');
    return response.data;
  }

  async startOnPremTrial(request: OnPremTrialRequest): Promise<OnPremCheckoutResponse> {
    const response = await this.client.post('/onprem/trial/start', request);
    return response.data;
  }

  async createOnPremCheckout(request: OnPremCheckoutRequest): Promise<OnPremCheckoutResponse> {
    const response = await this.client.post('/onprem/checkout', request);
    return response.data;
  }

  async getCurrentLicense(includeKey: boolean = false): Promise<OnPremLicense> {
    const response = await this.client.get(`/onprem/license${includeKey ? '?include_key=true' : ''}`);
    return response.data;
  }

  async convertTrialToPaid(): Promise<OnPremLicense> {
    const response = await this.client.post('/onprem/license/convert');
    return response.data;
  }

  // GitHub Integration APIs
  async getGitHubIntegrationStatus(): Promise<GitHubIntegrationStatus> {
    const response = await this.client.get<GitHubIntegrationStatus>('/tenant/github/status');
    return response.data;
  }

  async connectGitHubIntegration(): Promise<GitHubConnectResponse> {
    const response = await this.client.get<GitHubConnectResponse>('/tenant/github/connect');
    return response.data;
  }

  async completeGitHubCallback(request: GitHubCallbackRequest): Promise<GitHubCallbackResponse> {
    const response = await this.client.post<GitHubCallbackResponse>('/tenant/github/callback', request);
    return response.data;
  }

  async getGitHubRepositories(): Promise<GitHubRepositoriesResponse> {
    const response = await this.client.get<GitHubRepositoriesResponse>('/tenant/github/repositories');
    return response.data;
  }

  async getGitHubRepositoryFiles(repositoryId: string, path?: string): Promise<GitHubFilesResponse> {
    const params = new URLSearchParams({ repo_id: repositoryId });
    if (path) {
      params.append('path', path);
    }
    const response = await this.client.get<GitHubFilesResponse>(`/tenant/github/repositories/files?${params.toString()}`);
    return response.data;
  }

  async createGitHubPullRequest(request: CreatePullRequestRequest): Promise<CreatePullRequestResponse> {
    const response = await this.client.post<CreatePullRequestResponse>('/tenant/github/pullrequest/create', request);
    return response.data;
  }

  async createCodeFixPullRequest(request: {
    incident_id: string;
    repository_id: string;
    repo_full_name?: string;
    fixes: Array<{ file_path: string; fixed_content: string; original_content?: string; line_start?: number; line_end?: number }>;
    pr_title: string;
    pr_description: string;
    source_type?: string;
    source_id?: string;
  }): Promise<{ success: boolean; pr_url: string; pr_number: number; branch: string; files_updated: number }> {
    const response = await this.client.post('/incidents/code-fix/create-pr', request);
    return response.data;
  }

  async createCodeFixMergeRequest(request: {
    incident_id: string;
    repository_id: string;
    repo_full_name?: string;
    fixes: Array<{ file_path: string; fixed_content: string; original_content?: string; line_start?: number; line_end?: number }>;
    pr_title: string;
    pr_description: string;
    source_type?: string;
    source_id?: string;
  }): Promise<{ success: boolean; mr_url: string; mr_iid: number; branch: string; files_updated: number }> {
    const response = await this.client.post('/incidents/code-fix/create-mr', request);
    return response.data;
  }

  async retryCodeAnalysis(incidentId: string): Promise<{ status: string; message: string }> {
    const response = await this.client.post(`/incidents/${incidentId}/retry-code-analysis`);
    return response.data;
  }

  async discoverDeployableApplications(repositoryId: string): Promise<any> {
    const response = await this.client.post('/tenant/github/discover-applications', {
      repository_id: repositoryId
    }, {
      timeout: 300000, // 5 minutes timeout for large repository analysis
    });
    return response.data;
  }

  async getRepositoryAnalysis(repositoryId: string): Promise<any> {
    const response = await this.client.get(`/tenant/github/analyses/${repositoryId}`);
    return response.data;
  }

  async listRepositoryAnalyses(): Promise<any> {
    const response = await this.client.get('/tenant/github/analyses');
    return response.data;
  }

  async startDeploymentPreparation(application: any, repositoryOwner: string, repositoryName: string, forceRestart: boolean = false): Promise<any> {
    const payload = {
      repository_id: application.repository_id || '',
      repository_owner: repositoryOwner,
      repository_name: repositoryName,
      application_id: application.id,
      application_name: application.name,
      application_type: application.type,
      application_directory: application.directory_path,
      technology_stack: application.technology_stack,
      entry_points: application.entry_points,
      config_files: application.config_files,
      ports: application.ports,
      health_endpoints: application.health_endpoints,
      estimated_resources: application.estimated_resources,
      environment_variables: application.environment_variables || [],
      volumes_needed: application.volumes_needed || [],
      existing_dockerfile: application.existing_dockerfile || '',
      existing_helm_files: application.existing_helm_files || {},
      force_restart: forceRestart
    };

    const payloadSize = JSON.stringify(payload).length;
    console.log('[APIClient] startDeploymentPreparation payload size:', (payloadSize / 1024).toFixed(2), 'KB');

    try {
      console.log('[APIClient] Sending POST request...');
      const response = await this.client.post('/tenant/github/deployment-preparation/start', payload, {
        timeout: 120000, // 2 minutes timeout
        maxContentLength: 100 * 1024 * 1024, // 100 MB
        maxBodyLength: 100 * 1024 * 1024, // 100 MB
      });
      console.log('[APIClient] Response received:', response.status);
      return response.data;
    } catch (error: any) {
      console.error('[APIClient] Request failed:', error.message);
      if (error.code === 'ECONNABORTED') {
        console.error('[APIClient] Request timeout');
      }
      throw error;
    }
  }

  async getDeploymentPreparationStatus(jobId: string): Promise<any> {
    const response = await this.client.get(`/tenant/github/deployment-preparation/status/${jobId}`);
    return response.data;
  }

  async listDeploymentPreparationJobs(repositoryId?: string): Promise<any> {
    const params = repositoryId ? { repository_id: repositoryId } : {};
    const response = await this.client.get('/tenant/github/deployment-preparation/jobs', { params });
    return response.data;
  }

  async createDeploymentPR(jobId: string): Promise<any> {
    const response = await this.client.post(`/tenant/github/deployment-preparation/create-pr/${jobId}`);
    return response.data;
  }

  // GitLab Integration APIs
  async getGitLabIntegrationStatus(): Promise<GitLabIntegrationStatus> {
    const response = await this.client.get<GitLabIntegrationStatus>('/tenant/gitlab/status');
    return response.data;
  }

  async connectGitLabIntegration(): Promise<GitLabConnectResponse> {
    const response = await this.client.get<GitLabConnectResponse>('/tenant/gitlab/connect');
    return response.data;
  }

  async completeGitLabCallback(request: GitLabCallbackRequest): Promise<GitLabCallbackResponse> {
    const response = await this.client.post<GitLabCallbackResponse>('/tenant/gitlab/callback', request);
    return response.data;
  }

  async disconnectGitLabIntegration(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/tenant/gitlab/disconnect');
    return response.data;
  }

  async getGitLabRepositories(): Promise<GitLabRepositoriesResponse> {
    const response = await this.client.get<GitLabRepositoriesResponse>('/tenant/gitlab/repositories');
    return response.data;
  }

  async getGitLabProjects(search?: string): Promise<GitLabProjectsResponse> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await this.client.get<GitLabProjectsResponse>(`/tenant/gitlab/projects${params}`);
    return response.data;
  }

  async syncGitLabRepositories(projectIds: number[]): Promise<GitLabSyncRepositoriesResponse> {
    const response = await this.client.post<GitLabSyncRepositoriesResponse>('/tenant/gitlab/repositories/sync', {
      project_ids: projectIds
    });
    return response.data;
  }

  async toggleGitLabRepository(repositoryId: string, enabled: boolean): Promise<{ success: boolean; enabled: boolean }> {
    const response = await this.client.post('/tenant/gitlab/repositories/toggle', {
      repository_id: repositoryId,
      enabled: enabled
    });
    return response.data;
  }

  async updateGitLabRepositoryIaCConfig(
    repositoryId: string,
    iacType: string | null,
    iacRootPath: string | null,
    iacEnabled: boolean,
    awsConnectionId?: string | null,
    ociConnectionId?: string | null
  ): Promise<{ success: boolean; iac_enabled: boolean }> {
    const response = await this.client.post('/tenant/gitlab/repositories/iac-config', {
      repository_id: repositoryId,
      iac_type: iacType,
      iac_root_path: iacRootPath,
      iac_enabled: iacEnabled,
      aws_connection_id: awsConnectionId,
      oci_connection_id: ociConnectionId
    });
    return response.data;
  }

  async getGitLabRepositoryFiles(repositoryId: string, path?: string, ref?: string): Promise<any> {
    const params = new URLSearchParams({ repo_id: repositoryId });
    if (path) {
      params.append('path', path);
    }
    if (ref) {
      params.append('ref', ref);
    }
    const response = await this.client.get(`/tenant/gitlab/repositories/files?${params.toString()}`);
    return response.data;
  }

  async createGitLabMergeRequest(request: GitLabCreateMergeRequestRequest): Promise<GitLabCreateMergeRequestResponse> {
    const response = await this.client.post<GitLabCreateMergeRequestResponse>('/tenant/gitlab/mergerequest/create', request);
    return response.data;
  }

  // Traffic Insights methods
  async getTrafficInsights(clusterId?: string, forceRefresh: boolean = false): Promise<any> {
    const params = new URLSearchParams();
    if (clusterId && clusterId !== 'all') {
      params.append('cluster_id', clusterId);
    }
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    
    const response = await this.client.get(`/traffic-insights${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  }

  // Incident Response methods (K8s SRE)
  async listIncidents(clusterId?: string, status?: string, pageSize?: number, pageToken?: string, startTime?: number, endTime?: number, hideTransient?: boolean, search?: string, skipGrouping?: boolean): Promise<ListIncidentsResponse> {
    const params = new URLSearchParams();
    if (clusterId && clusterId !== 'all') {
      params.append('cluster_id', clusterId);
    }
    if (status && status !== 'all') {
      params.append('status', status);
    }
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (startTime !== undefined) {
      params.append('start_time', startTime.toString());
    }
    if (endTime !== undefined) {
      params.append('end_time', endTime.toString());
    }
    if (hideTransient !== undefined) {
      params.append('hide_transient', hideTransient.toString());
    }
    if (search) {
      params.append('search', search);
    }
    if (skipGrouping) {
      params.append('skip_grouping', 'true');
    }

    const response = await this.client.get<ListIncidentsResponse>(`/incidents${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  }

  async resolveAllClusterSchedulingIncidents(clusterId: string, hideTransient: boolean = true): Promise<{ success: boolean; resolved_count: number; failed_count: number }> {
    const response = await this.client.post<{ success: boolean; resolved_count: number; failed_count: number }>(
      '/incidents/cluster-scheduling/resolve-all',
      { cluster_id: clusterId, hide_transient: hideTransient }
    );
    return response.data;
  }

  async listClusterSchedulingIncidents(
    clusterId: string,
    hideTransient?: boolean,
    pageSize?: number,
    pageToken?: string,
    startTime?: number,
    endTime?: number,
    status?: string,
    search?: string,
  ): Promise<ListIncidentsResponse> {
    const params = new URLSearchParams();
    params.append('cluster_id', clusterId);
    if (hideTransient !== undefined) {
      params.append('hide_transient', hideTransient.toString());
    }
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (startTime) {
      params.append('start_time', startTime.toString());
    }
    if (endTime) {
      params.append('end_time', endTime.toString());
    }
    if (status) {
      params.append('status', status);
    }
    if (search) {
      params.append('search', search);
    }
    const response = await this.client.get<ListIncidentsResponse>(`/incidents/cluster-scheduling?${params.toString()}`);
    return response.data;
  }

  async getIncidentDetails(incidentId: string): Promise<IncidentDetails> {
    const response = await this.client.get<IncidentDetails>(`/incidents/${incidentId}`);
    return response.data;
  }

  async applyIncidentYAMLFix(incidentId: string, fixIndex: number): Promise<{ success: boolean; message: string; error_message?: string }> {
    // Get incident details to extract cluster_id, tenant_id, and YAML content
    const incident = await this.getIncidentDetails(incidentId);

    if (!incident.rca_result || !incident.rca_result.yaml_fixes || incident.rca_result.yaml_fixes.length === 0) {
      return {
        success: false,
        message: 'No YAML fixes available',
        error_message: 'This incident has no YAML fixes to apply'
      };
    }

    if (fixIndex < 0 || fixIndex >= incident.rca_result.yaml_fixes.length) {
      return {
        success: false,
        message: 'Invalid fix index',
        error_message: `Fix index ${fixIndex} is out of range`
      };
    }

    const fix = incident.rca_result.yaml_fixes[fixIndex];

    // Validate YAML content exists
    if (!fix.yaml_content || fix.yaml_content.trim() === '') {
      return {
        success: false,
        message: 'Empty YAML content',
        error_message: 'The YAML fix has no content to apply. This may be a generation error.'
      };
    }

    // Generate a unique request ID
    const requestId = `incident_${incidentId}_fix_${fixIndex}_${Date.now()}`;

    // Call router directly (same host as WebSocket, port routing handled by nginx/proxy)
    const routerRequest = {
      cluster_id: incident.cluster_id,
      tenant_id: incident.tenant_id,
      request_id: requestId,
      manifests: [
        {
          manifest_id: `incident_${incidentId}_fix_${fixIndex}`,
          yaml_content: fix.yaml_content,
          resource_type: fix.resource_kind || '',
          namespace: fix.resource_namespace || ''
        }
      ],
      timeout_seconds: 60
    };

    try {
      // Call router's YAML apply endpoint
      const response = await this.client.post<{ request_id: string; success: boolean; message: string; error?: string }>(
        '/yaml-apply',
        routerRequest
      );

      if (response.data.success) {
        // Status is now automatically updated by the server when YAML is applied
        // via updateIncidentRemediationStatus() in rest_internal_yaml_apply.go
        return {
          success: true,
          message: response.data.message || 'YAML fix applied successfully'
        };
      } else {
        return {
          success: false,
          message: 'YAML apply failed',
          error_message: response.data.error || 'Unknown error from router'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to apply YAML fix',
        error_message: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  }

  async markIncidentAsResolved(incidentId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>(
      `/incidents/${incidentId}/mark-resolved`
    );
    return response.data;
  }

  async validateIncidentFix(incidentId: string): Promise<{
    success: boolean;
    validation_status: string;
    validation_summary: string;
    validation_details: string[];
    checked_at: string;
  }> {
    const response = await this.client.post(
      `/incidents/${incidentId}/validate-fix`
    );
    return response.data;
  }

  async generateIncidentPostMortem(incidentId: string): Promise<{
    incident_id: string;
    postmortem_summary: string;
    what_went_well: string[];
    what_could_improve: string[];
    lessons_learned: Array<{
      lesson: string;
      severity: string;
      category: string;
    }>;
    prevention_recommendations: Array<{
      recommendation: string;
      priority: string;
      effort: string;
      category: string;
    }>;
    similar_incidents_to_monitor: string[];
    impact_assessment: {
      user_impact: string;
      duration_minutes: number;
      affected_users: string;
      business_impact: string;
      technical_impact: string;
    };
    detection_effectiveness: {
      time_to_detect: string;
      detection_method: string;
      effectiveness_rating: string;
      improvements: string;
    };
    remediation_effectiveness: {
      time_to_resolve: string;
      fix_success: string;
      effectiveness_rating: string;
      improvements: string;
    };
    status: string;
    error?: string;
  }> {
    const response = await this.client.post(
      `/incidents/${incidentId}/postmortem`
    );
    return response.data;
  }

  async updateImpactAssessment(incidentId: string, impactData: {
    user_impact: string;
    duration_minutes: number;
    affected_users: string;
    business_impact: string;
    technical_impact: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put(
      `/incidents/${incidentId}/postmortem/impact-assessment`,
      impactData
    );
    return response.data;
  }

  async getRecentIncidents(limit?: number): Promise<{ incidents: IncidentSummary[] }> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    const url = `/activity/recent-incidents${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get<{ incidents?: IncidentSummary[] }>(url);
    // Ensure we always return a valid structure (handle {} response from server)
    return { incidents: response.data.incidents || [] };
  }

  // Cloud Incident Response methods (AWS)
  async listCloudIncidents(
    awsConnectionId?: string, 
    awsAccountId?: string, 
    status?: string, 
    pageSize?: number, 
    pageToken?: string, 
    startTime?: number, 
    endTime?: number, 
    hideTransient?: boolean,
    search?: string  // Search query to filter across type, account, resources
  ): Promise<ListCloudIncidentsResponse> {
    const params = new URLSearchParams();
    if (awsConnectionId && awsConnectionId !== 'all') {
      params.append('aws_connection_id', awsConnectionId);
    }
    if (awsAccountId && awsAccountId !== 'all') {
      params.append('aws_account_id', awsAccountId);
    }
    if (status && status !== 'all') {
      params.append('status', status);
    }
    if (pageSize !== undefined) {
      params.append('page_size', pageSize.toString());
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }
    if (startTime !== undefined) {
      params.append('start_time', startTime.toString());
    }
    if (endTime !== undefined) {
      params.append('end_time', endTime.toString());
    }
    if (hideTransient !== undefined) {
      params.append('hide_transient', hideTransient.toString());
    }
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    const response = await this.client.get<ListCloudIncidentsResponse>(`/cloud-incidents${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  }

  async getCloudIncidentDetails(incidentId: string): Promise<CloudIncidentSummary> {
    const response = await this.client.get<CloudIncidentSummary>(`/cloud-incidents/${incidentId}`);
    return response.data;
  }

  async getCloudIncidentStats(awsConnectionId?: string): Promise<CloudIncidentStatsResponse> {
    const params = new URLSearchParams();
    if (awsConnectionId) {
      params.append('aws_connection_id', awsConnectionId);
    }
    const response = await this.client.get<CloudIncidentStatsResponse>(`/cloud-incidents-stats${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  }

  /**
   * Get cloud incidents affecting a specific resource
   * Supports both AWS and OCI resources (and future cloud providers)
   *
   * @param options.arn - Resource ARN (AWS) or OCID (OCI)
   * @param options.resourceId - Resource ID
   * @param options.cloudProvider - Filter by cloud provider ("aws", "oci")
   * @param options.awsConnectionId - AWS connection ID filter
   * @param options.awsAccountId - AWS account ID filter
   * @param options.ociConnectionId - OCI connection ID filter
   * @param options.ociTenancyOcid - OCI tenancy OCID filter
   */
  async getCloudIncidentsByResource(options: {
    arn?: string;
    resourceId?: string;
    cloudProvider?: 'aws' | 'oci';
    // AWS-specific
    awsConnectionId?: string;
    awsAccountId?: string;
    // OCI-specific (for future OCI incident support)
    ociConnectionId?: string;
    ociTenancyOcid?: string;
  }): Promise<{ incidents: CloudIncidentSummary[]; count: number }> {
    // Validate that at least one resource identifier is provided
    if (!options.arn && !options.resourceId) {
      throw new Error('getCloudIncidentsByResource: arn or resourceId required');
    }

    // When using resourceId (without arn), require a scoping parameter
    if (!options.arn && options.resourceId) {
      const hasScope = options.awsAccountId || options.awsConnectionId || options.ociConnectionId || options.ociTenancyOcid;
      if (!hasScope) {
        throw new Error('getCloudIncidentsByResource: when using resourceId, provide one of awsAccountId/awsConnectionId/ociConnectionId/ociTenancyOcid');
      }
    }

    const params = new URLSearchParams();
    if (options.arn) {
      params.append('arn', options.arn);
    }
    if (options.resourceId) {
      params.append('resource_id', options.resourceId);
    }
    if (options.cloudProvider) {
      params.append('cloud_provider', options.cloudProvider);
    }
    // AWS params
    if (options.awsConnectionId) {
      params.append('aws_connection_id', options.awsConnectionId);
    }
    if (options.awsAccountId) {
      params.append('aws_account_id', options.awsAccountId);
    }
    // OCI params
    if (options.ociConnectionId) {
      params.append('oci_connection_id', options.ociConnectionId);
    }
    if (options.ociTenancyOcid) {
      params.append('oci_tenancy_ocid', options.ociTenancyOcid);
    }
    const response = await this.client.get<{ incidents: CloudIncidentSummary[]; count: number }>(`/cloud-incidents/resource-incidents?${params.toString()}`);
    return response.data;
  }

  async markCloudIncidentResolved(incidentId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/cloud-incidents/${incidentId}/mark-resolved`);
    return response.data;
  }

  async updateCloudIncidentStatus(incidentId: string, status: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/cloud-incidents/${incidentId}/update-status`, { status });
    return response.data;
  }

  async triggerCloudIncidentRCA(incidentId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/cloud-incidents/${incidentId}/trigger-rca`);
    return response.data;
  }

  async retryCloudCodeAnalysis(incidentId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/cloud-incidents/${incidentId}/retry-code-analysis`);
    return response.data;
  }

  async applyCloudIncidentFix(incidentId: string, fixIndex: number): Promise<ApplyFixResponse> {
    const response = await this.client.post<ApplyFixResponse>(`/cloud-incidents/${incidentId}/apply-fix`, {
      fix_index: fixIndex,
    });
    return response.data;
  }

  async rollbackCloudIncidentFix(incidentId: string, fixIndex: number): Promise<RollbackFixResponse> {
    const response = await this.client.post<RollbackFixResponse>(`/cloud-incidents/${incidentId}/rollback-fix`, {
      fix_index: fixIndex,
    });
    return response.data;
  }

  async updateFixPlaceholders(incidentId: string, fixIndex: number, placeholders: CLIPlaceholder[]): Promise<UpdateFixPlaceholdersResponse> {
    const response = await this.client.post<UpdateFixPlaceholdersResponse>(`/cloud-incidents/${incidentId}/update-fix-placeholders`, {
      fix_index: fixIndex,
      placeholders,
    });
    return response.data;
  }

  async validateCloudIncidentFix(incidentId: string, fixIndex: number): Promise<ValidateCloudFixResponse> {
    const response = await this.client.post<ValidateCloudFixResponse>(`/cloud-incidents/${incidentId}/validate-fix`, {
      fix_index: fixIndex,
    });
    return response.data;
  }

  // IaC (Terraform) Integration methods
  async listIaCRepos(): Promise<{ iac_repos: IaCRepoConfig[] }> {
    const response = await this.client.get('/iac/repos');
    return response.data;
  }

  async enableIaCRepo(repositoryId: string, iacType: string, rootPath: string, cloudConnectionId?: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/iac/repos', {
      repository_id: repositoryId,
      iac_type: iacType,
      root_path: rootPath,
      cloud_connection_id: cloudConnectionId, // Backend will determine if AWS or OCI
    });
    return response.data;
  }

  async disableIaCRepo(repositoryId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/iac/repos/${repositoryId}`);
    return response.data;
  }

  async triggerIaCAnalysis(repositoryId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/iac/analyze', {
      repository_id: repositoryId,
    });
    return response.data;
  }

  async getCloudIncidentTerraformFixes(incidentId: string): Promise<TerraformFixesResponse> {
    const response = await this.client.get<TerraformFixesResponse>(`/cloud-incidents/terraform-fixes/${incidentId}`);
    return response.data;
  }

  async generateTerraformFix(
    incidentId: string,
    repositoryId: string,
    cliCommand: string,
    cliExplanation: string
  ): Promise<TerraformFix> {
    const response = await this.client.post<TerraformFix>(`/cloud-incidents/terraform-fixes/${incidentId}`, {
      repository_id: repositoryId,
      cli_command: cliCommand,
      cli_explanation: cliExplanation,
    });
    return response.data;
  }

  async createTerraformPR(
    incidentId: string,
    fixId: string,
    title?: string,
    description?: string
  ): Promise<CreateTerraformPRResponse> {
    const response = await this.client.post<CreateTerraformPRResponse>(
      `/cloud-incidents/terraform-fixes/${incidentId}/create-pr`,
      {
        fix_id: fixId,
        title,
        description,
      }
    );
    return response.data;
  }

  // Tribal Knowledge Integration methods
  async listTribalKnowledgeSources(): Promise<{ sources: TribalKnowledgeSourceConfig[] }> {
    const response = await this.client.get('/tribal-knowledge/sources');
    return response.data;
  }

  async createTribalKnowledgeSource(
    source: Omit<TribalKnowledgeSourceConfig, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>
  ): Promise<{ message: string; source: TribalKnowledgeSourceConfig }> {
    const response = await this.client.post('/tribal-knowledge/sources', source);
    return response.data;
  }

  async updateTribalKnowledgeSource(
    sourceId: string,
    updates: Partial<TribalKnowledgeSourceConfig>
  ): Promise<{ message: string; source: TribalKnowledgeSourceConfig }> {
    const response = await this.client.put(`/tribal-knowledge/sources/${sourceId}`, updates);
    return response.data;
  }

  async deleteTribalKnowledgeSource(sourceId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/tribal-knowledge/sources/${sourceId}`);
    return response.data;
  }

  async testTribalKnowledgeSource(sourceId: string): Promise<{ success: boolean; message: string; sample_results?: number }> {
    const response = await this.client.post(`/tribal-knowledge/sources/${sourceId}/test`);
    return response.data;
  }

  // Slack Integration methods
  async getSlackIntegrationStatus(): Promise<{
    connected: boolean;
    team_name?: string;
    team_id?: string;
    installed_by?: string;
    installed_at?: number;
  }> {
    const response = await this.client.get('/integrations/slack/status');
    return response.data;
  }

  async getSlackInstallUrl(): Promise<{ install_url: string; scopes: string[] }> {
    const response = await this.client.get('/integrations/slack/install-url');
    return response.data;
  }

  async connectSlackWorkspace(code: string, state?: string): Promise<{
    success: boolean;
    team_name: string;
    team_id: string;
    message: string;
  }> {
    const response = await this.client.post('/integrations/slack/oauth', { code, state });
    return response.data;
  }

  async disconnectSlackWorkspace(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/slack/disconnect');
    return response.data;
  }

  async syncSlackUsers(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/slack/sync-users');
    return response.data;
  }

  async createFounderSlackChannel(): Promise<{ success: boolean; channel_name: string; invite_url?: string; already_existed: boolean; message: string }> {
    const response = await this.client.post('/slack-connect/founder-channel');
    return response.data;
  }

  // PagerDuty Integration methods
  async getPagerDutyStatus(): Promise<PagerDutyStatus> {
    const response = await this.client.get('/integrations/pagerduty/status');
    return response.data;
  }

  async connectPagerDuty(params: {
    // Enhanced flow
    api_token?: string;
    webhook_secret?: string;
    // Legacy flow
    routing_key?: string;
    service_name?: string;
    service_id?: string;
  }): Promise<{
    success: boolean;
    message: string;
    service_name?: string;
    webhook_url?: string;
    needs_sync?: boolean;
  }> {
    const response = await this.client.post('/integrations/pagerduty/connect', params);
    return response.data;
  }

  // Legacy overload for backward compatibility
  async connectPagerDutyLegacy(routingKey: string, serviceName: string, serviceId?: string): Promise<{
    success: boolean;
    message: string;
    service_name: string;
  }> {
    const result = await this.connectPagerDuty({
      routing_key: routingKey,
      service_name: serviceName,
      service_id: serviceId,
    });
    // Legacy flow always returns service_name
    return {
      success: result.success,
      message: result.message,
      service_name: result.service_name || serviceName,
    };
  }

  async disconnectPagerDuty(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/pagerduty/disconnect');
    return response.data;
  }

  async testPagerDuty(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/pagerduty/test');
    return response.data;
  }

  async getPagerDutyConfig(): Promise<PagerDutyConfig> {
    const response = await this.client.get('/integrations/pagerduty/config');
    return response.data;
  }

  async updatePagerDutyConfig(config: Partial<PagerDutyConfig>): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put('/integrations/pagerduty/config', config);
    return response.data;
  }

  // PagerDuty Services methods
  async syncPagerDutyServices(): Promise<{ success: boolean; synced_count: number; total_count: number }> {
    const response = await this.client.post('/integrations/pagerduty/sync');
    return response.data;
  }

  async getPagerDutyServices(): Promise<PagerDutyService[]> {
    const response = await this.client.get('/integrations/pagerduty/services');
    // Map backend field pagerduty_service_description to interface field description
    return (response.data || []).map((svc: PagerDutyService & { pagerduty_service_description?: string }) => ({
      ...svc,
      description: svc.pagerduty_service_description ?? svc.description,
    }));
  }

  async configurePagerDutyService(serviceId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/integrations/pagerduty/services/${serviceId}/configure`);
    return response.data;
  }

  async updatePagerDutyService(serviceId: string, isDefault: boolean): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put(`/integrations/pagerduty/services/${serviceId}`, { is_default: isDefault });
    return response.data;
  }

  // PagerDuty Namespace Assignments methods
  async getPagerDutyNamespaceAssignments(): Promise<PagerDutyNamespaceAssignment[]> {
    const response = await this.client.get('/integrations/pagerduty/namespace-assignments');
    return response.data;
  }

  async createPagerDutyNamespaceAssignment(assignment: {
    service_id: string;
    namespace_id: string;
    namespace_name: string;
    cluster_id: string;
    cluster_name?: string;
  }): Promise<{ success: boolean; id: string }> {
    const response = await this.client.post('/integrations/pagerduty/namespace-assignments', assignment);
    return response.data;
  }

  async deletePagerDutyNamespaceAssignment(assignmentId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/integrations/pagerduty/namespace-assignments/${assignmentId}`);
    return response.data;
  }

  // PagerDuty Cloud Resource Assignments methods
  async getPagerDutyCloudResourceAssignments(): Promise<PagerDutyCloudResourceAssignment[]> {
    const response = await this.client.get('/integrations/pagerduty/cloud-resource-assignments');
    return response.data;
  }

  async createPagerDutyCloudResourceAssignment(assignment: {
    service_id: string;
    cloud_resource_id: string;
    resource_name?: string;
    resource_type: string;
    resource_arn?: string;
    cloud_provider: string;
    aws_account_id?: string;
    region?: string;
  }): Promise<{ success: boolean; id: string }> {
    const response = await this.client.post('/integrations/pagerduty/cloud-resource-assignments', assignment);
    return response.data;
  }

  async deletePagerDutyCloudResourceAssignment(assignmentId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/integrations/pagerduty/cloud-resource-assignments/${assignmentId}`);
    return response.data;
  }

  async getIncidentStats(startTime?: number, endTime?: number): Promise<{
    total_incidents: number;
    analyzed_count: number;
    remediated_count: number;
    resolved_count: number;
    investigating_count: number;
    critical_count: number;
    high_count: number;
  }> {
    const params = new URLSearchParams();
    if (startTime !== undefined) {
      params.append('start_time', startTime.toString());
    }
    if (endTime !== undefined) {
      params.append('end_time', endTime.toString());
    }
    
    const url = `/incidents-stats${params.toString() ? '?' + params.toString() : ''}`;
    const response = await this.client.get(url);
    return response.data;
  }

  // Executive Summary PDF generation
  async generateExecutiveSummary(): Promise<Blob> {
    const response = await this.client.get('/executive-summary/generate', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Known IPs API methods
  async listKnownIPs(): Promise<KnownIPsResponse> {
    const response = await this.client.get<KnownIPsResponse>('/known-ips');
    return response.data;
  }

  async createKnownIP(request: CreateKnownIPRequest): Promise<KnownIP> {
    const response = await this.client.post<KnownIP>('/known-ips', request);
    return response.data;
  }

  async updateKnownIP(id: string, request: UpdateKnownIPRequest): Promise<KnownIP> {
    const response = await this.client.put<KnownIP>(`/known-ips/${id}`, request);
    return response.data;
  }

  async deleteKnownIP(id: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/known-ips/${id}`);
    return response.data;
  }

  // =============================================================================
  // On-Prem Deployment Methods
  // =============================================================================

  async getOnPremReleases(): Promise<OnPremRelease[]> {
    const response = await this.client.get<OnPremRelease[]>('/onprem/releases');
    return response.data;
  }

  async getLatestOnPremRelease(): Promise<OnPremRelease> {
    const response = await this.client.get<OnPremRelease>('/onprem/releases/latest');
    return response.data;
  }

  async getSupportedLLMModels(): Promise<SupportedLLMModel[]> {
    const response = await this.client.get<SupportedLLMModel[]>('/onprem/llm-models');
    return response.data;
  }

  async getOnPremStatus(): Promise<OnPremStatus> {
    const response = await this.client.get<OnPremStatus>('/onprem/status');
    return response.data;
  }

  async createOrUpdateOnPremDeployment(data: CreateOnPremDeploymentRequest): Promise<OnPremDeployment> {
    const response = await this.client.post<OnPremDeployment>('/onprem/deployment', data);
    return response.data;
  }

  async generateHelmValues(data: GenerateHelmValuesRequest): Promise<GenerateHelmValuesResponse> {
    const response = await this.client.post<GenerateHelmValuesResponse>('/onprem/generate-values', data);
    return response.data;
  }

  async generateTLSCert(hostname: string): Promise<GenerateTLSCertResponse> {
    const response = await this.client.post<GenerateTLSCertResponse>('/onprem/generate-tls-cert', { hostname });
    return response.data;
  }

  async recordOnPremDownload(data: RecordDownloadRequest): Promise<OnPremDownloadHistory> {
    const response = await this.client.post<OnPremDownloadHistory>('/onprem/download', data);
    return response.data;
  }

  async markOnPremDeployed(data: MarkDeployedRequest): Promise<OnPremDeployment> {
    const response = await this.client.post<OnPremDeployment>('/onprem/mark-deployed', data);
    return response.data;
  }

  async getOnPremPullCredentials(): Promise<OnPremPullCredentialsResponse> {
    const response = await this.client.get<OnPremPullCredentialsResponse>('/onprem/pull-credentials');
    return response.data;
  }

  async requestOnPremImagesBundleURL(): Promise<OnPremImagesBundleResponse> {
    const response = await this.client.post<OnPremImagesBundleResponse>('/onprem/images-bundle-url');
    return response.data;
  }

  /**
   * Download the on-prem Helm chart.
   * Uses fetch with auth headers and triggers a file download.
   * The download is handled by our API which authenticates with GitHub to fetch from the private repo.
   */
  async downloadOnPremHelmChart(version: string = 'v1.0.0'): Promise<void> {
    const response = await this.client.get(`/onprem/helm-chart?version=${version}`, {
      responseType: 'blob',
    });
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `kestrel-${version.replace('v', '')}.tgz`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
    
    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/gzip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // ── Workflow Builder ──────────────────────────────────────────────────

  async listWorkflows(statusFilter?: string): Promise<WorkflowSummary[]> {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await this.client.get<WorkflowSummary[]>('/workflows', { params });
    return response.data;
  }

  async listWorkflowsPaginated(page: number, limit: number, filters?: { status?: string; name?: string; created_by?: string; updated_since?: string; updated_until?: string; integrations?: string }): Promise<{ workflows: WorkflowSummary[]; total: number; page: number; limit: number }> {
    const params: Record<string, string | number> = { page, limit };
    if (filters?.status) params.status = filters.status;
    if (filters?.name) params.name = filters.name;
    if (filters?.created_by) params.created_by = filters.created_by;
    if (filters?.updated_since) params.updated_since = filters.updated_since;
    if (filters?.updated_until) params.updated_until = filters.updated_until;
    if (filters?.integrations) params.integrations = filters.integrations;
    const response = await this.client.get('/workflows', { params });
    return response.data;
  }

  async getWorkflow(id: string): Promise<WorkflowSummary> {
    const response = await this.client.get<WorkflowSummary>(`/workflows/${id}`);
    return response.data;
  }

  async createWorkflow(data: CreateWorkflowRequest): Promise<WorkflowSummary> {
    const response = await this.client.post<WorkflowSummary>('/workflows', data);
    return response.data;
  }

  async updateWorkflow(id: string, data: UpdateWorkflowRequest): Promise<WorkflowSummary> {
    const response = await this.client.put<WorkflowSummary>(`/workflows/${id}`, data);
    return response.data;
  }

  async deleteWorkflow(id: string): Promise<void> {
    await this.client.delete(`/workflows/${id}`);
  }

  async activateWorkflow(id: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflows/${id}/activate`);
    return response.data;
  }

  async pauseWorkflow(id: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflows/${id}/pause`);
    return response.data;
  }

  async testWorkflow(id: string, customSignal?: Record<string, any>): Promise<WorkflowExecution> {
    const body = customSignal && Object.keys(customSignal).length > 0 ? { custom_signal: customSignal } : undefined;
    const response = await this.client.post<WorkflowExecution>(`/workflows/${id}/test`, body);
    return response.data;
  }

  async getWorkflowTestDefaults(id: string): Promise<{ source: string; signal_type: string; fields: Record<string, any> }> {
    const response = await this.client.get<{ source: string; signal_type: string; fields: Record<string, any> }>(`/workflows/${id}/test-defaults`);
    return response.data;
  }

  async duplicateWorkflow(id: string, name: string): Promise<WorkflowSummary> {
    const response = await this.client.post<WorkflowSummary>(`/workflows/${id}/duplicate`, { name });
    return response.data;
  }

  async getWorkflowCatalog(): Promise<WorkflowCatalog> {
    const path = IS_DEMO_MODE ? '/public/demo/workflows/catalog' : '/workflows/catalog';
    const response = await this.client.get<WorkflowCatalog>(path);
    return response.data;
  }

  async getWorkflowStats(): Promise<WorkflowStats> {
    const response = await this.client.get<WorkflowStats>('/workflows/stats');
    return response.data;
  }

  async getWorkflowObservability(): Promise<WorkflowObservabilityData> {
    const response = await this.client.get<WorkflowObservabilityData>('/workflows/observability');
    return response.data;
  }

  async getWorkflowIntegrationsStatus(): Promise<WorkflowIntegrationStatus[]> {
    const path = IS_DEMO_MODE ? '/public/demo/workflows/integrations/status' : '/workflows/integrations/status';
    const response = await this.client.get<WorkflowIntegrationStatus[]>(path);
    return response.data;
  }

  async getWorkflowTriggerFilterOptions(clusterIds?: string[], namespaces?: string[]): Promise<{
    namespaces: { id: string; name: string; cluster_id: string }[];
    workloads: { name: string; kind: string; namespace: string; cluster_id: string }[];
  }> {
    const params = new URLSearchParams();
    if (clusterIds && clusterIds.length > 0 && !(clusterIds.length === 1 && clusterIds[0] === '*')) {
      params.append('cluster_ids', clusterIds.join(','));
    }
    if (namespaces && namespaces.length > 0 && !(namespaces.length === 1 && namespaces[0] === '*')) {
      params.append('namespaces', namespaces.join(','));
    }
    const response = await this.client.get('/workflows/trigger-filter-options', { params });
    return response.data;
  }

  async generateWorkflow(prompt: string, simulated?: boolean): Promise<GenerateWorkflowResponse> {
    const path = IS_DEMO_MODE ? '/public/demo/workflows/generate' : '/workflows/generate';
    const response = await this.client.post<GenerateWorkflowResponse>(path, { prompt, ...(simulated ? { simulated: true } : {}) });
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Public demo builder (demo.usekestrel.ai) — shareable workflow snapshots.
  // -------------------------------------------------------------------------

  async demoShareWorkflow(payload: {
    name: string;
    description?: string;
    definition: WorkflowDefinition;
    trigger_config: any;
    nl_prompt?: string;
  }): Promise<{ success: boolean; id: string }> {
    const response = await this.client.post<{ success: boolean; id: string }>('/public/demo/workflows/share', payload);
    return response.data;
  }

  async demoGetSharedWorkflow(id: string): Promise<{
    id: string;
    name: string;
    description: string;
    definition: WorkflowDefinition;
    trigger_config: any;
    nl_prompt: string;
  }> {
    const response = await this.client.get(`/public/demo/workflows/shared/${encodeURIComponent(id)}`);
    return response.data;
  }

  async demoShareBundle(shareIds: string[]): Promise<{ success: boolean; id: string }> {
    const response = await this.client.post<{ success: boolean; id: string }>('/public/demo/workflows/share-bundle', { share_ids: shareIds });
    return response.data;
  }

  async demoGetSharedBundle(id: string): Promise<{
    id: string;
    workflows: Array<{
      id: string;
      name: string;
      description: string;
      definition: WorkflowDefinition;
      trigger_config: any;
      nl_prompt: string;
    }>;
  }> {
    const response = await this.client.get(`/public/demo/workflows/bundles/${encodeURIComponent(id)}`);
    return response.data;
  }

  /**
   * Kick off suggestion generation. Passing no integrations generates from
   * whatever is actually connected; passing integrations generates *simulated*
   * suggestions for them, letting a customer with an empty environment explore
   * what Kestrel would automate.
   */
  async suggestWorkflows(integrations?: string[]): Promise<{ success: boolean; status?: string; message?: string; simulated?: boolean; suggestions: SuggestedWorkflow[]; error?: string }> {
    const body = integrations?.length ? { integrations, simulated: true } : {};
    const response = await this.client.post<{ success: boolean; status?: string; message?: string; simulated?: boolean; suggestions: SuggestedWorkflow[]; error?: string }>('/workflows/suggest', body);
    return response.data;
  }

  /** Integrations that can produce suggestions, plus the per-run selection cap. */
  async getSuggestionSources(): Promise<{ sources: SuggestionSource[]; max_sources: number }> {
    const response = await this.client.get<{ sources: SuggestionSource[]; max_sources: number }>('/workflows/suggestion-sources');
    return response.data;
  }

  async getSuggestedWorkflows(): Promise<SuggestedWorkflow[]> {
    const response = await this.client.get<SuggestedWorkflow[]>('/workflows/suggestions');
    return response.data;
  }

  async dismissSuggestedWorkflows(): Promise<void> {
    await this.client.delete('/workflows/suggestions');
  }

  async saveSuggestedWorkflow(suggestion: { name: string; description: string; definition: any; trigger_config: any; nl_prompt?: string; simulated?: boolean }): Promise<void> {
    await this.client.post('/workflows/suggestions/save', suggestion);
  }

  async getWorkflowRequests(page = 1, perPage = 50, mine = false, status?: string): Promise<{ requests: WorkflowRequest[]; total: number; page: number; per_page: number; total_pages: number }> {
    const response = await this.client.get<{ requests: WorkflowRequest[]; total: number; page: number; per_page: number; total_pages: number }>('/workflow-requests', { params: { page, per_page: perPage, ...(mine ? { mine: 'true' } : {}), ...(status ? { status } : {}) } });
    return response.data;
  }

  async workflowRequestAction(requestId: string, action: 'approve' | 'reject' | 'create-and-approve'): Promise<any> {
    const response = await this.client.post(`/workflow-requests/${requestId}/${action}`);
    return response.data;
  }

  async getPendingJustification(requestId: string): Promise<{ pending: boolean; approval_id?: string; prompt_message?: string }> {
    const response = await this.client.get<{ pending: boolean; approval_id?: string; prompt_message?: string }>(`/workflow-requests/${requestId}/pending-justification`);
    return response.data;
  }

  async resolveJustification(requestId: string, justificationText: string): Promise<any> {
    const response = await this.client.post(`/workflow-requests/${requestId}/resolve-justification`, { justification_text: justificationText });
    return response.data;
  }

  async confirmWorkflowRequest(requestId: string, confirm: boolean): Promise<{ id: string; status: string; summary: string }> {
    const response = await this.client.post<{ id: string; status: string; summary: string }>(`/workflow-requests/${requestId}/confirm`, { confirm });
    return response.data;
  }

  async getWorkflowRequestCatalog(): Promise<{ workflows: { id: string; name: string; description?: string; categories?: string[]; keywords?: string[] }[] }> {
    const response = await this.client.get<{ workflows: { id: string; name: string; description?: string; categories?: string[]; keywords?: string[] }[] }>('/workflows/request-catalog');
    return response.data;
  }

  async requestIntegration(toolName: string, description: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>('/workflows/request-integration', { tool_name: toolName, description });
    return response.data;
  }

  async listCustomBlocks(): Promise<CustomBlock[]> {
    const response = await this.client.get<CustomBlock[]>('/custom-blocks');
    return response.data;
  }

  async createCustomBlock(input: CustomBlockInput): Promise<CustomBlock> {
    const response = await this.client.post<CustomBlock>('/custom-blocks', input);
    return response.data;
  }

  async updateCustomBlock(id: string, input: CustomBlockInput): Promise<CustomBlock> {
    const response = await this.client.put<CustomBlock>(`/custom-blocks/${id}`, input);
    return response.data;
  }

  async deleteCustomBlock(id: string): Promise<void> {
    await this.client.delete(`/custom-blocks/${id}`);
  }

  async parseCurlToBlock(curlCommand: string, exampleResponse?: string): Promise<ParseCurlResult> {
    const body: any = { curl_command: curlCommand };
    if (exampleResponse) body.example_response = exampleResponse;
    const path = IS_DEMO_MODE ? '/public/demo/custom-blocks/parse-curl' : '/custom-blocks/parse-curl';
    const response = await this.client.post<ParseCurlResult>(path, body);
    return response.data;
  }

  async parseScriptIO(script: string, language?: string): Promise<{
    success: boolean;
    inputs: { key: string; label: string; description?: string; default_value?: string }[];
    outputs: { key: string; label: string; description?: string }[];
    error?: string;
  }> {
    const response = await this.client.post('/workflows/parse-script-io', { script, language: language || 'python' });
    return response.data;
  }

  async parseWebhookPayload(payload: string): Promise<{
    success: boolean;
    event_type_path?: string;
    payload_mapping?: Record<string, string>;
    suggested_name?: string;
    suggested_description?: string;
    detected_event_type?: string;
    error?: string;
  }> {
    const path = IS_DEMO_MODE ? '/public/demo/custom-blocks/parse-webhook' : '/custom-blocks/parse-webhook';
    const response = await this.client.post(path, { payload });
    return response.data;
  }

  async generateResponseMapping(exampleResponse: string): Promise<{ mapping?: Record<string, string> }> {
    const response = await this.client.post('/custom-blocks/generate-response-mapping', { example_response: exampleResponse });
    return response.data;
  }

  /**
   * Stream Python SDK code that recreates the given workflow via SSE.
   * Calls callbacks as code chunks arrive.
   */
  async streamSDKCode(
    input: {
      workflow_name: string;
      description?: string;
      definition: any;
      trigger_config: any;
    },
    callbacks: {
      onChunk: (chunk: string) => void;
      onDone: () => void;
      onError: (msg: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    try {
      const token = this.sessionToken || localStorage.getItem('sessionToken') || '';
      const response = await fetch(`${API_BASE_URL}/workflows/sdk-code`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { 'X-Session-Token': token } : {}),
        },
        body: JSON.stringify(input),
        signal,
      });

      if (!response.ok || !response.body) {
        callbacks.onError(`Server returned ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events delimited by blank lines
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          let eventType = '';
          const dataLines: string[] = [];
          for (const line of block.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              dataLines.push(line.slice(6));
            }
          }

          const dataStr = dataLines.join('\n');
          if (eventType === 'code') {
            try {
              const chunk = JSON.parse(dataStr);
              if (typeof chunk === 'string') callbacks.onChunk(chunk);
            } catch { /* ignore malformed */ }
          } else if (eventType === 'error') {
            try {
              const msg = JSON.parse(dataStr);
              callbacks.onError(typeof msg === 'string' ? msg : 'Unknown error');
            } catch {
              callbacks.onError(dataStr);
            }
          } else if (eventType === 'done') {
            callbacks.onDone();
            return;
          }
        }
      }
      callbacks.onDone();
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      callbacks.onError(err?.message || 'Stream failed');
    }
  }

  async getConfluenceSpaces(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/confluence-spaces');
    return response.data;
  }

  async getJiraProjects(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/jira-projects');
    return response.data;
  }

  async getVercelProjects(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/vercel-projects');
    return response.data;
  }

  async getRailwayProjects(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/railway-projects');
    return response.data;
  }

  async getRailwayServices(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/railway-services${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getRailwayEnvironments(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/railway-environments${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getFlyApps(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/flyio-apps');
    return response.data;
  }

  async getFlyMachines(appName?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (appName) params.set('app_name', appName);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/flyio-machines${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusProjects(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get<{ key: string; name: string }[]>('/workflows/nebius-projects');
    return response.data;
  }

  async getNebiusClusters(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-clusters${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusNodeGroups(clusterId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-node-groups${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusInstances(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-instances${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusSubnets(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-subnets${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusPlatforms(projectId?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-platforms${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getNebiusPresets(projectId?: string, platform?: string): Promise<{ key: string; name: string }[]> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (platform) params.set('platform', platform);
    const qs = params.toString();
    const response = await this.client.get<{ key: string; name: string }[]>(`/workflows/nebius-presets${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getJiraUsers(query?: string, project?: string): Promise<{ account_id: string; display_name: string; email: string }[]> {
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    if (project) params.set('project', project);
    const qs = params.toString();
    const response = await this.client.get<{ account_id: string; display_name: string; email: string }[]>(`/workflows/jira-users${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getJiraIssueTypes(projectKey: string): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/jira-issue-types', { params: { project: projectKey } });
    return response.data;
  }

  async getLinearTeams(): Promise<{ id: string; key: string; name: string }[]> {
    const response = await this.client.get<{ id: string; key: string; name: string }[]>('/workflows/linear-teams');
    return response.data;
  }

  async getLinearUsers(query?: string): Promise<{ id: string; name: string; display_name: string; email: string }[]> {
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    const qs = params.toString();
    const response = await this.client.get<{ id: string; name: string; display_name: string; email: string }[]>(`/workflows/linear-users${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getLinearLabels(team: string): Promise<{ id: string; name: string }[]> {
    if (!team) return [];
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/linear-labels', { params: { team } });
    return response.data;
  }

  async getLinearProjects(team: string): Promise<{ id: string; name: string }[]> {
    if (!team) return [];
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/linear-projects', { params: { team } });
    return response.data;
  }

  async getLinearStates(opts: { team?: string; issue?: string }): Promise<{ id: string; name: string }[]> {
    if (!opts.team && !opts.issue) return [];
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/linear-states', { params: opts });
    return response.data;
  }

  async getWorkflowAWSAccounts(): Promise<{ id: string; account_id: string; label: string }[]> {
    const response = await this.client.get<{ id: string; account_id: string; label: string }[]>('/workflows/aws-accounts');
    return response.data;
  }

  async getPDEscalationPolicies(query?: string): Promise<{ id: string; name: string }[]> {
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    const qs = params.toString();
    const response = await this.client.get<{ id: string; name: string }[]>(`/workflows/pd-escalation-policies${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getPDServices(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/pd-services');
    return response.data;
  }

  async getDDMonitors(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/dd-monitors');
    return response.data;
  }

  async getArgoCDApps(clusterId?: string): Promise<{ name: string }[]> {
    const params = clusterId ? `?cluster_id=${clusterId}` : '';
    const response = await this.client.get<{ name: string }[]>(`/workflows/argocd-apps${params}`);
    return response.data;
  }

  async getArgoCDClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/argocd-clusters');
    return response.data;
  }

  async getArgoCDHistory(clusterId?: string, appName?: string): Promise<{ id: string; label: string; is_default: boolean }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    if (appName) params.set('app_name', appName);
    const response = await this.client.get<{ id: number; label: string; is_default: boolean }[]>(`/workflows/argocd-history?${params.toString()}`);
    return response.data.map(h => ({ ...h, id: String(h.id) }));
  }

  async getRolloutsClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/rollouts-clusters');
    return response.data;
  }

  async getRolloutsNames(clusterId?: string, namespace?: string): Promise<{ name: string; namespace: string }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    if (namespace) params.set('namespace', namespace);
    const response = await this.client.get<{ name: string; namespace: string }[]>(`/workflows/rollouts-names?${params.toString()}`);
    return response.data;
  }

  async getRolloutsRevisions(clusterId?: string, namespace?: string, rolloutName?: string): Promise<{ revision: number; label: string; is_default: boolean }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    if (namespace) params.set('namespace', namespace);
    if (rolloutName) params.set('rollout_name', rolloutName);
    const response = await this.client.get<{ revision: number; label: string; is_default: boolean }[]>(`/workflows/rollouts-revisions?${params.toString()}`);
    return response.data;
  }

  async getFluxClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/flux-clusters');
    return response.data;
  }

  async getFluxResources(clusterId?: string, kind?: string, namespace?: string): Promise<{ kind: string; name: string; namespace: string }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    if (kind) params.set('kind', kind);
    if (namespace) params.set('namespace', namespace);
    const response = await this.client.get<{ kind: string; name: string; namespace: string }[]>(`/workflows/flux-resources?${params.toString()}`);
    return response.data;
  }

  async getKarpenterClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/karpenter-clusters');
    return response.data;
  }

  async getKarpenterNodePools(clusterId?: string): Promise<{ name: string }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    const response = await this.client.get<{ name: string }[]>(`/workflows/karpenter-nodepools?${params.toString()}`);
    return response.data;
  }

  async getKyvernoClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/kyverno-clusters');
    return response.data;
  }

  async getKyvernoPolicies(clusterId?: string): Promise<{ name: string; kind: string; namespace?: string; enforcement_action?: string }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    const response = await this.client.get<{ name: string; kind: string; namespace?: string; enforcement_action?: string }[]>(`/workflows/kyverno-policies?${params.toString()}`);
    return response.data;
  }

  async getTrivyClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/trivy-clusters');
    return response.data;
  }

  async getTrivyComplianceReports(clusterId?: string): Promise<{ name: string; title: string; pass_count: number; fail_count: number }[]> {
    const params = new URLSearchParams();
    if (clusterId) params.set('cluster_id', clusterId);
    const response = await this.client.get<{ name: string; title: string; pass_count: number; fail_count: number }[]>(`/workflows/trivy-compliance-reports?${params.toString()}`);
    return response.data;
  }

  // Runbook upload methods
  async uploadRunbook(file: File): Promise<{ id: string; filename: string; gcs_path: string; file_size_bytes: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/workflow-suggestions/runbooks/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getRunbookUploads(): Promise<Array<{ id: string; filename: string; source: string; analysis_status: string; automated_workflow_ids: string[]; created_at: string }>> {
    const response = await this.client.get('/workflow-suggestions/runbooks');
    return response.data;
  }

  async deleteRunbook(id: string): Promise<void> {
    await this.client.delete(`/workflow-suggestions/runbooks/${id}`);
  }

  async linkRunbookToWorkflow(runbookId: string, workflowId: string): Promise<void> {
    await this.client.post(`/workflow-suggestions/runbooks/${runbookId}/link`, { workflow_id: workflowId });
  }

  async getRunbookContent(id: string): Promise<string> {
    const response = await this.client.get(`/workflow-suggestions/runbooks/${id}/content`, { responseType: 'text' });
    return response.data;
  }

  async getDDClusters(): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/dd-clusters');
    return response.data;
  }

  async getConfluencePages(spaceKey: string, query?: string): Promise<{ id: string; title: string }[]> {
    const params = new URLSearchParams({ space: spaceKey });
    if (query) params.set('query', query);
    const response = await this.client.get<{ id: string; title: string }[]>(`/workflows/confluence-pages?${params.toString()}`);
    return response.data;
  }

  async getGitHubWorkflowInputs(repo: string, workflowFile: string): Promise<GitHubWorkflowInput[]> {
    const response = await this.client.get<{ inputs: GitHubWorkflowInput[] }>('/workflows/github-workflow-inputs', {
      params: { repo, workflow_file: workflowFile },
    });
    return response.data.inputs ?? [];
  }

  async getGitHubWorkflowFiles(repo: string): Promise<{ name: string; path: string }[]> {
    const response = await this.client.get<{ files: { name: string; path: string }[] }>('/workflows/github-workflow-files', {
      params: { repo },
    });
    return response.data.files ?? [];
  }

  async getGitHubBranches(repo: string): Promise<{ branches: string[] }> {
    const response = await this.client.get<{ branches: string[] }>(`/github/repos/${repo}/branches`);
    return response.data;
  }

  async getGitHubWorkflows(repo: string): Promise<{ workflows: { name: string; file: string }[] }> {
    const response = await this.client.get<{ workflows: { name: string; file: string }[] }>(`/github/repos/${repo}/workflows`);
    return response.data;
  }

  async searchSlackChannels(query: string = ''): Promise<{ id: string; name: string }[]> {
    const response = await this.client.get<{ id: string; name: string }[]>('/workflows/slack-channels', { params: { q: query } });
    return response.data;
  }

  async searchSlackUsers(query: string = ''): Promise<{ id: string; name: string; real_name: string; display_name: string }[]> {
    const response = await this.client.get<{ id: string; name: string; real_name: string; display_name: string }[]>('/workflows/slack-users', { params: { q: query } });
    return response.data;
  }

  async listWorkflowExecutions(workflowId: string, page: number = 1, pageSize: number = 10): Promise<WorkflowExecutionPage> {
    const response = await this.client.get<WorkflowExecutionPage>(`/workflows/${workflowId}/executions`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  }

  async getWorkflowExecution(id: string): Promise<WorkflowExecutionDetail> {
    const response = await this.client.get<WorkflowExecutionDetail>(`/workflow-executions/${id}`);
    return response.data;
  }

  async cancelWorkflowExecution(id: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflow-executions/${id}/cancel`);
    return response.data;
  }

  async replayWorkflowExecution(id: string, mode: 'full' | 'from_failed' = 'from_failed'): Promise<{ execution_id: string }> {
    const response = await this.client.post<{ execution_id: string }>(`/workflow-executions/${id}/replay?mode=${mode}`);
    return response.data;
  }

  async listWorkflowVersions(workflowId: string, page: number = 1, pageSize: number = 10): Promise<{ versions: WorkflowVersion[]; total: number; page: number; page_size: number }> {
    const response = await this.client.get<{ versions: WorkflowVersion[]; total: number; page: number; page_size: number }>(`/workflows/${workflowId}/versions?page=${page}&page_size=${pageSize}`);
    return response.data;
  }

  async getWorkflowVersion(workflowId: string, version: number): Promise<WorkflowVersion> {
    const response = await this.client.get<WorkflowVersion>(`/workflows/${workflowId}/versions/${version}`);
    return response.data;
  }

  async rollbackWorkflow(workflowId: string, version: number): Promise<void> {
    await this.client.post(`/workflows/${workflowId}/rollback?version=${version}`);
  }

  async listAllExecutions(params: {
    page?: number;
    page_size?: number;
    workflow_id?: string;
    status?: string;
    trigger_source?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{
    executions: (WorkflowExecution & { workflow_name: string; workflow_integrations?: string[] })[];
    total: number;
    page: number;
    page_size: number;
    workflow_options: { id: string; name: string }[];
  }> {
    const response = await this.client.get('/workflow-executions', { params });
    return response.data;
  }

  async listPendingApprovals(): Promise<WorkflowApproval[]> {
    const response = await this.client.get<WorkflowApproval[]>('/workflow-approvals/pending');
    return response.data;
  }

  async listResolvedApprovals(page = 1, perPage = 10): Promise<{ approvals: WorkflowApproval[]; total: number; page: number; per_page: number; total_pages: number }> {
    const response = await this.client.get<{ approvals: WorkflowApproval[]; total: number; page: number; per_page: number; total_pages: number }>('/workflow-approvals/resolved', { params: { page, per_page: perPage } });
    return response.data;
  }

  async approveWorkflowStep(approvalId: string, feedback?: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflow-approvals/${approvalId}/approve`, feedback ? { feedback } : undefined);
    return response.data;
  }

  async rejectWorkflowStep(approvalId: string, feedback?: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflow-approvals/${approvalId}/reject`, feedback ? { feedback } : undefined);
    return response.data;
  }

  async requestChangesWorkflowStep(approvalId: string, feedback: string): Promise<{ status: string }> {
    const response = await this.client.post<{ status: string }>(`/workflow-approvals/${approvalId}/request-changes`, { feedback });
    return response.data;
  }

  // User Groups
  async listUserGroups(): Promise<UserGroup[]> {
    const response = await this.client.get<UserGroup[]>('/user-groups');
    return response.data;
  }

  async createUserGroup(name: string, description: string): Promise<UserGroup> {
    const response = await this.client.post<UserGroup>('/user-groups', { name, description });
    return response.data;
  }

  async updateUserGroup(id: string, name: string, description: string): Promise<void> {
    await this.client.put(`/user-groups/${id}`, { name, description });
  }

  async deleteUserGroup(id: string): Promise<void> {
    await this.client.delete(`/user-groups/${id}`);
  }

  async listUserGroupMembers(groupId: string): Promise<UserGroupMember[]> {
    const response = await this.client.get<UserGroupMember[]>(`/user-groups/${groupId}/members`);
    return response.data;
  }

  async addUserGroupMembers(groupId: string, userIds: string[]): Promise<void> {
    await this.client.post(`/user-groups/${groupId}/members`, { user_ids: userIds });
  }

  async removeUserGroupMember(groupId: string, userId: string): Promise<void> {
    await this.client.delete(`/user-groups/${groupId}/members/${userId}`);
  }

  // Role Access Config
  async getRoleAccessConfig(): Promise<Record<string, { page_access: string[]; api_key_scopes: string[] }>> {
    const response = await this.client.get('/role-access-config');
    return response.data || {};
  }

  async updateRoleAccessConfig(role: string, pageAccess: string[], apiKeyScopes: string[]): Promise<void> {
    await this.client.put('/role-access-config', { role, page_access: pageAccess, api_key_scopes: apiKeyScopes });
  }

  // ArgoCD Integration
  async getArgoCDStatus(): Promise<{ connected: boolean; status: string; server_url?: string }> {
    const response = await this.client.get('/integrations/argocd/status');
    return response.data;
  }

  async connectArgoCD(serverUrl: string, apiToken: string): Promise<{ status: string; server_url: string }> {
    const response = await this.client.post('/integrations/argocd/connect', { server_url: serverUrl, api_token: apiToken });
    return response.data;
  }

  async disconnectArgoCD(): Promise<void> {
    await this.client.post('/integrations/argocd/disconnect');
  }

  async testArgoCD(): Promise<{ success: boolean; app_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/argocd/test');
    return response.data;
  }

  async listArgoCDApps(): Promise<string[]> {
    const response = await this.client.get<string[]>('/integrations/argocd/apps');
    return response.data;
  }

  async getPostHogStatus(): Promise<{ connected: boolean; host?: string; project_id?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/posthog/status');
    return response.data;
  }

  async connectPostHog(params: { host: string; project_id: string; api_key: string }): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/posthog/connect', params);
    return response.data;
  }

  async disconnectPostHog(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/integrations/posthog/disconnect');
    return response.data;
  }

  async testPostHog(): Promise<{ success: boolean; project_name?: string; error?: string }> {
    const response = await this.client.post('/integrations/posthog/test');
    return response.data;
  }

  // Vercel Integration
  async getVercelStatus(): Promise<{ connected: boolean; team_id?: string; last_webhook_at?: string; webhook_event_count?: number; webhook_secret_configured?: boolean }> {
    const response = await this.client.get('/integrations/vercel/status');
    return response.data;
  }

  async connectVercel(params: { api_token: string; team_id?: string }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/vercel/connect', params);
    return response.data;
  }

  async disconnectVercel(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/vercel/disconnect');
    return response.data;
  }

  async testVercel(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/vercel/test');
    return response.data;
  }

  async saveVercelWebhookSecret(params: { webhook_secret: string }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/vercel/webhook-secret', params);
    return response.data;
  }

  // Railway Integration
  async getRailwayStatus(): Promise<{ connected: boolean; last_webhook_at?: string; webhook_event_count?: number; webhook_secret_configured?: boolean }> {
    const response = await this.client.get('/integrations/railway/status');
    return response.data;
  }

  async connectRailway(params: { api_token: string }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/railway/connect', params);
    return response.data;
  }

  async disconnectRailway(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/railway/disconnect');
    return response.data;
  }

  async testRailway(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/railway/test');
    return response.data;
  }

  async saveRailwayWebhookSecret(params: { webhook_secret: string }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/railway/webhook-secret', params);
    return response.data;
  }

  // Fly.io Integration
  async getFlyioStatus(): Promise<{ connected: boolean; poll_interval?: number; org_slug?: string; last_poll_at?: string; machines_tracked?: number }> {
    const response = await this.client.get('/integrations/flyio/status');
    return response.data;
  }

  async connectFlyio(params: { api_token: string; poll_interval?: number; org_slug?: string }): Promise<{ status: string; poll_interval?: number; org_slug?: string }> {
    const response = await this.client.post('/integrations/flyio/connect', params);
    return response.data;
  }

  async disconnectFlyio(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/flyio/disconnect');
    return response.data;
  }

  async testFlyio(): Promise<{ success: boolean; app_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/flyio/test');
    return response.data;
  }

  // Nebius Integration
  async getNebiusStatus(): Promise<{ connected: boolean; region?: string; poll_interval?: number }> {
    const response = await this.client.get('/integrations/nebius/status');
    return response.data;
  }

  async connectNebius(params: { credentials?: string; service_account_id?: string; public_key_id?: string; private_key?: string; region?: string; poll_interval?: number }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/nebius/connect', params);
    return response.data;
  }

  async disconnectNebius(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/nebius/disconnect');
    return response.data;
  }

  async testNebius(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/nebius/test');
    return response.data;
  }

  // Beam Integration
  async getBeamStatus(): Promise<{ connected: boolean; poll_interval?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/beam/status');
    return response.data;
  }

  async connectBeam(params: { api_token: string; poll_interval?: number; gateway_base_url?: string }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/beam/connect', params);
    return response.data;
  }

  async disconnectBeam(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/beam/disconnect');
    return response.data;
  }

  async testBeam(): Promise<{ success: boolean; deployment_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/beam/test');
    return response.data;
  }

  async getBeamDeployments(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/beam-deployments');
    return response.data;
  }

  async getBeamContainers(deploymentId?: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/beam-containers', { params: deploymentId ? { deployment_id: deploymentId } : {} });
    return response.data;
  }

  async getBeamMachines(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/beam-machines');
    return response.data;
  }

  async getBeamSandboxes(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/beam-sandboxes');
    return response.data;
  }

  // Daytona Integration
  async getDaytonaStatus(): Promise<{ connected: boolean; api_url?: string; webhook_configured?: boolean; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/daytona/status');
    return response.data;
  }

  async connectDaytona(params: { api_key: string; api_url?: string; webhook_secret: string }): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/daytona/connect', params);
    return response.data;
  }

  async disconnectDaytona(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/daytona/disconnect');
    return response.data;
  }

  async testDaytona(): Promise<{ success: boolean; sandbox_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/daytona/test');
    return response.data;
  }

  async getDaytonaSandboxes(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/daytona-sandboxes');
    return response.data;
  }

  async getDaytonaSnapshots(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/daytona-snapshots');
    return response.data;
  }

  async getDaytonaVolumes(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/daytona-volumes');
    return response.data;
  }

  async getDaytonaTargets(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/daytona-targets');
    return response.data;
  }

  // Supabase Integration
  async getSupabaseStatus(): Promise<{ connected: boolean; api_url?: string; poll_interval?: number; webhook_configured?: boolean; last_webhook_at?: string; webhook_event_count?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/supabase/status');
    return response.data;
  }

  async connectSupabase(params: { access_token: string; api_url?: string; webhook_secret?: string; poll_interval?: number }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/supabase/connect', params);
    return response.data;
  }

  async disconnectSupabase(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/supabase/disconnect');
    return response.data;
  }

  async testSupabase(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/supabase/test');
    return response.data;
  }

  async getSupabaseProjects(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/supabase-projects');
    return response.data;
  }

  async getSupabaseBranches(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/supabase-branches');
    return response.data;
  }

  async getSupabaseReplicas(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/supabase-replicas');
    return response.data;
  }

  // PlanetScale Integration
  async getPlanetScaleStatus(): Promise<{ connected: boolean; organization?: string; poll_interval?: number; webhook_configured?: boolean; webhook_secret_count?: number; last_webhook_at?: string; webhook_event_count?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/planetscale/status');
    return response.data;
  }

  async connectPlanetScale(params: { token_id: string; token: string; organization: string; webhook_secret?: string; webhook_secrets?: string[]; poll_interval?: number }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/planetscale/connect', params);
    return response.data;
  }

  async disconnectPlanetScale(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/planetscale/disconnect');
    return response.data;
  }

  async testPlanetScale(): Promise<{ success: boolean; database_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/planetscale/test');
    return response.data;
  }

  async getPlanetScaleDatabases(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/planetscale-databases');
    return response.data;
  }

  async getPlanetScaleBranches(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/planetscale-branches');
    return response.data;
  }

  async getPlanetScaleDeployRequests(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/planetscale-deploy-requests');
    return response.data;
  }

  // Neon Integration
  async getNeonStatus(): Promise<{ connected: boolean; org_id?: string; poll_interval?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/neon/status');
    return response.data;
  }

  async connectNeon(params: { api_key: string; org_id?: string; api_url?: string; poll_interval?: number }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/neon/connect', params);
    return response.data;
  }

  async disconnectNeon(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/neon/disconnect');
    return response.data;
  }

  async testNeon(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/neon/test');
    return response.data;
  }

  async getNeonProjects(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/neon-projects');
    return response.data;
  }

  async getNeonBranches(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/neon-branches');
    return response.data;
  }

  async getNeonEndpoints(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/neon-endpoints');
    return response.data;
  }

  // ClickHouse Integration
  async getClickHouseStatus(): Promise<{ connected: boolean; org_id?: string; org_name?: string; poll_interval?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/clickhouse/status');
    return response.data;
  }

  async connectClickHouse(params: { key_id: string; key_secret: string; org_id?: string; api_url?: string; poll_interval?: number }): Promise<{ status: string; org_id?: string; org_name?: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/clickhouse/connect', params);
    return response.data;
  }

  async disconnectClickHouse(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/clickhouse/disconnect');
    return response.data;
  }

  async testClickHouse(): Promise<{ success: boolean; service_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/clickhouse/test');
    return response.data;
  }

  async getClickHouseServices(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/clickhouse-services');
    return response.data;
  }

  async getClickHouseClickPipes(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/clickhouse-clickpipes');
    return response.data;
  }

  async getClickHouseBackups(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/clickhouse-backups');
    return response.data;
  }

  // Jenkins Integration
  async getJenkinsStatus(): Promise<{ connected: boolean; base_url?: string; username?: string; webhook_secret?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/jenkins/status');
    return response.data;
  }

  async connectJenkins(params: { base_url: string; username: string; api_token: string }): Promise<{ status: string; webhook_secret?: string }> {
    const response = await this.client.post('/integrations/jenkins/connect', params);
    return response.data;
  }

  async disconnectJenkins(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/jenkins/disconnect');
    return response.data;
  }

  async testJenkins(): Promise<{ success: boolean; job_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/jenkins/test');
    return response.data;
  }

  async getJenkinsJobs(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/jenkins-jobs');
    return response.data;
  }

  // CircleCI Integration
  async getCircleCIStatus(): Promise<{ connected: boolean; org_slug?: string; webhook_secret?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/circleci/status');
    return response.data;
  }

  async connectCircleCI(params: { api_token: string; org_slug?: string }): Promise<{ status: string; webhook_secret?: string }> {
    const response = await this.client.post('/integrations/circleci/connect', params);
    return response.data;
  }

  async disconnectCircleCI(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/circleci/disconnect');
    return response.data;
  }

  async testCircleCI(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/circleci/test');
    return response.data;
  }

  async getCircleCIProjects(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/circleci-projects');
    return response.data;
  }

  async getCircleCIPipelines(project: string, branch?: string): Promise<Array<{ key: string; name: string }>> {
    const params = new URLSearchParams({ project });
    if (branch) params.set('branch', branch);
    const response = await this.client.get(`/workflows/circleci-pipelines?${params.toString()}`);
    return response.data;
  }

  // SonarCloud Integration
  async getSonarCloudStatus(): Promise<{ connected: boolean; organization?: string; webhook_secret?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/sonarcloud/status');
    return response.data;
  }

  async connectSonarCloud(params: { organization: string; api_token: string }): Promise<{ status: string; webhook_secret?: string }> {
    const response = await this.client.post('/integrations/sonarcloud/connect', params);
    return response.data;
  }

  async disconnectSonarCloud(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/sonarcloud/disconnect');
    return response.data;
  }

  async testSonarCloud(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/sonarcloud/test');
    return response.data;
  }

  async getSonarCloudProjects(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/sonarcloud-projects');
    return response.data;
  }

  // Okta Integration
  async getOktaStatus(): Promise<{ connected: boolean; org_url?: string; last_poll_at?: string; events_seen?: number; last_event_at?: string }> {
    const response = await this.client.get('/integrations/okta/status');
    return response.data;
  }

  async connectOkta(params: { org_url: string; api_token: string }): Promise<{ status: string; org_url?: string }> {
    const response = await this.client.post('/integrations/okta/connect', params);
    return response.data;
  }

  async disconnectOkta(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/okta/disconnect');
    return response.data;
  }

  async testOkta(): Promise<{ success: boolean; user_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/okta/test');
    return response.data;
  }

  async getOktaUsers(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/okta-users');
    return response.data;
  }

  async getOktaGroups(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/okta-groups');
    return response.data;
  }

  // Terraform Cloud Integration
  async getTerraformStatus(): Promise<{ connected: boolean; organization?: string; base_url?: string; notification_token?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/terraform/status');
    return response.data;
  }

  async connectTerraform(params: { api_token: string; organization: string; base_url?: string }): Promise<{ status: string; notification_token?: string }> {
    const response = await this.client.post('/integrations/terraform/connect', params);
    return response.data;
  }

  async disconnectTerraform(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/terraform/disconnect');
    return response.data;
  }

  async testTerraform(): Promise<{ success: boolean; workspace_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/terraform/test');
    return response.data;
  }

  async getTerraformWorkspaces(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/terraform-workspaces');
    return response.data;
  }

  async getTerraformRuns(workspace: string): Promise<Array<{ key: string; name: string }>> {
    const params = new URLSearchParams({ workspace });
    const response = await this.client.get(`/workflows/terraform-runs?${params.toString()}`);
    return response.data;
  }

  // Databricks integration
  async getDatabricksStatus(): Promise<{ connected: boolean; workspace_url?: string; poll_interval?: number; last_poll_at?: string; events_fired?: number }> {
    const response = await this.client.get('/integrations/databricks/status');
    return response.data;
  }

  async connectDatabricks(params: { workspace_url: string; api_token: string }): Promise<{ status: string; workspace_url?: string }> {
    const response = await this.client.post('/integrations/databricks/connect', params);
    return response.data;
  }

  async disconnectDatabricks(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/databricks/disconnect');
    return response.data;
  }

  async testDatabricks(): Promise<{ success: boolean; job_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/databricks/test');
    return response.data;
  }

  async getDatabricksJobs(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/databricks-jobs');
    return response.data;
  }

  async getDatabricksClusters(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/databricks-clusters');
    return response.data;
  }

  async getDatabricksPipelines(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/databricks-pipelines');
    return response.data;
  }

  async getDatabricksWarehouses(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/databricks-warehouses');
    return response.data;
  }

  // AWS cost optimization dropdowns
  async getAWSBudgetNames(awsAccount?: string): Promise<Array<{ key: string; name: string }>> {
    const params = new URLSearchParams();
    if (awsAccount) params.set('aws_account', awsAccount);
    const response = await this.client.get(`/workflows/aws-budget-names?${params.toString()}`);
    return response.data;
  }

  async getAWSCEServices(awsAccount?: string): Promise<Array<{ key: string; name: string }>> {
    const params = new URLSearchParams();
    if (awsAccount) params.set('aws_account', awsAccount);
    const response = await this.client.get(`/workflows/aws-ce-services?${params.toString()}`);
    return response.data;
  }

  // Generic fetch for the AWS ops/security/data dropdowns (ASGs, EKS
  // clusters/nodegroups, Lambda functions, ECS services, log groups, RDS
  // instances, DynamoDB tables, SQS queues, IAM users). `path` is the URL
  // suffix after /workflows/aws-, e.g. "asg-names".
  async getAWSOpsDropdown(path: string, params?: Record<string, string | undefined>): Promise<Array<{ key: string; name: string }>> {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (v) search.set(k, v);
    }
    const response = await this.client.get(`/workflows/aws-${path}?${search.toString()}`);
    return response.data;
  }

  async getAWSResourceIDs(
    resourceType: 'instances' | 'volumes' | 'eips' | 'snapshots',
    awsAccount?: string,
    region?: string
  ): Promise<Array<{ key: string; name: string }>> {
    const params = new URLSearchParams();
    params.set('type', resourceType);
    if (awsAccount) params.set('aws_account', awsAccount);
    if (region) params.set('region', region);
    const response = await this.client.get(`/workflows/aws-resource-ids?${params.toString()}`);
    return response.data;
  }

  // Pulumi Cloud Integration
  async getPulumiStatus(): Promise<{ connected: boolean; organization?: string; base_url?: string; webhook_secret?: string; last_webhook_at?: string; webhook_event_count?: number }> {
    const response = await this.client.get('/integrations/pulumi/status');
    return response.data;
  }

  async connectPulumi(params: { api_token: string; organization: string; base_url?: string }): Promise<{ status: string; webhook_secret?: string }> {
    const response = await this.client.post('/integrations/pulumi/connect', params);
    return response.data;
  }

  async disconnectPulumi(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/pulumi/disconnect');
    return response.data;
  }

  async testPulumi(): Promise<{ success: boolean; stack_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/pulumi/test');
    return response.data;
  }

  async getPulumiStacks(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/pulumi-stacks');
    return response.data;
  }

  // HashiCorp Vault Integration
  async getVaultStatus(): Promise<{ connected: boolean; address?: string; namespace?: string; poll_interval?: number; last_poll_at?: string; resources_tracked?: number; sealed?: boolean }> {
    const response = await this.client.get('/integrations/vault/status');
    return response.data;
  }

  async connectVault(params: { address: string; token: string; namespace?: string; poll_interval?: number }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/vault/connect', params);
    return response.data;
  }

  async disconnectVault(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/vault/disconnect');
    return response.data;
  }

  async testVault(): Promise<{ success: boolean; sealed?: boolean; version?: string; error?: string }> {
    const response = await this.client.post('/integrations/vault/test');
    return response.data;
  }

  async getVaultMounts(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/vault-mounts');
    return response.data;
  }

  async getVaultSecretPaths(mount: string, path?: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/vault-secret-paths', { params: { mount, ...(path ? { path } : {}) } });
    return response.data;
  }

  async getVaultPolicies(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/vault-policies');
    return response.data;
  }

  async getVaultDatabaseMounts(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/vault-database-mounts');
    return response.data;
  }

  async getVaultStaticRoles(mount?: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/vault-static-roles', { params: mount ? { mount } : {} });
    return response.data;
  }

  // Infisical Integration
  async getInfisicalStatus(): Promise<{ connected: boolean; site_url?: string; client_id?: string; poll_interval?: number; last_poll_at?: string; resources_tracked?: number }> {
    const response = await this.client.get('/integrations/infisical/status');
    return response.data;
  }

  async connectInfisical(params: { client_id: string; client_secret: string; site_url?: string; poll_interval?: number }): Promise<{ status: string; poll_interval?: number }> {
    const response = await this.client.post('/integrations/infisical/connect', params);
    return response.data;
  }

  async disconnectInfisical(): Promise<{ status: string }> {
    const response = await this.client.post('/integrations/infisical/disconnect');
    return response.data;
  }

  async testInfisical(): Promise<{ success: boolean; project_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/infisical/test');
    return response.data;
  }

  async getInfisicalProjects(): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/infisical-projects');
    return response.data;
  }

  async getInfisicalEnvironments(project?: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/infisical-environments', { params: project ? { project } : {} });
    return response.data;
  }

  async getInfisicalFolders(project: string, environment: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/infisical-folders', { params: { project, environment } });
    return response.data;
  }

  async getInfisicalSecretSyncs(project?: string): Promise<Array<{ key: string; name: string }>> {
    const response = await this.client.get('/workflows/infisical-secret-syncs', { params: project ? { project } : {} });
    return response.data;
  }

  // Cloudflare Integration
  async getCloudflareStatus(): Promise<{ connected: boolean; status: string; account_id?: string; webhook_url?: string; webhook_secret?: string }> {
    const response = await this.client.get('/integrations/cloudflare/status');
    return response.data;
  }

  async connectCloudflare(apiToken: string, accountId: string): Promise<{ status: string; account_id: string }> {
    const response = await this.client.post('/integrations/cloudflare/connect', { api_token: apiToken, account_id: accountId });
    return response.data;
  }

  async disconnectCloudflare(): Promise<void> {
    await this.client.post('/integrations/cloudflare/disconnect');
  }

  async testCloudflareConnection(): Promise<{ success: boolean; zone_count?: number; error?: string }> {
    const response = await this.client.post('/integrations/cloudflare/test');
    return response.data;
  }

  async getCloudflareZones(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/integrations/cloudflare/zones');
    return response.data;
  }

  async getCloudflareWorkers(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/integrations/cloudflare/workers');
    return response.data;
  }

  async getCloudflareLBPools(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/integrations/cloudflare/lb-pools');
    return response.data;
  }

  async getCloudflareTunnels(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/integrations/cloudflare/tunnels');
    return response.data || [];
  }

  async getCloudflareAccessApps(): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get('/integrations/cloudflare/access-apps');
    return response.data || [];
  }

  async getCloudflareDNSRecords(zoneId: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get(`/integrations/cloudflare/dns-records?zone_id=${zoneId}`);
    return response.data || [];
  }

  async getCloudflareWAFRulesets(zoneId: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get(`/integrations/cloudflare/waf-rulesets?zone_id=${zoneId}`);
    return response.data || [];
  }

  async getCloudflareWorkerVersions(scriptName: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get(`/integrations/cloudflare/worker-versions?script=${scriptName}`);
    return response.data || [];
  }

  async getCloudflareAccessPolicies(appId: string): Promise<{ key: string; name: string }[]> {
    const response = await this.client.get(`/integrations/cloudflare/access-policies?app_id=${appId}`);
    return response.data || [];
  }

  async submitWorkflowRequest(prompt: string, source: string = 'platform'): Promise<any> {
    const response = await this.client.post('/workflow-requests/submit', { prompt, source });
    return response.data;
  }

  async replyToWorkflowRequest(requestId: string, message: string): Promise<any> {
    const response = await this.client.post(`/workflow-requests/${requestId}/reply`, { message });
    return response.data;
  }

}

export const apiClient = new ApiClient();

// Chat History interfaces
export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'agent';
  content: string;
  order?: number;
  thinking_text?: string;
  yaml_fixes?: Record<string, any>;
  cloud_fixes?: Record<string, any>;
  datadog_metrics?: any[];
  code_fixes?: Record<string, any>;
  code_provider?: string;
  created_at?: string;
}

export interface ChatSession {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  context_type?: string;
  context_id?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface ChatSessionsResponse {
  sessions: ChatSession[];
}

export interface SessionMessagesResponse {
  messages: ChatMessage[];
}

export interface CreateChatSessionRequest {
  title?: string;
  context_type?: string;
  context_id?: string;
}

export interface UpdateChatSessionRequest {
  title?: string;
  /** Re-point the session at a new context id (e.g. draft workflow chat → saved workflow id). */
  context_id?: string;
}

export interface AddMessageRequest {
  role: 'user' | 'assistant' | 'agent';
  content: string;
  thinking_text?: string;
  yaml_fixes?: Record<string, any>;
}

// AWS Integration interfaces
export interface AWSConnection {
  id: string;
  tenant_id: string;
  account_id: string;
  role_arn: string;
  external_id: string;
  region: string;
  connection_status: 'pending' | 'connected' | 'failed' | 'disconnected';
  last_verified_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface AWSBootstrapRequest {
  region: string;
}

export interface AWSBootstrapResponse {
  external_id: string;
  cfn_launch_url: string;
  cli_create_cmd: string;
  cli_output_cmd: string;
  kestrel_account_id: string;
  region: string;
}

export interface OnPremCloudInventoryTemplateResponse {
  stack_name: string;
  role_name: string;
  region: string;
  cli_create_cmd: string;
  cli_output_cmd: string;
  parameters: {
    PodRoleName: string;
    RoleName: string;
  };
  parameter_descriptions: {
    PodRoleName: string;
  };
}

export interface HealthStatus {
  status: string;
  service: string;
  timestamp: string;
  is_onprem_mode: boolean;
  dataplane_region?: string;
  dataplane_host?: string;
}

export interface AWSVerifyRequest {
  role_arn: string;
  external_id: string;
  region: string;
}

export interface AWSVerifyResponse {
  account_id: string;
  status: string;
  message: string;
}

export interface AWSConnectionsResponse {
  connections: AWSConnection[];
  count: number;
}

// GCP (Google Cloud) Integration interfaces
export interface GCPConnection {
  id: string;
  tenant_id: string;
  project_id: string;
  project_number?: string;
  org_id?: string;
  service_account_email: string;
  workload_identity_pool: string;
  workload_identity_provider: string;
  // Empty until the customer enables the BigQuery billing export. Cost blocks
  // report "cost data pending" rather than failing while it is unset.
  billing_export_table?: string;
  default_region: string;
  granted_role_version: number;
  connection_status: 'pending' | 'connected' | 'failed' | 'disconnected';
  last_verified_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface GCPBootstrapRequest {
  project_id: string;
  include_remediation?: boolean;
}

export interface GCPBootstrapResponse {
  project_id: string;
  kestrel_service_account: string;
  service_account_email: string;
  workload_identity_pool: string;
  workload_identity_provider: string;
  setup_script: string;
  cloud_shell_url: string;
  read_only_roles: string[];
  remediation_roles: string[];
  billing_export_steps: string[];
  role_version: number;
}

export interface GCPVerifyRequest {
  project_id: string;
  service_account_email?: string;
  billing_export_table?: string;
  default_region?: string;
}

export interface GCPVerifyResponse {
  connection?: GCPConnection;
  project_id?: string;
  project_number?: string;
  status: 'connected' | 'failed';
  message: string;
  has_billing_export?: boolean;
}

export interface GCPStatusResponse {
  connected: boolean;
  project_count: number;
  has_billing_export: boolean;
  projects?: {
    id: string;
    project_id: string;
    has_billing_export: boolean;
    last_verified_at?: string;
  }[];
  current_role_version?: number;
}

// OCI (Oracle Cloud Infrastructure) Integration interfaces
export interface OCIConnection {
  id: string;
  tenant_id: string;
  tenancy_ocid: string;
  user_ocid: string;
  fingerprint: string;
  region: string;
  compartment_ocid?: string;
  connection_name?: string;
  connection_status: 'pending' | 'connected' | 'failed' | 'disconnected';
  last_verified_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface OCIVerifyRequest {
  tenancy_ocid: string;
  user_ocid: string;
  fingerprint: string;
  private_key: string;
  region: string;
  compartment_ocid?: string;
  connection_name?: string;
}

export interface OCIVerifyResponse {
  tenancy_ocid: string;
  tenancy_name: string;
  region: string;
  home_region?: string;
  status: string;
  message: string;
  capabilities?: string;
}

export interface OCIConnectionsResponse {
  connections: OCIConnection[];
  count: number;
}

export interface OCISetupStep {
  step_number: number;
  title: string;
  description: string;
  command?: string;
  link?: string;
}

export interface OCIRegion {
  identifier: string;
  name: string;
}

export interface OCISetupInstructionsResponse {
  steps: OCISetupStep[];
  regions: OCIRegion[];
}

export interface OCIRegionsResponse {
  regions: OCIRegion[];
}

// Cloud Resource interfaces (for AWS and OCI resource inventory)
export interface CloudResource {
  id: string;
  tenant_id: string;

  // Cloud provider discrimination ("aws" or "oci")
  cloud_provider: 'aws' | 'oci';

  // AWS-specific fields (empty for OCI resources)
  aws_connection_id?: string;
  aws_account_id?: string;

  // OCI-specific fields (empty for AWS resources)
  oci_connection_id?: string;
  oci_tenancy_ocid?: string;
  compartment_id?: string;

  // Common fields
  region: string;
  resource_type: string; // AWS: AWS::IAM::User, OCI: OCI::Compute::Instance
  resource_id: string;
  arn?: string; // ARN for AWS, OCID for OCI
  name?: string;
  first_seen_at: string;
  last_seen_at: string;
  resource_status: string;
  created_at: string;

  // VPC/VCN Association (for topology map hierarchy: Account -> VPC/VCN -> Resources)
  // NULL/undefined for global resources (IAM, S3 / Identity, ObjectStorage)
  vpc_id?: string; // VPC ID for AWS, VCN OCID for OCI
  subnet_id?: string;
  availability_zone?: string;

  // IP addresses for Flow Log correlation
  private_ip?: string;  // Primary private IP
  public_ip?: string;   // Public IP if assigned
  // Additional IPs are in cloud_resource_ips table (for multi-ENI resources)
  additional_ips?: CloudResourceIP[];

  // Incident data (populated by server, not stored in DB)
  incident_count?: number;
  incident_severity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface CloudResourceIP {
  ip_address: string;
  ip_type: 'private' | 'public' | 'elastic';
  eni_id?: string;
  is_primary: boolean;
}

export interface CloudResourcesResponse {
  resources: CloudResource[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CloudResourcesSummary {
  total: number;
  by_type: { [key: string]: number };
  by_region?: { [key: string]: number };
  account_id?: string;
  by_tenancy?: { [key: string]: number }; // For OCI tenancy breakdown
}

// OCI Resource interfaces
export interface OCIResourcesResponse {
  resources: CloudResource[];
  count: number;
  total_count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

export interface OCIResourcesSummary {
  total: number;
  by_type: { [key: string]: number };
  by_tenancy: { [key: string]: number };
}

export interface OCISyncSchedule {
  id: string;
  tenant_id: string;
  oci_connection_id: string;
  enabled: boolean;
  sync_interval_minutes: number;
  root_compartment_ocid?: string;
  include_subcompartments: boolean;
  last_sync_at?: string;
  last_sync_status?: 'running' | 'completed' | 'failed';
  last_sync_error?: string;
  last_sync_resources_found: number;
  last_sync_duration_seconds?: number;
  next_sync_at?: string;
  created_at: string;
  updated_at: string;
}

export interface OCISyncStatusResponse {
  schedules: OCISyncSchedule[];
  count: number;
}

export interface OCITriggerSyncRequest {
  connection_id: string;
}

export interface OCITriggerSyncResponse {
  message: string;
  status: string;
}

// AWS Account Flow Logs interfaces
export interface AWSAccountFlowsResponse {
  aws_account_id: string;
  flows: FlowSummary[];
  count: number;
}

export interface AWSAccountFlowLogsStatus {
  aws_account_id: string;
  enabled: boolean;
  vpc_count: number;
  vpcs: VPCFlowLogConfig[];
}

export interface VPCFlowLogConfig {
  vpc_id: string;
  region?: string;
  resource_count?: number;
  flow_log_id?: string;
  log_group_name?: string;
  enabled: boolean;
  last_processed_timestamp?: string;
  last_poll_time?: string;
  error_message?: string;
}

export interface EnableAWSAccountFlowLogsResponse {
  message: string;
  vpc_count: number;
  vpcs: {
    vpc_id: string;
    flow_log_id?: string;
    log_group_name?: string;
    enabled: boolean;
  }[];
}

// OCI VCN Flow Logs interfaces (mirror of AWS VPC Flow Logs for OCI)
export interface OCITenancyFlowLogsStatus {
  tenancy_ocid: string;
  enabled: boolean;
  vcn_count: number;
  vcns: VCNFlowLogConfig[];
}

export interface VCNFlowLogConfig {
  vcn_id: string;
  vcn_name?: string;
  region?: string;
  resource_count?: number;
  log_group_ocid?: string;
  log_ocid?: string;
  enabled: boolean;
  last_processed_timestamp?: string;
  last_poll_time?: string;
  error_message?: string;
}

export interface EnableOCITenancyFlowLogsResponse {
  message: string;
  vcn_count: number;
  vcns: {
    vcn_id: string;
    vcn_name?: string;
    log_group_ocid?: string;
    log_ocid?: string;
    enabled: boolean;
  }[];
}

// EKS Cluster interfaces
export interface EKSCluster {
  id: string;
  tenant_id: string;
  aws_connection_id: string;
  cluster_name: string;
  region: string;
  version: string;
  node_group_size: string;
  instance_types: string[];
  min_nodes: number;
  max_nodes: number;
  desired_nodes: number;
  cluster_arn?: string;
  cluster_endpoint?: string;
  vpc_id?: string;
  subnet_ids?: string[];
  security_group_id?: string;
  node_group_arn?: string;
  status: 'pending' | 'creating_vpc' | 'creating_cluster' | 'creating_nodegroup' | 'installing_cilium' | 'installing_operator' | 'ready' | 'failed' | 'deleting' | 'deleted';
  progress_percentage: number;
  current_step?: string;
  error_message?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  ready_at?: string;
}

export interface EKSClusterStep {
  id: string;
  cluster_id: string;
  step_name: string;
  step_order: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ClusterSize {
  name: string;
  display_name: string;
  description: string;
  instance_type: string;
  min_nodes: number;
  max_nodes: number;
  desired_nodes: number;
  vcpus: number;
  memory: string;
  monthly_price_estimate: string;
}

export interface CreateEKSClusterRequest {
  cluster_name: string;
  region: string;
  version: string;
  aws_connection_id: string;
  node_group_size: string;
  instance_types?: string[];
  min_nodes?: number;
  max_nodes?: number;
  desired_nodes?: number;
}

export interface EKSClustersResponse {
  clusters: EKSCluster[];
  count: number;
}

export interface EKSClusterProgressResponse {
  cluster: EKSCluster;
  steps: EKSClusterStep[];
}

export interface ClusterSizesResponse {
  sizes: ClusterSize[];
}

// VPC Flow Logs interfaces (for EKS VPC CNI network traffic)
export interface VPCFlowLogsStatus {
  enabled: boolean;
  vpc_id?: string;
  flow_log_id?: string;
  log_destination_type?: string;
  log_group_name?: string;
  traffic_type?: string;
  last_processed_timestamp?: string;
  last_poll_time?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  message?: string;
}

export interface EnableVPCFlowLogsRequest {
  vpc_id: string;
  aws_connection_id: string;
}

export interface EnableVPCFlowLogsResponse {
  message: string;
  vpc_id: string;
  flow_log_id: string;
  log_group_name: string;
  enabled: boolean;
}

// Stripe Billing interfaces
export interface StartFreeTrialResponse {
  success: boolean;
  message: string;
  expires_at: number;
}

export interface CreateCheckoutSessionRequest {
  tier: 'starter' | 'growth' | 'enterprise';
  quantity: number;
}

export interface CreateCheckoutSessionResponse {
  session_id: string;
  session_url?: string;
  client_secret?: string;
  publishable_key: string;
}

export interface CustomerPortalResponse {
  portal_url: string;
}

export interface SubscriptionStatus {
  has_active_subscription: boolean;
  tier?: string;
  status?: string;
  quantity?: number;
  current_period_end?: number;
  cancel_at_period_end?: boolean;
  is_free_trial?: boolean;       // True if this is a no-CC free trial (vs Stripe trial)
  is_product_subscription?: boolean; // True if using new per-product billing model
  // Usage stats
  clusters_used: number;
  clusters_limit: number;        // -1 means unlimited
  cloud_accounts_used: number;
  cloud_accounts_limit: number;  // -1 means unlimited
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
}

export interface InvoicesResponse {
  invoices: Invoice[];
}

// On-Prem License interfaces
export interface OnPremPricing {
  unit_price_monthly: number;      // $300
  unit_price_yearly: number;       // $3,600
  support_percentage: number;      // 25%
  trial_days: number;              // 14
  billing_period: string;          // "yearly"
  unit_description: string;        // "1 Kubernetes cluster + 1 Cloud account"
}

export interface OnPremTrialRequest {
  units: number;
}

export interface OnPremCheckoutRequest {
  units: number;
}

export interface OnPremCheckoutResponse {
  session_id?: string;
  session_url?: string;
  publishable_key?: string;
  license_key?: string;        // For trial, license is generated immediately
  trial_expires_at?: string;
}

export interface OnPremLicense {
  id: string;
  tenant_id: string;
  license_type: 'trial' | 'paid' | 'enterprise';
  status: 'active' | 'expired' | 'revoked' | 'pending';
  issued_at: string;
  starts_at: string;
  expires_at: string;
  max_clusters: number;
  max_cloud_accounts: number;
  features: string[];
  units: number;
  billing_period: string;
  license_key?: string;        // Only included when specifically requested
  days_until_expiry: number;
  is_in_grace_period: boolean;
}

export interface LicenseValidationResponse {
  valid: boolean;
  error?: string;
  tenant_id?: string;
  tenant_name?: string;
  license_type?: string;
  expires_at?: string;
  max_clusters?: number;
  max_cloud_accounts?: number;
  features?: string[];
  days_until_expiry?: number;
  is_in_grace_period?: boolean;
}

// GitHub Integration interfaces
export interface GitHubConnectResponse {
  installation_url: string;
  app_name: string;
}

export interface GitHubFile {
  name: string;
  path: string;
  type: string; // "file" or "dir"
  size: number;
  sha: string;
  url: string;
  git_url: string;
  download_url?: string;
}

export interface GitHubFilesResponse {
  files: GitHubFile[];
  repository: {
    id: string;
    name: string;
    full_name: string;
    owner: string;
  };
  path: string;
}

export interface CreatePullRequestRequest {
  repository_id: string;
  target_file: string;
  yaml_content: string;
  pr_title: string;
  pr_description?: string;
  commit_message?: string;
  update_mode?: 'append' | 'replace' | 'new_file' | 'smart_merge';
  source_type: string;
  source_id?: string;
}

export interface CreatePullRequestResponse {
  success: boolean;
  pull_request: {
    number: number;
    url: string;
    title: string;
    state: string;
    branch: string;
  };
  diff_preview: {
    lines_added: number;
    lines_removed: number;
    has_changes: boolean;
    total_lines: number;
  };
}

// Incident Response interfaces (K8s SRE)
export interface IncidentSignal {
  signal_type: string;    // event, pod_status, node_condition, rollout_status, pod_logs
  timestamp: number;      // Unix timestamp
  namespace: string;
  resource_name: string;
  resource_kind: string;
  severity: string;
  reason: string;
  message: string;
  metadata: Record<string, any>; // JSON metadata
}

export interface IncidentTimelineEvent {
  timestamp: number;        // Unix timestamp
  event_type: string;       // alert, log_entry, status_change, deployment
  description: string;
  source: string;           // pod_logs, kubernetes_event, metrics, etc.
  severity: string;
}

export interface IncidentYamlFix {
  file_path: string;
  file_type: string;        // create, update, delete
  yaml_content: string;
  existing_yaml?: string;   // Original YAML for updates
  explanation: string;
  resource_name?: string;
  resource_kind?: string;
  resource_namespace?: string;
}

export interface IncidentCodeFix {
  file_path: string;
  file_type: string;        // modify, create, delete
  original_code?: string;   // Original code snippet
  fixed_code: string;       // Fixed code
  explanation: string;
  line_start: number;       // Starting line number
  line_end: number;         // Ending line number
  language: string;         // Programming language
  tests_included: boolean;  // Whether fix includes tests
  repo_id?: string;         // GitHub repository ID where the file was found
}

export interface IncidentRcaResult {
  incident_type: string;
  root_cause: string;       // Includes transient explanation if transient
  timeline: IncidentTimelineEvent[];
  investigation_summary: StructuredInvestigationSummary; // Structured format only
  yaml_fixes: IncidentYamlFix[];
  code_fixes: IncidentCodeFix[]; // For application-level incidents
  affected_resources: string | Array<Record<string, string>>; // JSON-encoded string from server or parsed array
  evidence_metrics?: EvidenceMetric[]; // Metrics that serve as evidence for the root cause
  completed_at: number;     // Unix timestamp
  github_pr_url?: string;   // URL of created PR for code fixes
  github_pr_number?: number; // PR number
  transient?: boolean;      // True if AI agent classified as transient
  is_application_level_failure?: boolean; // True if failure is due to application code bug
  stack_trace_summary?: string; // Extracted stack trace from logs
  suspected_source_files?: SuspectedSourceFile[]; // Files from stack trace
  code_provider?: string; // "github" or "gitlab" - which provider was used for code analysis
  code_fix_status?: string; // "completed", "attempted", "analyzing"
  code_fix_error?: string; // Error message from agent if analysis failed
  causal_prs?: CausalPR[]; // PRs that may have caused the incident
  causal_pr_status?: string; // "searching", "completed" - tracks causal PR search progress
}

// CausalPR represents a pull request that may have caused an incident
export interface CausalPR {
  url: string;
  title: string;
  number: number;
  author: string;
  merged_at: string;
  files_changed: string[];
  relevance_explanation: string;
  confidence_score: number; // 0.0 - 1.0, used for ranking only
  provider: string; // "github" | "gitlab"
  repo_name: string;
  commit_sha?: string; // SHA for direct commits (not PRs)
  change_type?: string; // "pr" | "mr" | "commit"
}

export interface SuspectedSourceFile {
  file_path: string;
  line_number: number;
  function_name: string;
  error_type: string;
  context: string;
}

export interface StructuredInvestigationSummary {
  incident_summary: { text: string };
  
  timeline_of_events: Array<{
    time: string;
    label: string;
    description: string;
    icon: string;
    time_before?: string;
  }>;
  
  root_cause: {
    summary: string;
    details?: string[];
  };
  
  affected_resources: Array<{
    type: string;
    name: string;
    namespace: string;
    status?: string;
  }>;
  
  resolution_recommendation: {
    primary: string;
    steps?: string[];
    prevention?: string;
  };
}

export interface MetricAnnotation {
  timestamp: string;
  label: string;
  description: string;
}

export interface MetricDataPoint {
  timestamp: string;
  value: number;
}

export interface EvidenceMetric {
  metric_name: string;
  display_name: string;
  namespace: string;
  workload_name: string;
  pod_name: string;
  unit: string;
  significance: string;
  data_points: MetricDataPoint[];
  annotations: MetricAnnotation[];
}

export interface IncidentSummary {
  id: string;
  tenant_id: string;
  cluster_id: string;
  cluster_name: string;
  type: string;             // OOMKilled, CrashLoopBackOff, ImagePullError, ApplicationError, Segfault, etc.
  category: string;         // infrastructure, application
  severity: string;         // critical, high, medium, low
  status: string;           // open, investigating, resolved, remediation_applied
  detected_at: number;      // Unix timestamp
  resolved_at?: number;     // Unix timestamp
  primary_resource: string; // e.g., "default/my-pod"
  primary_resource_kind: string; // e.g., "Pod"
  has_rca_result: boolean;
  root_cause_summary?: string; // Includes transient explanation if transient
  yaml_fix_count: number;
  code_fix_count?: number;  // For application incidents
  remediation_applied: boolean;
  remediation_applied_at?: number;
  remediation_applied_by?: string;
  remediation_fixes_count: number;
  github_pr_url?: string;   // For application incidents
  github_pr_number?: number;
  transient?: boolean;      // True if AI agent classified as transient
  namespace_owner_emails?: string[]; // Email addresses of namespace owners
  // PagerDuty source tracking
  source_type?: string;           // "signal" or "pagerduty"
  pagerduty_alert_id?: string;
  pagerduty_alert_url?: string;
  has_pagerduty_signals?: boolean; // True if any PagerDuty alerts are in detection signals
  // Cluster group fields (synthetic row grouping cluster_scheduling incidents)
  is_cluster_group?: boolean;
  child_incident_ids?: string[];
  affected_workload_count?: number;
  investigating_count?: number;
  resolved_count?: number;
}

export interface IncidentDetails {
  id: string;
  tenant_id: string;
  cluster_id: string;
  cluster_name: string;
  type: string;
  category: string;         // infrastructure, application
  severity: string;
  status: string;
  signals: IncidentSignal[];
  detected_at: number;
  resolved_at?: number;
  rca_result?: IncidentRcaResult;
  remediation_applied: boolean;
  remediation_applied_at?: number;
  remediation_applied_by?: string;
  remediation_fixes_count: number;
  // Validation result (from AI agent checking if fix worked)
  validation_status?: string;       // fixed, not_fixed, recurring, unknown
  validation_summary?: string;
  validation_details?: string[];
  validated_at?: number;
  // Post-mortem analysis (auto-generated when incident is marked as resolved)
  postmortem_generated?: boolean;
  postmortem_result?: {
    postmortem_summary?: string;
    what_went_well?: string[];
    what_could_improve?: string[];
    lessons_learned?: Array<{
      lesson: string;
      severity: string;
      category: string;
    }>;
    prevention_recommendations?: Array<{
      recommendation: string;
      priority: string;
      effort: string;
      category: string;
    }>;
    similar_incidents_to_monitor?: string[];
    impact_assessment?: {
      user_impact: string;
      duration_minutes: number;
      affected_users: string;
      business_impact: string;
      technical_impact: string;
    };
    detection_effectiveness?: {
      time_to_detect: string;
      detection_method: string;
      effectiveness_rating: string;
      improvements: string;
    };
    remediation_effectiveness?: {
      time_to_resolve: string;
      fix_success: string;
      effectiveness_rating: string;
      improvements: string;
    };
  };
  postmortem_at?: number;
  // Application-level incident fields
  error_stack_trace?: string;
  error_message?: string;
  source_file_path?: string;
  source_line_number?: number;
  exit_code?: number;
  github_repository_id?: string; // ID of selected repository from tenant_github_repositories
  namespace_owner_emails?: string[]; // Email addresses of namespace owners
  // Incident history (previous occurrences of the same issue)
  previous_incidents?: Array<{
    id: string;
    type: string;
    incident_type?: string;
    status: string;
    severity: string;
    detected_at: number;
    yaml_fix_count?: number;
    code_fix_count?: number;
  }>;
  occurrence_number?: number;
  // Code fix PR/MR tracking (populated from backend PR lookup)
  code_fix_pr_url?: string;
  code_fix_pr_state?: string;    // open, closed, merged, opened
  code_fix_pr_provider?: string; // github, gitlab
}

export interface ListIncidentsResponse {
  incidents: IncidentSummary[];
  total_count: number;
  next_page_token?: string;
  // Aggregate statistics across all filtered incidents (not just current page)
  investigating_count?: number;
  resolved_count?: number;
  remediation_applied_count?: number;
  resolved_confirmed_count?: number;
  critical_count?: number;
  high_count?: number;
  unique_workload_count?: number;
}

// Cloud Incident types (AWS)
export interface CloudIncidentSignal {
  signal_type: string;
  timestamp: string;
  aws_account_id: string;
  aws_connection_id: string;
  region: string;
  resource_arn?: string;
  resource_type?: string;
  resource_id?: string;
  service_name: string;
  severity: string;
  event_name?: string;
  event_source?: string;
  error_code?: string;
  error_message?: string;
  message: string;
  user_identity?: {
    type: string;
    arn?: string;
    account_id?: string;
    user_name?: string;
    principal_id?: string;
  };
  metadata?: Record<string, any>;
}

export interface CloudAffectedResource {
  arn: string;
  resource_type: string;
  resource_id: string;
  resource_name?: string;
  region: string;
}

export interface CloudTimelineEvent {
  timestamp: string;
  event_type: string;
  description: string;
  source: string;
  severity: string;
}

// File to create before running an SSM command
export interface SSMCommandFile {
  path: string;         // File path on the EC2 instance (e.g., /tmp/reassignment.json)
  content: string;      // File content
  description: string;  // Human-readable description
}

// SSM command to run on an EC2 instance
export interface SSMCommand {
  description: string;           // Human-readable description
  command: string;               // The shell command to run
  timeout?: number;              // Timeout in seconds (default: 300)
  working_dir?: string;          // Working directory
  files?: SSMCommandFile[];      // Files to create before running
}

// CLIPlaceholder represents a placeholder in a CLI command that needs user input
export interface CLIPlaceholder {
  name: string;        // Placeholder name (e.g., "trusted_ip_range")
  description: string; // Description for the user
  example?: string;    // Example value
  value?: string;      // User-provided value (filled in by frontend)
}

export interface CloudAPIFix {
  service: string;
  operation: string;
  resource_arn: string;
  parameters: Record<string, any>;
  cli_command: string;
  rollback_command?: string;
  explanation: string;
  risk_level: string;
  // Placeholders that need user input before execution
  placeholders?: CLIPlaceholder[];
  // SSM command execution fields
  ssm_instance_id?: string;      // EC2 instance ID to run commands on
  ssm_commands?: SSMCommand[];   // Commands to run via SSM
  ssm_region?: string;           // Region where the EC2 instance is located
  // Async operation fields - for long-running operations that need polling
  is_async?: boolean;                    // True if operation is async and needs polling
  polling_command?: string;              // AWS CLI command to check operation status
  polling_interval_seconds?: number;     // How often to poll (default 30)
  polling_success_pattern?: string;      // Pattern to match for success (e.g., "COMPLETE")
  polling_timeout_seconds?: number;      // Max time to wait (default 1800)
  async_operation_arn?: string;          // ARN of the async operation (extracted from response)
  async_status?: 'pending' | 'polling' | 'completed' | 'failed' | 'timeout';
  async_last_polled_at?: string;         // ISO date string
  async_last_poll_result?: string;       // Last poll response
  // Execution status fields
  status?: 'pending' | 'applied' | 'rolled_back' | 'failed';
  applied_at?: string; // ISO date string
  applied_by?: string;
  apply_result?: string;
  apply_error?: string;
  rolled_back_at?: string; // ISO date string
  rollback_result?: string;
  rollback_error?: string;
}

export interface ApplyFixRequest {
  incident_id: string;
  fix_index: number;
}

export interface ApplyFixResponse {
  success: boolean;
  message: string;
  output?: string;
  error?: string;
  // AI fix attempt fields
  fix_attempted?: boolean;
  fixed_command?: string;
  fix_explanation?: string;
  fix_changes_made?: string[];
  cannot_auto_fix?: boolean;
}

export interface UpdateFixPlaceholdersResponse {
  success: boolean;
  message: string;
  cli_command: string; // Updated CLI command with substituted values
  error?: string;
}

export interface RollbackFixRequest {
  incident_id: string;
  fix_index: number;
}

export interface RollbackFixResponse {
  success: boolean;
  message: string;
  output?: string;
  error?: string;
}

export interface ValidateCloudFixResponse {
  success: boolean;
  validation_status: 'fixed' | 'not_fixed' | 'recurring' | 'unknown';
  validation_summary: string;
  validation_details: string[];
  checked_at: string;
  aws_commands_executed: number;
  error?: string;
}

// Tribal Knowledge types
export interface TribalKnowledgeSnippet {
  source_type: string;  // slack, notion, confluence, jira, linear, glean
  source_name: string;  // e.g., "#incidents-channel", "Engineering Wiki"
  title: string;
  content: string;
  url: string;
  relevance_score: number;
  author?: string;
  timestamp?: string;
  relevance_reason?: string;
  key_insights?: string[];
}

export interface TribalKnowledgeSourceConfig {
  id?: string;
  tenant_id?: string;
  source_type: string;  // slack, notion, confluence, jira, linear, glean
  enabled: boolean;
  name?: string;  // Display name for this source
  api_key?: string;  // Only used when creating/updating
  api_token?: string;  // Only used when creating/updating
  workspace_id?: string;
  base_url?: string;
  search_scope?: string[];
  config?: Record<string, unknown>;
  connection_status?: string;  // pending, connected, error
  last_connection_test?: string;
  connection_error?: string;
  has_api_token?: boolean;  // Indicates if API token is configured
  has_api_key?: boolean;  // Indicates if API key is configured
  uses_kestrel_integration?: boolean;  // True if this source uses existing Kestrel integration (e.g., Slack)
  created_at?: string;
  updated_at?: string;
}

export interface TribalKnowledgeSearchRequest {
  incident_type: string;
  severity: string;
  services: string[];
  resources: string[];
  error_messages?: string[];
  root_cause?: string;
}

export interface TribalKnowledgeSearchResponse {
  query: string;
  incident_type: string;
  snippets: TribalKnowledgeSnippet[];
  sources_searched: string[];
  search_duration_ms: number;
}

export interface CloudCodeFix {
  file_path: string;
  file_type: string;
  original_code: string;
  fixed_code: string;
  explanation: string;
  line_start: number;
  line_end: number;
  language?: string;
  repo_id?: string;
}

export interface CloudRCAResult {
  incident_type: string;
  root_cause: string;
  timeline: CloudTimelineEvent[];
  investigation_summary: any;
  api_fixes: CloudAPIFix[];
  oci_cli_fixes?: OCICLIFix[];
  affected_resources: Array<Record<string, string>>;
  completed_at: number;
  transient: boolean;
  false_positive: boolean;
  tribal_knowledge_used?: TribalKnowledgeSnippet[];
  is_application_level_failure?: boolean;
  stack_trace_summary?: string;
  suspected_source_files?: Array<{ file_path: string; line_number?: number; function_name?: string; error_type?: string; context?: string }>;
  code_fixes?: CloudCodeFix[];
  code_provider?: string;
  code_fix_status?: string;
  code_fix_pr_url?: string;
}

// OCI CLI fix interface
export interface OCICLIFix {
  service: string;
  operation: string;
  resource_ocid: string;
  compartment_ocid?: string;
  parameters?: Record<string, any>;
  cli_command: string;
  rollback_command: string;
  explanation: string;
  risk_level: string;
  // Placeholders that need user input before execution
  placeholders?: CLIPlaceholder[];
  // Execution status
  status?: string;
  applied_at?: number;
  applied_by?: string;
  apply_result?: string;
  apply_error?: string;
  rolled_back_at?: number;
  rollback_result?: string;
  rollback_error?: string;
  // Async operation fields - for long-running operations that need polling (e.g., OCI work requests)
  is_async?: boolean;
  polling_command?: string;
  polling_interval_seconds?: number;
  polling_success_pattern?: string;
  polling_timeout_seconds?: number;
  async_work_request_id?: string;  // OCI work request OCID (extracted from response)
  async_operation_arn?: string;    // AWS ARN (for compatibility)
  async_status?: 'pending' | 'polling' | 'completed' | 'failed' | 'timeout';
  async_last_polled_at?: string;
  async_last_poll_result?: string;
  // SSM command execution fields (for OCI compute instance commands - future support)
  ssm_instance_id?: string;
  ssm_commands?: SSMCommand[];
  ssm_region?: string;
}

export interface CloudIncidentSummary {
  id: string;
  tenant_id: string;
  cloud_provider?: string; // "aws" or "oci" - defaults to "aws" for backwards compatibility
  aws_connection_id: string;
  aws_account_id: string;
  // OCI-specific fields
  oci_connection_id?: string;
  oci_tenancy_ocid?: string;
  oci_compartment_id?: string;
  type: string;
  severity: string;
  status: string;
  detected_at: number;
  resolved_at?: number;
  signals: CloudIncidentSignal[];
  affected_resources: CloudAffectedResource[];
  rca_result?: CloudRCAResult;
  cloud_resource_owner_emails?: string[];
  // PagerDuty source tracking
  source_type?: string;           // "signal" or "pagerduty"
  pagerduty_alert_id?: string;
  pagerduty_alert_url?: string;
  has_pagerduty_signals?: boolean; // True if any PagerDuty alerts are in detection signals
  // Incident history (previous occurrences of the same issue)
  previous_incidents?: Array<{
    id: string;
    type: string;
    incident_type?: string;
    status: string;
    severity: string;
    detected_at: number;
    api_fix_count?: number;
    oci_cli_fix_count?: number;
    terraform_fix_count?: number;
    code_fix_count?: number;
  }>;
  occurrence_number?: number;
}

export interface ListCloudIncidentsResponse {
  incidents: CloudIncidentSummary[];
  total_count: number;
  next_page_token?: string;
}

export interface CloudIncidentStatsResponse {
  total_open: number;
  total_investigating: number;
  total_resolved: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

// IaC (Terraform) types
export interface IaCRepoConfig {
  repository_id: string;
  tenant_id: string;
  iac_type: string;
  iac_root_path: string;
  iac_enabled: boolean;
  aws_connection_id?: string;
  last_analyzed_at?: string;
  analysis_result?: Record<string, any>;
  repo_full_name: string;
  default_branch: string;
}

export interface TerraformFix {
  id: string;
  cloud_incident_id: string;
  repository_id: string;
  file_path: string;
  original_content?: string;
  proposed_content: string;
  diff_content?: string;
  explanation?: string;
  resource_type?: string;
  resource_identifier?: string;
  pr_id?: string;
  pr_status: string;
  pr_url?: string;
  status: string;
  created_at: string;
  repo_full_name?: string;
}

export interface TerraformFixesResponse {
  terraform_fixes: TerraformFix[];
}

export interface CreateTerraformPRResponse {
  success: boolean;
  pr_url?: string;
  pr_number?: number;
  branch?: string;
  error?: string;
}

// =============================================================================
// On-Prem Deployment Types
// =============================================================================

export interface OnPremRelease {
  id: string;
  version: string;           // Helm chart version (e.g., "v1.0.2")
  image_version: string;     // Docker image tag (e.g., "v1.0.0")
  release_date: string;
  release_notes: string;
  is_latest: boolean;
  is_active: boolean;
  docker_images: Record<string, string>;
  helm_chart_versions: Record<string, string>;
  // Download URLs for artifacts
  images_bundle_url?: string;  // Pre-packaged tar.gz for air-gapped deployments
  helm_chart_url?: string;     // Packaged Helm chart tgz
  min_upgrade_version?: string;
  breaking_changes?: string[];
  supported_llm_providers: string[];
  created_at: string;
  updated_at: string;
}

export interface OnPremDeployment {
  id: string;
  tenant_id: string;
  deployment_type: 'standard' | 'airgapped';
  current_version?: string;
  deployed_at?: string;
  last_download_at?: string;
  last_download_version?: string;
  llm_provider: string;
  llm_model_id?: string;
  llm_endpoint_url?: string;
  bedrock_region?: string;
  secrets_provider: string;
  deployment_config?: Record<string, any>;
  notes?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SupportedLLMModel {
  id: string;
  name: string;
  provider: string;
  description: string;
  recommended: boolean;
}

export interface OnPremStatus {
  deployment: OnPremDeployment | null;
  latest_release: OnPremRelease | null;
  upgrade_available: boolean;
  download_history: OnPremDownloadHistory[];
}

export interface OnPremDownloadHistory {
  id: string;
  tenant_id: string;
  deployment_id?: string;
  release_version: string;
  component: string;
  file_size_bytes?: number;
  checksum?: string;
  downloaded_by?: string;
  downloaded_at: string;
}

export interface CreateOnPremDeploymentRequest {
  deployment_type: 'standard' | 'airgapped';
  llm_provider: string;
  llm_model_id?: string;
  llm_endpoint_url?: string;
  bedrock_region?: string;
  secrets_provider?: string;
  deployment_config?: {
    // Infrastructure configuration
    ecr_registry_url?: string;
    iam_role_arn?: string;
    // External database
    use_external_database?: boolean;
    postgres_host?: string;
    postgres_port?: number;
    postgres_db?: string;
    postgres_user?: string;
    redis_host?: string;
    redis_port?: number;
    // Elasticsearch/OpenSearch
    elasticsearch_enabled?: boolean;
    elasticsearch_endpoint?: string;
    elasticsearch_username?: string;
    // Note: password not saved for security
    // Ingress
    ingress_class_name?: string;
    ingress_host?: string;
    // GitHub Enterprise Server (for air-gapped deployments)
    github_enterprise_url?: string;
    github_app_id?: string;
    github_app_slug?: string;
  };
  notes?: string;
}

export interface GenerateHelmValuesRequest {
  ecr_registry_url: string;
  deployment_type?: 'standard' | 'airgapped';  // deployment type
  use_external_database?: boolean;  // false = use bundled Postgres/Redis
  postgres_host?: string;
  postgres_port?: number;
  postgres_db?: string;
  postgres_user?: string;
  redis_host?: string;
  redis_port?: number;
  llm_provider: string;
  llm_model_id?: string;
  llm_endpoint_url?: string;
  bedrock_region?: string;
  openai_api_key?: string;
  openai_model_id?: string;
  iam_role_arn?: string;
  secrets_provider?: string;
  ingress_class_name?: string;
  ingress_host?: string;
  // Elasticsearch/OpenSearch configuration
  elasticsearch_enabled?: boolean;
  elasticsearch_endpoint?: string;
  elasticsearch_username?: string;
  elasticsearch_password?: string;
  // GitHub Enterprise Server configuration (for air-gapped deployments)
  github_enterprise_url?: string;
  github_app_id?: string;
  github_app_slug?: string;
  // License key for on-prem deployment
  license_key?: string;
}

export interface GenerateHelmValuesResponse {
  server_values: string;
  agents_values: string;
  router_values: string;
  frontend_values: string;
  unified_values: string;
  // CA certificate for TLS - must be added to GitHub Enterprise Server's trusted certificates
  ingress_ca_cert?: string;
}

export interface GenerateTLSCertResponse {
  ca_cert: string;      // PEM-encoded CA certificate (add to GHE trusted certs)
  server_cert: string;  // PEM-encoded server certificate
  server_key: string;   // PEM-encoded private key
}

export interface RecordDownloadRequest {
  release_version: string;
  component: string;
  file_size_bytes?: number;
  checksum?: string;
}

export interface MarkDeployedRequest {
  version: string;
}

export interface OnPremPullCredentialsResponse {
  registry: string;
  username: string;
  token: string;
  docker_login_command: string;
  k8s_secret_command: string;
  commands: string; // Combined commands for copying
  instructions: string; // Legacy field
  images: Record<string, string>;
  expires_at?: string | null;
}

export interface OnPremImagesBundleResponse {
  version: string;
  status: string;
  message: string;
  tenant_id: string;
  contact: string;
  bundle_size: string;
  download_url?: string;
  expires_at?: string;
}

// Utility function to clean up error messages from gRPC technical details
export function cleanErrorMessage(error: any): string {
  let errorMessage = 'An error occurred';
  
  if (error.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error.response?.data?.error) {
    errorMessage = error.response.data.error;
  } else if (error.response?.data) {
    errorMessage = typeof error.response.data === 'string' ? error.response.data : errorMessage;
  } else if (error.message) {
    errorMessage = error.message;
  }
  
  // Clean up gRPC technical details from error message
  errorMessage = errorMessage.replace(/^rpc error: code = \w+ desc = /, '');
  
  return errorMessage;
}

// ── Workflow Builder interfaces ─────────────────────────────────────────

export interface WorkflowSummary {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  definition: WorkflowDefinition;
  trigger_config: any;
  nl_prompt?: string;
  alert_config?: any;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  last_triggered_at?: string;
  trigger_count: number;
  /** Sandbox workflow built with integrations simulated as connected; can only be test-run, never activated. */
  simulated?: boolean;
}

export interface WorkflowDefinition {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface WorkflowNode {
  id: string;
  type: 'trigger' | 'action' | 'condition' | 'approval' | 'refine_approval' | 'loop' | 'for_each';
  data: any;
  position: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  definition: WorkflowDefinition;
  trigger_config: any;
  nl_prompt?: string;
  alert_config?: any;
  from_suggestion_id?: string;
  simulated?: boolean;
}

export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  definition?: WorkflowDefinition;
  trigger_config?: any;
  nl_prompt?: string;
  alert_config?: any;
}

export interface WorkflowCatalog {
  signals: SignalTemplate[];
  actions: ActionTemplate[];
  integrations: IntegrationMeta[];
  custom_blocks?: CustomBlock[];
  trigger_variables?: Record<string, OutputField[]>;
  slack_channels?: { id: string; name: string }[];
  slack_users?: { id: string; name: string; real_name?: string }[];
  github_repos?: string[];
  gitlab_repos?: string[];
  confluence_spaces?: { key: string; name: string }[];
  jira_projects?: { key: string; name: string }[];
  linear_teams?: { id: string; key: string; name: string }[];
  vercel_projects?: { id: string; name: string }[];
  railway_projects?: { id: string; name: string }[];
  railway_services?: { id: string; name: string }[];
  railway_environments?: { id: string; name: string }[];
  flyio_apps?: { id: string; name: string }[];
  clusters?: { id: string; name: string; has_argocd?: string; has_datadog?: string }[];
  aws_accounts?: { id: string; account_id: string }[];
  gcp_projects?: { key: string; name: string }[];
  kestrel_users?: { id: string; email: string; name: string }[];
  user_groups?: { id: string; name: string; member_count: string }[];
  namespaces?: { name: string; cluster_id: string }[];
  workloads?: { name: string; kind: string; namespace: string; cluster_id: string }[];
  argocd_apps?: { cluster_id: string; cluster_name: string; app_name: string }[];
  pagerduty_services?: { id: string; name: string }[];
  datadog_metrics?: string[];
  github_workflows?: { repo: string; file: string }[];
  cloudflare_zones?: { key: string; name: string }[];
  cloudflare_workers?: { key: string; name: string }[];
  cloudflare_lb_pools?: { key: string; name: string }[];
  cloudflare_tunnels?: { key: string; name: string }[];
  cloudflare_access_apps?: { key: string; name: string }[];
  jenkins_jobs?: { key: string; name: string }[];
  circleci_projects?: { key: string; name: string }[];
  sonarcloud_projects?: { key: string; name: string }[];
  okta_users?: { key: string; name: string }[];
  okta_groups?: { key: string; name: string }[];
  terraform_workspaces?: { key: string; name: string }[];
  databricks_jobs?: { key: string; name: string }[];
  pulumi_stacks?: { key: string; name: string }[];
  vault_mounts?: { key: string; name: string }[];
  infisical_projects?: { key: string; name: string }[];
}

export interface WorkflowStats {
  total_workflows: number;
  active_workflows: number;
  total_executions: number;
  status_breakdown: Record<string, number>;
  daily_executions: { date: string; completed: number; failed: number; running: number; waiting: number }[];
}

export interface SignalTemplate {
  id: string;
  source: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  signal_type: string;
  default_filters: any;
}

export interface OutputField {
  name: string;
  label: string;
  description?: string;
  /** Discrete values this output can take (for exit-condition value dropdowns). */
  values?: string[];
  /** Builder-side option source for ID-like outputs (mirrors ActionConfigField.dynamic_source). */
  dynamic_source?: string;
  /** Marks outputs that carry secret material — redacted in persisted step results and masked in the UI. */
  sensitive?: boolean;
}

export interface ActionTemplate {
  id: string;
  integration: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  fields: ActionConfigField[];
  outputs?: OutputField[];
}

export interface ActionConfigField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  default?: any;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  dynamic_source?: string;
}

export interface IntegrationMeta {
  id: string;
  name: string;
  icon: string;
  description: string;
  requires_setup: boolean;
  setup_path?: string;
}

export interface WorkflowIntegrationStatus {
  id: string;
  name: string;
  connected: boolean;
  setup_path?: string;
}

export interface GenerateWorkflowResponse {
  success: boolean;
  definition?: WorkflowDefinition;
  trigger_config?: any;
  name?: string;
  description?: string;
  explanation?: string;
  error?: string;
  error_code?: string;
  unconnected_integrations?: { id: string; name: string; setup_path?: string }[];
  unsupported_operations?: { requested: string; reason?: string }[];
}

export interface SuggestedWorkflow {
  id?: string;
  name: string;
  description: string;
  definition: WorkflowDefinition;
  trigger_config: any;
  nl_prompt?: string;
  source?: string;
  status?: string;
  /** Generated for an integration that is not connected; opens in simulated mode. */
  simulated?: boolean;
  created_workflow_id?: string;
  created_at_workflow?: string;
}

/** An integration Kestrel can generate workflow suggestions for. */
export interface SuggestionSource {
  source: string;
  label: string;
  /** Integration key the icon switch resolves; differs from `source` for Kestrel requests. */
  icon: string;
}

export interface WorkflowRequest {
  id: string;
  tenant_id: string;
  requester_slack_user_id: string;
  requester_name: string;
  requester_email?: string;
  source: string;
  source_channel_id: string;
  source_thread_ts: string;
  prompt: string;
  category: string;
  matched_workflow_id?: string;
  execution_id?: string;
  parameters: Record<string, any>;
  missing_parameters: string[];
  suggested_workflow?: {
    name: string;
    description: string;
    definition: any;
    trigger_config: any;
    explanation: string;
    nl_prompt?: string;
  };
  status: string;
  execution_error?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowVersion {
  id: string;
  workflow_id: string;
  tenant_id: string;
  version_number: number;
  name: string;
  description?: string;
  definition: any;
  trigger_config: any;
  nl_prompt?: string;
  alert_config?: any;
  created_by?: string;
  created_by_email?: string;
  created_at: string;
  change_summary?: string;
}

export interface WorkflowExecution {
  id: string;
  workflow_id: string;
  tenant_id: string;
  incident_id?: string;
  trigger_signal: any;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | 'waiting_approval' | 'waiting';
  started_at: string;
  completed_at?: string;
  step_results: any[];
  error_message?: string;
  current_step_id?: string;
}

// Returned by GET /api/workflow-executions/{id}. Embeds a snapshot of the
// parent workflow (joined via LEFT JOIN, so soft-deleted parents still
// resolve). The execution-detail page should use this and NOT also call
// GET /api/workflows/{id}.
export interface WorkflowExecutionDetail extends WorkflowExecution {
  workflow?: {
    id: string;
    name: string;
    status: 'draft' | 'active' | 'paused' | 'archived';
    definition: WorkflowDefinition;
    trigger_config: any;
    deleted_at?: string;
  };
}

export interface WorkflowExecutionPage {
  executions: WorkflowExecution[];
  total: number;
  page: number;
  page_size: number;
}

export interface WorkflowApproval {
  id: string;
  execution_id: string;
  tenant_id: string;
  step_id: string;
  approval_type: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  requested_at: string;
  responded_at?: string;
  responded_by?: string;
  responded_by_email?: string;
  pr_url?: string;
  expires_at?: string;
  context?: any;
  approval_rules?: ApprovalRule[];
  approval_responses?: ApprovalResponse[];
}

export interface ApprovalRuleEntry {
  type: 'user' | 'group';
  id: string;
  label?: string;
}

export interface GitHubWorkflowInput {
  name: string;
  description?: string;
  required: boolean;
  default?: string;
  type?: string;
  options?: string[];
}

export interface ApprovalRule {
  entries: ApprovalRuleEntry[];
}

export interface ApprovalResponse {
  user_id: string;
  responded_at: string;
  status: 'approved' | 'rejected';
}

export interface UserGroup {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  member_count: number;
}

export interface UserGroupMember {
  id: string;
  group_id: string;
  user_id: string;
  email: string;
  role: string;
  name: string;
  added_at: string;
}

export interface WorkflowObservabilityData {
  success_rate: number;
  mean_duration_sec: number;
  mean_approval_wait_sec: number;
  request_conversion_rate: number;
  active_workflows: number;
  total_executions_30d: number;
  per_workflow: { workflow_id: string; workflow_name: string; completed: number; failed: number; waiting: number; total: number; failure_rate: number }[];
  daily_per_workflow: { date: string; workflow_id: string; workflow_name: string; count: number }[];
  duration_by_workflow: { workflow_id: string; workflow_name: string; avg_sec: number; p95_sec: number }[];
  slowest_steps: { step_action: string; integration: string; avg_sec: number; count: number }[];
  integration_invocations: { integration: string; count: number; error_rate: number }[];
  integration_errors: { integration: string; count: number }[];
  top_actions: { action: string; integration: string; invocations: number; successes: number; failures: number; error_rate: number; avg_sec: number }[];
  approval_wait_by_workflow: { workflow_id: string; workflow_name: string; avg_wait_sec: number; count: number }[];
  daily_approval_outcomes: { date: string; approved: number; rejected: number }[];
  daily_requests: { date: string; slack: number; cli: number; platform: number; other: number }[];
  request_categories: { category: string; count: number }[];
  trigger_sources: { source: string; count: number }[];
}

export interface CustomBlockParam {
  key: string;
  label: string;
  description?: string;
  default_value?: string;
}

export interface CustomBlock {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  icon_url?: string;
  icon_url_dark?: string;
  integration_name?: string;
  color?: string;
  block_type?: string;
  method: string;
  url_template: string;
  headers_template: Record<string, string>;
  body_template?: string;
  content_type: string;
  auth_type: string;
  response_mapping?: Record<string, string>;
  parameters?: CustomBlockParam[];
  webhook_path?: string;
  signature_header?: string;
  payload_mapping?: Record<string, string>;
  event_type_path?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomBlockInput {
  name: string;
  description: string;
  icon_url?: string;
  icon_url_dark?: string;
  integration_name?: string;
  color?: string;
  method: string;
  url_template: string;
  headers_template: Record<string, string>;
  body_template?: string;
  content_type: string;
  auth_type: string;
  credential?: string;
  response_mapping?: Record<string, string>;
  parameters?: CustomBlockParam[];
  block_type?: string;
  webhook_secret?: string;
  signature_header?: string;
  payload_mapping?: Record<string, string>;
  event_type_path?: string;
}

export interface ParseCurlResult {
  success: boolean;
  method?: string;
  url_template?: string;
  headers_template?: Record<string, string>;
  body_template?: string;
  content_type?: string;
  auth_type?: string;
  credential_detected?: boolean;
  suggested_name?: string;
  suggested_description?: string;
  parameters?: CustomBlockParam[];
  response_mapping?: Record<string, string>;
  error?: string;
}